import React, {
  lazy,
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useDeferredValue,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Camera,
  Layers,
  CheckCircle,
  Loader2,
  Trash2,
  ArrowRight,
  Sparkles,
  Zap,
  AlertCircle,
  Check,
  Wifi,
  WifiOff,
  X,
  FileText,
  Mic,
  MicOff,
  Pin,
} from 'lucide-react';
import {
  AppPhase,
  GradingMode,
  Course,
  Assignment,
  Student,
  GradedWork,
  GradingResponse,
  GeometricData,
  SubscriptionStatus,
} from './types';
import {
  analyzeMultiPagePaper,
  analyzePaper,
  assessFrame,
  extractRubricFromImage,
  generateRubric,
} from './services/geminiService';
import {
  loadAuthSession,
  saveAuthSession,
  clearAuthSession,
  touchAuthSession,
} from './lib/authSession';
import { auth } from './lib/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  updateProfile,
  sendPasswordResetEmail,
  OAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { getAppleIdTokenForFirebaseAuth } from './lib/appleSignInWeb';
import { clearLocalUserDataAsync } from './lib/clearLocalUserData';
import { sendSignupAttribution } from './lib/sendSignupAttribution';
import { recordActivationEvent } from './lib/activation';
import {
  classroomSyncErrorMessage,
  isClassroomApiConfigError,
  isStaleOAuthProjectError,
} from './lib/classroomSyncErrorMessage';
import { hasOAuthReturnInUrl, parseOAuthReturnToken } from './lib/parseOAuthReturnToken';
import { revokeGoogleOAuthAccessToken } from './lib/revokeGoogleAccessToken';
import type { AccountDeletionRequest, AccountDeletionResult } from './lib/accountDeletionFlow';
import { purgeServerAccountData } from './lib/accountDeletionFlow';
import { requestGoogleAccessToken } from './lib/requestGoogleAccessToken';
import {
  completeGoogleRedirectSignIn,
  hasGoogleRedirectPendingMarker,
  signInWithGoogleFirebase,
} from './lib/firebaseGoogleSignIn';
import {
  getGoogleOAuthClientId,
  GOOGLE_CLIENT_ID_MISSING_MESSAGE,
} from './lib/googleOAuthClientId';
import { ClassroomService, type ClassroomTokenHooks } from './services/classroomService';
import { logEvent } from './analytics';
import { safeParseJson } from './utils/safeParseJson';
import {
  buildHomeScheduleHighlights,
  buildHomeTopPriorityActions,
  buildHomeUrgentAlerts,
  buildHomeWeekInsights,
  type HomeScheduleItemLite,
} from './lib/buildHomeScreen';
import {
  loadShortcutUsageStore,
  saveShortcutUsageStore,
  bumpShortcutUsage,
  rankHomeShortcuts,
  type HomeShortcutKey,
} from './lib/homeShortcutUsage';
import {
  DEFAULT_TEACH_TIMER_MS,
  makeIdleCountdown,
  pauseCountdown,
  resetCountdown,
  startCountdown,
  tickCountdown,
  type TeachCohort,
  type TeachLastRandomPick,
  type TeachTimerState,
} from './lib/teachSession';
import { loadTeachSessionSnapshot, saveTeachSessionSnapshot } from './lib/teachSessionPersistence';
import { loadScheduleUiSnapshot, saveScheduleUiSnapshot } from './lib/scheduleUiPersistence';
import {
  ELITE_FLAGS_DEFAULTS,
  ELITE_FLAGS_KEY,
  ELITE_METRICS_KEY,
  type EliteFeatureFlags,
  type EliteMetricsStore,
} from './lib/elitePilot';
import {
  clearLastGradeSession,
  LAST_GRADE_SESSION_STORAGE_KEY,
  loadLastGradeSession,
  saveLastGradeSession,
} from './lib/lastGradeSession';
import {
  deriveHubTrustStatus,
  findNextUngradedStudent,
  loadGradeHubSessionMeta,
} from './lib/gradeHub/session';
import { bumpSubmissionCount, recordFirstUseIfNeeded } from './lib/gradeHub/retentionHints';
import { PageWrapper } from './components/PageWrapper';
import type { OptionsConfirmState } from './views/phases/OptionsPhaseView';
import type { ScheduleItem, ScheduleUndoEntry, ScheduleViewMode } from './lib/scheduleTypes';
import { formatLocalYMD } from './lib/scheduleTypes';
import { getOrCreateDriveFolder } from './lib/googleDriveUpload';
import { lightHaptic, successHaptic } from './lib/haptics';
import { ConsentBanner } from './components/ConsentBanner';
import { AppContext } from './context/AppContext';
import { hasCompletedOnboarding } from './lib/onboardingGate';
import { GradeFlowShell } from './components/grade/GradeFlowShell';
import { GradeStepHeader } from './components/grade/GradeStepHeader';
import { GradeFinaleView } from './components/grade/GradeFinaleView';
import { GradeSyncPreflightView } from './components/grade/GradeSyncPreflightView';
import { GradeSyncingView } from './components/grade/GradeSyncingView';
import { OfflineGradeSyncIntentBanner } from './components/grade/OfflineGradeSyncIntentBanner';
import { DesktopSidebar } from './components/shell/DesktopSidebar';
import { MobileShellDrawer } from './components/shell/MobileShellDrawer';
import { AuditDraftHydrator } from './components/grade/studio/AuditDraftHydrator';
import { GradeSessionRestore } from './components/grade/studio/GradeSessionRestore';
import { GradeReviewStudio } from './components/grade/studio/GradeReviewStudio';
import {
  countAuditStats,
  filterVisibleBySearch,
  getAuditVisibleRows,
  getAuditResolvedFocus,
} from './lib/auditStudioDerived';
import { GradePipelineSteps } from './components/grade/GradePipelineSteps';
import {
  deleteRubricTemplate,
  getRubricTemplates,
  saveRubricTemplate,
} from './lib/rubricTemplates';
import {
  getStoredStripeSubscriptionId,
  setStoredStripeSubscriptionId,
} from './lib/billingSubscription';
import { useDarkMode } from './hooks/useDarkMode';
import { useOnline } from './hooks/useOnline';
import { appendSyncLog } from './lib/syncLog';
import {
  buildFeedbackBatchExportHtml,
  openFeedbackBatchPrintWindow,
} from './lib/feedbackBatchExport';
import { clearOfflineActionQueue, drainOfflineActionQueue } from './lib/offlineActionQueue';
import { replayOfflineAction } from './lib/replayOfflineAction';
import {
  clearOfflineGradeSyncIntent,
  getOfflineGradeSyncIntent,
  isOfflineGradeSyncIntentValid,
  saveOfflineGradeSyncIntent,
  type OfflineGradeSyncIntent,
} from './lib/offlineGradeSyncIntent';
import { DEMO_GRADE_ONLY, resolvePhaseForApp } from './lib/demoGradeOnly';
import { GRADING_ONLY_MODE, gradeEntryPhase, appLandingPhase } from './lib/gradingOnlyMode';
import { APP_SHELL_ENABLED, appShellPhaseFromHash, hashForAppShellPhase } from './lib/appShellMode';
import {
  DEMO_WORKFLOW_ENABLED,
  DEMO_WORKFLOW_HASH,
  isDemoWorkflowHash,
} from './lib/demoWorkflowMode';
import {
  shouldShowAppBottomNav,
  shouldConfirmLeaveGrading,
  LEAVE_GRADING_CONFIRM_MESSAGE,
} from './lib/appBottomNavVisibility';
import type { ScanFlowStep } from './lib/scanGrading/types';
import { DashboardHomeContainer } from './components/shell/home/DashboardHomeContainer';
import { GradeHomeContainer } from './components/shell/grade/GradeHomeContainer';
import { PlanHomeContainer } from './components/shell/plan/PlanHomeContainer';
import { CommunicateHomeContainer } from './components/shell/communicate/CommunicateHomeContainer';
import { ManageHomeContainer } from './components/shell/manage/ManageHomeContainer';
import { PrivacyDashboard } from './components/shell/manage/PrivacyDashboard';
import { ComplianceStatusPage } from './components/shell/manage/ComplianceStatusPage';
import { buildPrivacyGoogleClassroomStatus } from './lib/privacy/googleClassroomStatus';
import { AppBottomNav } from './components/shell/navigation/AppBottomNav';
import { DemoWorkflow } from './components/demo/DemoWorkflow';
import { resolveFastScanStep } from './lib/resolveFastScanStep';
import { GradingOnlySettingsSheet } from './components/shell/GradingOnlySettingsSheet';
import type { RecentGradeTarget } from './components/grade/GradeDashboardRecentCard';
import { FloatingChromeProvider } from './context/FloatingChromeContext';
import { DraggableSettingsMenu } from './components/design-system/DraggableSettingsMenu';

const AuthViewLazy = lazy(() => import('./views/AuthView').then((m) => ({ default: m.AuthView })));
const CommunicationDashboardLazy = lazy(() =>
  import('./CommunicationDashboard').then((m) => ({ default: m.CommunicationDashboard }))
);
const OnboardingLazy = lazy(() =>
  import('./components/Onboarding').then((m) => ({ default: m.Onboarding }))
);
const LessonArchitectPlanScreenLazy = lazy(() =>
  import('./components/lesson-architect/LessonArchitectPlanScreen').then((m) => ({
    default: m.LessonArchitectPlanScreen,
  }))
);
const TeachDeskLazy = lazy(() =>
  import('./components/teach/TeachDesk').then((m) => ({ default: m.TeachDesk }))
);
const TeachPresenterLazy = lazy(() =>
  import('./components/teach/TeachPresenter').then((m) => ({ default: m.TeachPresenter }))
);
const SchedulePhaseViewLazy = lazy(() =>
  import('./views/phases/SchedulePhaseView').then((m) => ({ default: m.SchedulePhaseView }))
);
const DashboardPhaseViewLazy = lazy(() =>
  import('./views/phases/DashboardPhaseView').then((m) => ({ default: m.DashboardPhaseView }))
);
const OptionsPhaseViewLazy = lazy(() =>
  import('./views/phases/OptionsPhaseView').then((m) => ({ default: m.OptionsPhaseView }))
);
const GradingWorkflowV2Lazy = lazy(() =>
  import('./components/grade/workflow-v2/GradingWorkflowV2').then((m) => ({
    default: m.GradingWorkflowV2,
  }))
);

function PhaseLazyFallback(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-3 bg-slate-50 dark:bg-slate-950"
      role="status"
      aria-live="polite"
    >
      <Loader2 className="w-10 h-10 animate-spin text-indigo-500" aria-hidden />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

function AuthLoadingScreen({
  label = 'Loading DoneGrading',
}: {
  label?: string;
}): React.ReactElement {
  return (
    <div className="h-dvh w-full flex items-center justify-center bg-slate-950 text-white">
      <div className="flex items-center gap-3 text-sm font-black uppercase tracking-[0.18em] text-white/80">
        <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
        <span>{label}</span>
      </div>
    </div>
  );
}

function currentRouteForLog(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function devLogAuthGate(message: string, data: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;
  console.info(`[DoneGrading] ${message}`, data);
}

type SortMode = 'recent' | 'alphabetical' | 'manual';

/** National first; U.S. states A–Z by name (Lesson Architect standards jurisdiction). */
const PLAN_JURISDICTION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = (() => {
  const states: { value: string; label: string }[] = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
  ];
  states.sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
  return [{ value: 'National', label: 'National / General' }, ...states];
})();

/** Firebase / unknown API errors */
const getUnknownErr = (err: unknown): { code: string; message: string } => {
  if (!err || typeof err !== 'object') return { code: '', message: '' };
  const r = err as Record<string, unknown>;
  return {
    code: typeof r.code === 'string' ? r.code : '',
    message: typeof r.message === 'string' ? r.message : '',
  };
};

type SpeechRecognitionResultLike = {
  results: ArrayLike<{ 0?: { transcript?: string } }>;
};

type WebSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionResultLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

type WebSpeechRecognitionCtor = new () => WebSpeechRecognition;

function getWebSpeechRecognitionCtor(): WebSpeechRecognitionCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as {
    SpeechRecognition?: WebSpeechRecognitionCtor;
    webkitSpeechRecognition?: WebSpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

const useSpeechToText = (
  onResult: (text: string) => void,
  options?: {
    continuous?: boolean;
    interimResults?: boolean;
    autoRestart?: boolean;
    lang?: string;
  }
) => {
  const [isListening, setIsListening] = useState(false);
  const [hasSupport, setHasSupport] = useState(false);
  const recognitionRef = useRef<WebSpeechRecognition | null>(null);
  const shouldListenRef = useRef(false);
  const optsRef = useRef(options);
  optsRef.current = options;

  useEffect(() => {
    const SpeechRecognition = getWebSpeechRecognitionCtor();
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = !!optsRef.current?.continuous;
      recognitionRef.current.interimResults = !!optsRef.current?.interimResults;
      recognitionRef.current.lang = optsRef.current?.lang || 'en-US';
      setHasSupport(true);

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };
      recognitionRef.current.onresult = (event: SpeechRecognitionResultLike) => {
        try {
          const transcript = event.results?.[0]?.[0]?.transcript ?? '';
          if (transcript) onResult(transcript);
        } catch {
          // ignore
        }
      };

      recognitionRef.current.onerror = () => {
        // Keep UI responsive even when recognition errors out.
        setIsListening(false);
        shouldListenRef.current = false;
      };
      recognitionRef.current.onend = () => {
        if (shouldListenRef.current && optsRef.current?.autoRestart) {
          try {
            recognitionRef.current?.start();
            return;
          } catch {
            // fall through
          }
        }
        setIsListening(false);
        shouldListenRef.current = false;
      };
    } else {
      setHasSupport(false);
    }
  }, [onResult]);

  useEffect(() => {
    if (!isListening) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      shouldListenRef.current = false;
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
      setIsListening(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isListening]);

  const toggleListening = () => {
    if (isListening) {
      shouldListenRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        shouldListenRef.current = true;
        recognitionRef.current?.start();
        // isListening will flip true in onstart
      } catch (e) {
        console.error('Speech recognition start failed', e);
        shouldListenRef.current = false;
      }
    }
  };

  return { isListening, toggleListening, hasSupport };
};

const VoiceInputButton: React.FC<{ onResult: (text: string) => void; className?: string }> = ({
  onResult,
  className = '',
}) => {
  const { t } = useTranslation();
  const { isListening, toggleListening, hasSupport } = useSpeechToText(onResult);

  if (!hasSupport) return null;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        toggleListening();
      }}
      aria-pressed={isListening}
      aria-label={isListening ? t('a11y.voiceInputStop') : t('a11y.voiceInputStart')}
      className={`p-2 rounded-xl transition-all duration-300 shadow-sm backdrop-blur-md ${isListening ? 'bg-red-500 text-white animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.6)]' : 'bg-white/60 dark:bg-slate-800/60 border border-white/50 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/20 hover:text-indigo-600 dark:hover:text-indigo-300 active:scale-95'} ${className}`}
      title={isListening ? t('a11y.voiceInputStop') : t('a11y.voiceInputStart')}
    >
      {isListening ? (
        <MicOff className="w-3 h-3" aria-hidden />
      ) : (
        <Mic className="w-3 h-3" aria-hidden />
      )}
    </button>
  );
};

// --- ADVANCED IMAGE CROPPER HELPER ---
const cropImageToBoundingBox = (base64: string, corners: GeometricData | null): Promise<string> => {
  if (!corners) return Promise.resolve(base64);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const actualXs = [
          corners.topLeft[0],
          corners.topRight[0],
          corners.bottomRight[0],
          corners.bottomLeft[0],
        ].map((x) => (x / 1000) * img.width);
        const actualYs = [
          corners.topLeft[1],
          corners.topRight[1],
          corners.bottomRight[1],
          corners.bottomLeft[1],
        ].map((y) => (y / 1000) * img.height);

        const cx = actualXs.reduce((a, b) => a + b, 0) / 4;
        const cy = actualYs.reduce((a, b) => a + b, 0) / 4;

        const scale = 0.96;
        const insetXs = actualXs.map((x) => cx + (x - cx) * scale);
        const insetYs = actualYs.map((y) => cy + (y - cy) * scale);

        const minX = Math.max(0, Math.min(...insetXs));
        const maxX = Math.min(img.width, Math.max(...insetXs));
        const minY = Math.max(0, Math.min(...insetYs));
        const maxY = Math.min(img.height, Math.max(...insetYs));

        const cropW = maxX - minX;
        const cropH = maxY - minY;

        if (cropW <= 0 || cropH <= 0) return resolve(base64);

        const canvas = document.createElement('canvas');
        canvas.width = cropW;
        canvas.height = cropH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(base64);

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cropW, cropH);

        ctx.beginPath();
        ctx.moveTo(insetXs[0] - minX, insetYs[0] - minY);
        ctx.lineTo(insetXs[1] - minX, insetYs[1] - minY);
        ctx.lineTo(insetXs[2] - minX, insetYs[2] - minY);
        ctx.lineTo(insetXs[3] - minX, insetYs[3] - minY);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(img, minX, minY, cropW, cropH, 0, 0, cropW, cropH);
        resolve(canvas.toDataURL('image/jpeg', 0.9).split(',')[1]);
      } catch {
        resolve(base64);
      }
    };
    img.onerror = () => resolve(base64);
    img.src = `data:image/jpeg;base64,${base64}`;
  });
};

const uploadImageToDrive = async (
  token: string,
  base64Data: string,
  fileName: string,
  folderId: string
) => {
  const boundary = '-------314159265358979323846';
  const delimiter = '\r\n--' + boundary + '\r\n';
  const close_delim = '\r\n--' + boundary + '--';
  const multipartRequestBody =
    delimiter +
    'Content-Type: application/json\r\n\r\n' +
    JSON.stringify({ name: fileName, parents: [folderId] }) +
    delimiter +
    'Content-Type: image/jpeg\r\n' +
    'Content-Transfer-Encoding: base64\r\n\r\n' +
    base64Data +
    close_delim;

  const res = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': `multipart/related; boundary=${boundary}`,
    },
    body: multipartRequestBody,
  });

  if (!res.ok) {
    const err = await res.json();
    throw new Error(`Drive Upload Error: ${err.error?.message}`);
  }
  return res.json();
};

// --- MAIN APP COMPONENT ---

const App: React.FC = () => {
  const { t } = useTranslation();
  const [phase, setPhaseRaw] = useState<AppPhase>(() =>
    DEMO_GRADE_ONLY ? gradeEntryPhase() : AppPhase.AUTHENTICATION
  );
  const setPhase = useCallback((next: AppPhase) => {
    setPhaseRaw(resolvePhaseForApp(next));
  }, []);
  // Navigate between app-shell phases by driving the URL hash (the app-shell
  // hash effect maps it back to a phase). No-op vs. plain setPhase when the
  // flag is off. Used only by the Phase 1B shell placeholders.
  const navigateAppShell = useCallback(
    (target: AppPhase) => {
      if (APP_SHELL_ENABLED && typeof window !== 'undefined') {
        const hash = hashForAppShellPhase(target);
        if (hash && window.location.hash !== hash) {
          window.location.hash = hash;
          return;
        }
      }
      setPhase(target);
    },
    [setPhase]
  );

  // Onboarding 1C demo navigation. Entering drives the URL to #/demo (the
  // app-shell hash effect maps it to AppPhase.DEMO_WORKFLOW); exiting clears the
  // demo hash. The demo is in-memory only, so no leave-confirm/data-loss guard
  // is needed. All three are no-ops vs. plain setPhase when the flags are off.
  const handleTryDemo = useCallback(() => {
    if (APP_SHELL_ENABLED && DEMO_WORKFLOW_ENABLED && typeof window !== 'undefined') {
      if (window.location.hash !== DEMO_WORKFLOW_HASH) {
        window.location.hash = DEMO_WORKFLOW_HASH;
        return;
      }
    }
    setPhase(AppPhase.DEMO_WORKFLOW);
  }, [setPhase]);

  const handleExitDemoToHome = useCallback(() => {
    navigateAppShell(AppPhase.HOME);
  }, [navigateAppShell]);

  const handleStartRealGradingFromDemo = useCallback(() => {
    // Clear the demo hash WITHOUT firing hashchange, then open the real
    // workflow. Demo data never carries into the real session.
    if (typeof window !== 'undefined' && isDemoWorkflowHash(window.location.hash)) {
      window.history.replaceState(null, '', '#/home');
    }
    setPhase(AppPhase.SCAN_WORKFLOW);
  }, [setPhase]);

  // Focused-workflow capture state, reported up by GradingWorkflowV2. Lets the
  // app bottom nav stay visible across grading yet hide on the live camera /
  // crop surface, and lets us guard against silent data loss when leaving a
  // session in progress. Inert when the app shell flag is off.
  const [scanCaptureState, setScanCaptureState] = useState<{
    scanStep: ScanFlowStep | null;
    cropEditorOpen: boolean;
    sessionActive: boolean;
  }>({ scanStep: null, cropEditorOpen: false, sessionActive: false });

  const handleScanCaptureStateChange = useCallback(
    (next: { scanStep: ScanFlowStep; cropEditorOpen: boolean; sessionActive: boolean }) => {
      setScanCaptureState(next);
    },
    []
  );
  useEffect(() => {
    if (!DEMO_GRADE_ONLY && !GRADING_ONLY_MODE) return;
    setPhaseRaw((current) => resolvePhaseForApp(current));
  }, []);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !DEMO_GRADE_ONLY && !GRADING_ONLY_MODE && !hasCompletedOnboarding()
  );
  const [gradingOnlySettingsOpen, setGradingOnlySettingsOpen] = useState(false);
  const [mobileShellNavOpen, setMobileShellNavOpen] = useState(false);
  const hideNonGradeHubPhases = DEMO_GRADE_ONLY || GRADING_ONLY_MODE;
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [classroom, setClassroom] = useState<ClassroomService | null>(null);
  const [isOfflineBannerDismissed, setIsOfflineBannerDismissed] = useState(false);
  const [showOnlineRestore, setShowOnlineRestore] = useState(false);
  const showOnlineRestoreBrief = useCallback(() => {
    setShowOnlineRestore(true);
    setTimeout(() => setShowOnlineRestore(false), 4000);
  }, []);
  const isOnline = useOnline({ onBecomeOnline: showOnlineRestoreBrief });
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (isOnline && prevOnlineRef.current === false) {
      void drainOfflineActionQueue(async (action) => {
        const ok = await replayOfflineAction(action, { accessToken });
        appendSyncLog({
          level: ok ? 'success' : 'error',
          message: ok
            ? t('options.offlineQueue.queueLogSent', { type: action.type })
            : t('options.offlineQueue.queueLogDeferred', { type: action.type }),
        });
        return ok;
      });
    }
    prevOnlineRef.current = isOnline;
  }, [isOnline, accessToken, t]);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  /** Non-retryable Classroom API / OAuth project mismatch — pauses background sync. */
  const [classroomConfigBlocked, setClassroomConfigBlocked] = useState(false);
  const prevSyncStatusRef = useRef(syncStatus);
  useEffect(() => {
    const prev = prevSyncStatusRef.current;
    if (prev === syncStatus) return;
    if (syncStatus === 'error') {
      appendSyncLog({
        level: 'error',
        message: 'Classroom sync failed (courses or roster).',
      });
    } else if (syncStatus === 'ok' && prev === 'error') {
      appendSyncLog({
        level: 'success',
        message: 'Classroom sync OK again.',
      });
    }
    prevSyncStatusRef.current = syncStatus;
  }, [syncStatus]);
  const [isDarkMode, setIsDarkMode] = useDarkMode();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [globalSearchQuery] = useState('');
  const [assignmentSearchQuery, setAssignmentSearchQuery] = useState('');

  const [educatorName, setEducatorName] = useState<string>(
    () => localStorage.getItem('dg_educator_name') || ''
  );
  const [eliteFlags, setEliteFlags] = useState<EliteFeatureFlags>(() => {
    const stored = safeParseJson<Partial<EliteFeatureFlags> | null>(
      localStorage.getItem(ELITE_FLAGS_KEY),
      null
    );
    return { ...ELITE_FLAGS_DEFAULTS, ...(stored || {}) };
  });
  const [eliteMetrics, setEliteMetrics] = useState<EliteMetricsStore>(() =>
    safeParseJson<EliteMetricsStore>(localStorage.getItem(ELITE_METRICS_KEY), {
      eventCounts: {},
      lastEventAt: null,
    })
  );
  const trackEliteEvent = (event: string, payload?: Record<string, unknown>) => {
    setEliteMetrics((prev) => {
      const next: EliteMetricsStore = {
        eventCounts: {
          ...prev.eventCounts,
          [event]: (prev.eventCounts[event] || 0) + 1,
        },
        lastEventAt: Date.now(),
      };
      try {
        localStorage.setItem(ELITE_METRICS_KEY, JSON.stringify(next));
      } catch {
        // ignore local metrics persistence failures
      }
      return next;
    });
    try {
      logEvent('elite_event', { name: event, ...(payload || {}) });
    } catch {
      // telemetry should never impact teacher flow
    }
  };

  /** Dev/staging only: set `VITE_BYPASS_SUBSCRIPTION_GATING=true` to skip paywall checks. Never enable in production. */
  const BYPASS_SUBSCRIPTION_GATING = import.meta.env.VITE_BYPASS_SUBSCRIPTION_GATING === 'true';

  // Subscription: persisted locally; production default is unpaid until Stripe checkout succeeds.
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>(() => {
    const stored = safeParseJson<SubscriptionStatus | null>(
      localStorage.getItem('dg_subscription_status'),
      null
    );
    if (
      stored &&
      (stored === 'none' ||
        stored === 'trialing' ||
        stored === 'active' ||
        stored === 'past_due' ||
        stored === 'canceled')
    ) {
      return stored;
    }
    return import.meta.env.DEV ? 'trialing' : 'none';
  });
  const isPaid =
    DEMO_GRADE_ONLY ||
    GRADING_ONLY_MODE ||
    BYPASS_SUBSCRIPTION_GATING ||
    subscriptionStatus === 'trialing' ||
    subscriptionStatus === 'active';
  const [paywallCheckoutLoading, setPaywallCheckoutLoading] = useState(false);
  const [paywallCheckoutError, setPaywallCheckoutError] = useState<string | null>(null);
  const [subscriptionRefreshBusy, setSubscriptionRefreshBusy] = useState(false);
  const [subscriptionRefreshError, setSubscriptionRefreshError] = useState<string | null>(null);
  const billingApiBase = useMemo(
    () => (import.meta.env.VITE_BILLING_API_BASE ?? '').replace(/\/$/, ''),
    []
  );

  useEffect(() => {
    try {
      localStorage.setItem('dg_subscription_status', JSON.stringify(subscriptionStatus));
    } catch {
      /* ignore */
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing_success') !== '1') return;
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const cleanUrl = () => {
      window.history.replaceState({}, '', window.location.pathname);
    };

    let cancelled = false;
    void (async () => {
      try {
        const url = `${billingApiBase}/api/billing/checkout-session?session_id=${encodeURIComponent(sessionId)}`;
        const r = await fetch(url);
        const data = (await r.json()) as {
          status?: string;
          error?: string;
          subscriptionId?: string;
        };
        if (cancelled) return;
        if (!r.ok) {
          console.error('checkout-session', data);
          cleanUrl();
          return;
        }
        if (data.subscriptionId) {
          setStoredStripeSubscriptionId(data.subscriptionId);
        }
        const st = data.status;
        if (
          st === 'none' ||
          st === 'trialing' ||
          st === 'active' ||
          st === 'past_due' ||
          st === 'canceled'
        ) {
          setSubscriptionStatus(st);
          setPhase(AppPhase.DASHBOARD);
        }
        cleanUrl();
      } catch (e) {
        console.error(e);
        if (!cancelled) cleanUrl();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [billingApiBase]);

  /** Restore subscription from Stripe on load when this device has a saved sub id (§10.1). */
  useEffect(() => {
    const subId = getStoredStripeSubscriptionId();
    if (!subId) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch(
          `${billingApiBase}/api/billing/subscription-status?subscription_id=${encodeURIComponent(subId)}`
        );
        const data = (await r.json()) as { status?: string; error?: string };
        if (cancelled || !r.ok) return;
        const st = data.status;
        if (
          st === 'none' ||
          st === 'trialing' ||
          st === 'active' ||
          st === 'past_due' ||
          st === 'canceled'
        ) {
          setSubscriptionStatus(st);
        }
      } catch {
        /* offline / misconfigured API */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [billingApiBase]);

  const refreshSubscriptionFromStripe = useCallback(async () => {
    setSubscriptionRefreshError(null);
    const subId = getStoredStripeSubscriptionId();
    if (!subId) {
      setSubscriptionRefreshError(
        'No subscription on this device yet. Start checkout from the paywall once, then return here.'
      );
      return;
    }
    setSubscriptionRefreshBusy(true);
    try {
      const r = await fetch(
        `${billingApiBase}/api/billing/subscription-status?subscription_id=${encodeURIComponent(subId)}`
      );
      const data = (await r.json()) as { status?: string; error?: string; code?: string };
      if (!r.ok) {
        if (data.code === 'BILLING_DISABLED') {
          setSubscriptionRefreshError('Billing is not enabled on the server.');
        } else {
          setSubscriptionRefreshError(data.error || 'Could not refresh subscription.');
        }
        return;
      }
      const st = data.status;
      if (
        st === 'none' ||
        st === 'trialing' ||
        st === 'active' ||
        st === 'past_due' ||
        st === 'canceled'
      ) {
        setSubscriptionStatus(st);
        logEvent('subscription_refresh', { status: st });
      }
    } catch {
      setSubscriptionRefreshError('Network error. Check connection and try again.');
    } finally {
      setSubscriptionRefreshBusy(false);
    }
  }, [billingApiBase]);

  const [showMoreAuthOptions, setShowMoreAuthOptions] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');

  const [dashboardSort, setDashboardSort] = useState<SortMode>(() =>
    safeParseJson<SortMode>(localStorage.getItem('dg_dash_sort'), 'recent')
  );
  const [assignmentSort, setAssignmentSort] = useState<SortMode>(() =>
    safeParseJson<SortMode>(localStorage.getItem('dg_asn_sort'), 'recent')
  );
  void setDashboardSort;

  const todayLabel = useMemo(() => {
    try {
      return new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      });
    } catch {
      return '';
    }
  }, []);

  const effectiveGoogleClientId = useMemo(() => getGoogleOAuthClientId() ?? '', []);

  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccessMessage, setAuthSuccessMessage] = useState<string | null>(null);
  const [creationError, setCreationError] = useState<string | null>(null);

  const [courses, setCourses] = useState<Course[]>(() =>
    safeParseJson<Course[]>(localStorage.getItem('dg_cache_courses'), [])
  );
  const [assignments, setAssignments] = useState<Assignment[]>(() =>
    safeParseJson<Assignment[]>(localStorage.getItem('dg_cache_assignments'), [])
  );
  const [students, setStudents] = useState<Student[]>(() =>
    safeParseJson<Student[]>(localStorage.getItem('dg_cache_students'), [])
  );

  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [gradingMode, setGradingMode] = useState<GradingMode | null>(null);
  const [isResumingGradeSession, setIsResumingGradeSession] = useState(false);

  const [firebaseUser, setFirebaseUser] = useState<import('firebase/auth').User | null>(null);
  const [firebaseAuthLoading, setFirebaseAuthLoading] = useState(true);
  const [firebaseRedirectResolving, setFirebaseRedirectResolving] = useState(() =>
    hasGoogleRedirectPendingMarker()
  );
  const [firebaseIdTokenVerified, setFirebaseIdTokenVerified] = useState(false);
  const [verifiedFirebaseTokenLength, setVerifiedFirebaseTokenLength] = useState(0);
  const firebaseAuthVerified = !!firebaseUser && firebaseIdTokenVerified;

  const isSignedIn = useMemo(
    () =>
      DEMO_GRADE_ONLY ||
      (GRADING_ONLY_MODE ? firebaseAuthVerified : !!accessToken || firebaseAuthVerified),
    [accessToken, firebaseAuthVerified]
  );

  useEffect(() => {
    if (!GRADING_ONLY_MODE || DEMO_GRADE_ONLY || firebaseAuthLoading || !isSignedIn) return;
    if (phase === AppPhase.AUTHENTICATION) {
      // appLandingPhase() === gradeEntryPhase() when the app shell is off, so
      // default behavior is unchanged. With VITE_APP_SHELL on it lands on HOME.
      setPhase(appLandingPhase());
    }
  }, [GRADING_ONLY_MODE, DEMO_GRADE_ONLY, firebaseAuthLoading, isSignedIn, phase, setPhase]);

  useEffect(() => {
    setMobileShellNavOpen(false);
  }, [phase]);

  const rubricStorageUserId = firebaseUser?.uid || 'local';

  const [gradedWorks, setGradedWorks] = useState<GradedWork[]>(() =>
    safeParseJson<GradedWork[]>(localStorage.getItem('dg_pending_sync'), [])
  );
  const [offlineGradeSyncIntent, setOfflineGradeSyncIntent] =
    useState<OfflineGradeSyncIntent | null>(() => getOfflineGradeSyncIntent());
  const [offlineGradeSyncBannerDismissed, setOfflineGradeSyncBannerDismissed] = useState(false);

  useEffect(() => {
    const raw = getOfflineGradeSyncIntent();
    if (!raw) {
      setOfflineGradeSyncIntent(null);
      return;
    }
    if (!isOfflineGradeSyncIntentValid(raw, gradedWorks.length)) {
      clearOfflineGradeSyncIntent();
      setOfflineGradeSyncIntent(null);
      return;
    }
    setOfflineGradeSyncIntent(raw);
  }, [gradedWorks]);

  const undoLastPendingGrade = useCallback(() => {
    setGradedWorks((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.status === 'synced') return prev;
      return prev.slice(0, -1);
    });
  }, []);

  const [history, setHistory] = useState<GradedWork[]>(() =>
    safeParseJson<GradedWork[]>(localStorage.getItem('dg_history'), [])
  );

  const SCHEDULE_KEY = 'dg_schedule_items_v1';
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>(() =>
    safeParseJson<ScheduleItem[]>(localStorage.getItem(SCHEDULE_KEY), [])
  );
  const scheduleUiInitial = useMemo(() => loadScheduleUiSnapshot(), []);
  const [scheduleView, setScheduleView] = useState<ScheduleViewMode>(() => {
    if (scheduleUiInitial?.view) return scheduleUiInitial.view;
    try {
      const raw = localStorage.getItem('dg_schedule_view_v1');
      return raw === 'agenda' || raw === 'daily' || raw === 'weekly' || raw === 'monthly'
        ? raw
        : 'agenda';
    } catch {
      return 'agenda';
    }
  });
  const [scheduleCursor, setScheduleCursor] = useState<string>(
    () => scheduleUiInitial?.cursor ?? new Date().toISOString().slice(0, 10)
  );
  /** Jump + scroll highlight in Agenda (deep link from Home / Grade hub Today card). */
  const [scheduleFocusRequest, setScheduleFocusRequest] = useState<{
    itemId: string;
    dateISO: string;
  } | null>(null);
  const [scheduleSearch, setScheduleSearch] = useState(() => scheduleUiInitial?.search ?? '');
  const [scheduleFilterOpen, setScheduleFilterOpen] = useState(false);
  const [scheduleFilterKinds, setScheduleFilterKinds] = useState<
    Set<NonNullable<ScheduleItem['kind']>>
  >(() => new Set(scheduleUiInitial?.filterKinds ?? []));
  const [scheduleFilterCourseId, setScheduleFilterCourseId] = useState<string>(
    () => scheduleUiInitial?.filterCourseId ?? ''
  );
  const [scheduleFilterOverdueOnly, setScheduleFilterOverdueOnly] = useState(
    () => scheduleUiInitial?.filterOverdueOnly ?? false
  );
  const [scheduleFilterRecurringOnly, setScheduleFilterRecurringOnly] = useState(
    () => scheduleUiInitial?.filterRecurringOnly ?? false
  );
  const [scheduleUndo, setScheduleUndo] = useState<ScheduleUndoEntry | null>(null);
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleKind, setScheduleKind] = useState<
    'event' | 'reminder' | 'teacherBlock' | 'appointment' | 'meeting'
  >('reminder');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleNotes, setScheduleNotes] = useState('');
  const [scheduleCourseId, setScheduleCourseId] = useState('');
  const [scheduleAssignmentId, setScheduleAssignmentId] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleRecurrence, setScheduleRecurrence] = useState<
    'once' | 'daily' | 'weekly' | 'monthly'
  >('once');
  const [scheduleFormOpen, setScheduleFormOpen] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [activeScheduleHour, setActiveScheduleHour] = useState<number | null>(null);

  const [optionsDataPanelOpen, setOptionsDataPanelOpen] = useState(false);
  const [optionsConfirm, setOptionsConfirm] = useState<OptionsConfirmState>({
    open: false,
    kind: null,
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingRubric, setIsGeneratingRubric] = useState(false);
  const [, setIsSyncingClassroom] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{
    current: number;
    total: number;
    message: string;
    successes: number;
    failures: number;
    emailSuccesses: number;
    emailFailures: number;
  }>({
    current: 0,
    total: 0,
    message: '',
    successes: 0,
    failures: 0,
    emailSuccesses: 0,
    emailFailures: 0,
  });

  const [creationCourse, setCreationCourse] = useState<Course | null>(null);
  const [newCourseName, setNewCourseName] = useState('');
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);

  const [newAsnTitle, setNewAsnTitle] = useState('');
  const [newAsnDesc, setNewAsnDesc] = useState('');
  const [newAsnMaxScore, setNewAsnMaxScore] = useState<number>(100);
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);

  const [_isAutoMode, _setIsAutoMode] = useState(true);
  void _isAutoMode;
  void _setIsAutoMode;
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [showQuickPick, setShowQuickPick] = useState(false);
  const [pendingWork, setPendingWork] = useState<
    (Partial<GradingResponse> & { imageUrls: string[] }) | null
  >(null);
  const [activeGeometry, setActiveGeometry] = useState<GeometricData | null>(null);
  const [scanHealth, setScanHealth] = useState<number>(0);
  const [_oneWordCommand, setOneWordCommand] = useState<string | null>(null);
  void _oneWordCommand;
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Student targeting mode for scan attribution.
  // - single: match each scan to one student (manual or OCR match)
  // - batch: teacher selects multiple students once; scans map sequentially
  const [scanStudentMode, setScanStudentMode] = useState<'single' | 'batch'>(() => {
    try {
      const raw = localStorage.getItem('dg_scan_student_mode_v1');
      return raw === 'single' || raw === 'batch' ? raw : 'batch';
    } catch {
      return 'batch';
    }
  });
  const [batchSelectedStudentIds, setBatchSelectedStudentIds] = useState<Set<string>>(new Set());
  const [batchRosterFilter, setBatchRosterFilter] = useState<
    'all' | 'not_graded' | 'at_risk' | 'missing_email' | 'recently_graded'
  >('all');
  const [batchRosterQuery, setBatchRosterQuery] = useState('');
  const [batchRosterSort, setBatchRosterSort] = useState<'manual' | 'last_name'>(() => {
    try {
      const raw = localStorage.getItem('dg_batch_roster_sort_v1');
      return raw === 'manual' || raw === 'last_name' ? raw : 'last_name';
    } catch {
      return 'last_name';
    }
  });
  const [batchRosterOrderIds, setBatchRosterOrderIds] = useState<string[]>([]);
  const batchStudentOrderRef = useRef<string[]>([]);
  const batchNextIndexRef = useRef<number>(0);

  // Audit selection (bulk edit/sync/delete)
  const [auditSelectedIndexes, setAuditSelectedIndexes] = useState<Set<number>>(new Set());
  const [auditEditSelectedOnly, setAuditEditSelectedOnly] = useState(false);
  void setAuditEditSelectedOnly;
  void auditEditSelectedOnly;

  // Batch-level sync options (persist until teacher changes).
  // Defaults: enabled (teacher-friendly, minimal taps).
  const GRADE_SYNC_POST_TO_CLASSROOM_KEY = 'dg_grade_sync_post_to_classroom_v1';
  const GRADE_SYNC_EMAIL_STUDENTS_KEY = 'dg_grade_sync_email_students_v1';
  const GRADE_SYNC_SAVE_TO_DRIVE_KEY = 'dg_grade_sync_save_to_drive_v1';

  const [gradeSyncPostToClassroom, setGradeSyncPostToClassroom] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(GRADE_SYNC_POST_TO_CLASSROOM_KEY);
      return raw === 'false' ? false : true;
    } catch {
      return true;
    }
  });
  const [gradeSyncEmailStudents, setGradeSyncEmailStudents] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(GRADE_SYNC_EMAIL_STUDENTS_KEY);
      return raw === 'false' ? false : true;
    } catch {
      return true;
    }
  });
  const [gradeSyncSaveToDrive, setGradeSyncSaveToDrive] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(GRADE_SYNC_SAVE_TO_DRIVE_KEY);
      return raw === 'false' ? false : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        GRADE_SYNC_POST_TO_CLASSROOM_KEY,
        gradeSyncPostToClassroom ? 'true' : 'false'
      );
      localStorage.setItem(
        GRADE_SYNC_EMAIL_STUDENTS_KEY,
        gradeSyncEmailStudents ? 'true' : 'false'
      );
      localStorage.setItem(GRADE_SYNC_SAVE_TO_DRIVE_KEY, gradeSyncSaveToDrive ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  }, [gradeSyncPostToClassroom, gradeSyncEmailStudents, gradeSyncSaveToDrive]);

  // Inline editor (Review/Audit queue): only one open at a time.
  const [auditEditIndex, setAuditEditIndex] = useState<number | null>(null);
  const [auditDraftScore, setAuditDraftScore] = useState<string>('');
  const [auditDraftFeedback, setAuditDraftFeedback] = useState<string>('');

  const [auditFilter, setAuditFilter] = useState<'all' | 'errors' | 'drafts' | 'ready'>('all');
  const [auditQueueSearch, setAuditQueueSearch] = useState('');
  /** Desktop audit studio: which row is shown in the center column (`lg+`). */
  const [auditStudioFocusIdx, setAuditStudioFocusIdx] = useState<number | null>(null);
  const [syncPreflightIndexes, setSyncPreflightIndexes] = useState<number[] | null>(null);
  const [syncReceipts, setSyncReceipts] = useState<
    Array<{
      key: string;
      studentName: string;
      ok: boolean;
      error?: string;
      emailOk?: boolean;
      emailSkipped?: boolean;
      emailMissing?: boolean;
      drivePagesSaved?: number;
      driveSkipped?: boolean;
      classroomSkipped?: boolean;
    }>
  >([]);
  const [syncFailedKeys, setSyncFailedKeys] = useState<Set<string>>(new Set());

  const [scanQueueCount, setScanQueueCount] = useState(0);
  const [scanReviewQueueCount, setScanReviewQueueCount] = useState(0);
  const [scanQueueHint, setScanQueueHint] = useState<string | null>(null);
  const [showCameraTray, setShowCameraTray] = useState(false);
  const [scanLastDeleted, setScanLastDeleted] = useState<{
    work: GradedWork;
    index: number;
  } | null>(null);
  const [scanSessionStats, setScanSessionStats] = useState({
    captured: 0,
    manualCorrections: 0,
    totalGradeMs: 0,
    gradedCount: 0,
  });
  const scanQueueRef = useRef<
    {
      id: string;
      base64: string;
      dataUrl: string;
      createdAt: number;
      scanHealth?: number;
      corners?: GeometricData | null;
      transcription?: string;
    }[]
  >([]);
  const scanReviewRef = useRef<{ id: string; result: GradingResponse; dataUrl: string }[]>([]);
  const frameAssessInFlightRef = useRef<boolean>(false);

  const [manualScore, setManualScore] = useState<string>('');
  const [manualFeedback, setManualFeedback] = useState<string>('');

  const cooldownRef = useRef<boolean>(false);
  const multiPageCaptureInFlightRef = useRef<boolean>(false);
  const multiPageLastHashRef = useRef<string | null>(null);

  const [multiPageCapture, setMultiPageCapture] = useState<{
    croppedDataUrls: string[];
    apiBase64s: string[];
    detectedStudentName?: string;
  }>({ croppedDataUrls: [], apiBase64s: [], detectedStudentName: undefined });
  const [multiPageHint, setMultiPageHint] = useState<string | null>(null);

  const [selectedQuickPickIds, setSelectedQuickPickIds] = useState<Set<string>>(new Set());
  const [guardrailOverride, setGuardrailOverride] = useState(false);
  const [customRubric, setCustomRubric] = useState('');
  const [isScanningRubric, setIsScanningRubric] = useState(false);
  const [rubricSuccess, setRubricSuccess] = useState(false);
  const [rubricScanProgress, setRubricScanProgress] = useState<number>(0);
  const [rubricScanError, setRubricScanError] = useState<string | null>(null);
  const [rubricAutoAttempts, setRubricAutoAttempts] = useState<number>(0);
  const [rubricTemplatePickerId, setRubricTemplatePickerId] = useState('');
  const [rubricQuickSaveTitle, setRubricQuickSaveTitle] = useState('');
  const [rubricTemplatesTick, setRubricTemplatesTick] = useState(0);

  const savedRubricTemplates = useMemo(() => {
    void rubricTemplatesTick;
    return getRubricTemplates(rubricStorageUserId);
  }, [rubricStorageUserId, rubricTemplatesTick]);

  const applyRubricTemplateById = useCallback(
    (id: string) => {
      if (!id) {
        setRubricTemplatePickerId('');
        return;
      }
      const tpl = getRubricTemplates(rubricStorageUserId).find((x) => x.id === id);
      if (!tpl) return;
      setCustomRubric(tpl.rubric);
      setRubricTemplatePickerId(id);
      logEvent('rubric_template_apply', { templateId: id });
    },
    [rubricStorageUserId]
  );

  const saveCurrentRubricAsTemplate = useCallback(() => {
    const body = customRubric.trim();
    if (!body) return;
    const title =
      rubricQuickSaveTitle.trim() ||
      `Rubric · ${selectedAssignment?.title?.slice(0, 40) || 'Saved'} · ${new Date().toLocaleDateString()}`;
    saveRubricTemplate(rubricStorageUserId, {
      title,
      rubric: body,
      maxScore: selectedAssignment?.maxScore ?? 100,
    });
    setRubricQuickSaveTitle('');
    setRubricTemplatesTick((n) => n + 1);
    logEvent('rubric_template_save', { titleLen: title.length });
  }, [
    customRubric,
    rubricQuickSaveTitle,
    rubricStorageUserId,
    selectedAssignment?.maxScore,
    selectedAssignment?.title,
  ]);

  const removeSelectedRubricTemplate = useCallback(() => {
    if (!rubricTemplatePickerId) return;
    deleteRubricTemplate(rubricStorageUserId, rubricTemplatePickerId);
    setRubricTemplatePickerId('');
    setRubricTemplatesTick((n) => n + 1);
    logEvent('rubric_template_delete', {});
  }, [rubricStorageUserId, rubricTemplatePickerId]);

  // If the teacher switches assignments, reset rubric UI so scanning starts clean.
  useEffect(() => {
    setCustomRubric('');
    setRubricSuccess(false);
    setIsScanningRubric(false);
    setRubricScanProgress(0);
    setRubricScanError(null);
    setRubricAutoAttempts(0);
    setRubricTemplatePickerId('');
    setRubricQuickSaveTitle('');
  }, [selectedAssignment?.id]);

  useEffect(() => {
    if (selectedCourse && selectedAssignment && selectedCourse.id && selectedAssignment.id) {
      saveLastGradeSession({
        courseId: selectedCourse.id,
        assignmentId: selectedAssignment.id,
        courseName: selectedCourse.name,
        assignmentTitle: selectedAssignment.title,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persist when id + labels change; not whole object refs
  }, [selectedCourse?.id, selectedAssignment?.id, selectedCourse?.name, selectedAssignment?.title]);

  // Phase 2: Voice-to-task (local)
  const GRADE_FOLLOWUPS_KEY = 'dg_grade_followups_v1';
  const QUICK_TODOS_KEY = 'dg_quick_todos_v1';
  const [gradeFollowUps, setGradeFollowUps] = useState<
    { id: string; text: string; createdAt: number; done?: boolean }[]
  >(() =>
    safeParseJson(
      localStorage.getItem(GRADE_FOLLOWUPS_KEY),
      [] as { id: string; text: string; createdAt: number; done?: boolean }[]
    )
  );
  const [quickTodos, setQuickTodos] = useState<
    { id: string; text: string; createdAt: number; done?: boolean }[]
  >(() =>
    safeParseJson(
      localStorage.getItem(QUICK_TODOS_KEY),
      [] as { id: string; text: string; createdAt: number; done?: boolean }[]
    )
  );
  void setGradeFollowUps;
  void setQuickTodos;

  const NAV_USAGE_KEY = 'dg_nav_usage_v1';
  const [navUsage, setNavUsage] = useState<Record<string, number>>(() =>
    safeParseJson(localStorage.getItem(NAV_USAGE_KEY), {} as Record<string, number>)
  );
  const [dragCourseId, setDragCourseId] = useState<string | null>(null);
  void dragCourseId;
  void setDragCourseId;
  const [dragAssignmentId, setDragAssignmentId] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(GRADE_FOLLOWUPS_KEY, JSON.stringify(gradeFollowUps));
    } catch {
      /* ignore */
    }
  }, [gradeFollowUps]);
  useEffect(() => {
    try {
      localStorage.setItem(QUICK_TODOS_KEY, JSON.stringify(quickTodos));
    } catch {
      /* ignore */
    }
  }, [quickTodos]);
  useEffect(() => {
    try {
      localStorage.setItem(NAV_USAGE_KEY, JSON.stringify(navUsage));
    } catch {
      /* ignore */
    }
  }, [navUsage]);

  const [shortcutUsageStore, setShortcutUsageStore] = useState(loadShortcutUsageStore);
  useEffect(() => {
    saveShortcutUsageStore(shortcutUsageStore);
  }, [shortcutUsageStore]);

  const recordShortcutUse = useCallback((key: HomeShortcutKey) => {
    setShortcutUsageStore((prev) => bumpShortcutUsage(prev, key));
  }, []);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const previousPhaseForTeachPrefillRef = useRef<AppPhase>(AppPhase.AUTHENTICATION);

  const sortItems = <T extends { name?: string; title?: string; lastUsed?: number }>(
    items: T[],
    mode: SortMode
  ): T[] => {
    const result = [...items];
    if (mode === 'recent') return result.sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0));
    if (mode === 'alphabetical')
      return result.sort((a, b) =>
        (a.name || a.title || '').localeCompare(b.name || b.title || '')
      );
    return result;
  };

  const [showCourses, setShowCourses] = useState(true);
  const [rosterCourseOpenId, setRosterCourseOpenId] = useState<string | null>(null);
  const [rosterCourseStudents, setRosterCourseStudents] = useState<Record<string, Student[]>>({});
  const [rosterCourseAssignments, setRosterCourseAssignments] = useState<
    Record<string, Assignment[]>
  >({});
  const [rosterSelectedStudentIds, setRosterSelectedStudentIds] = useState<Set<string>>(new Set());
  const [rosterSelectedAssignmentId, setRosterSelectedAssignmentId] = useState<string>('');
  const [rosterLoadingCourseId, setRosterLoadingCourseId] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);

  // Teach tab (in-class timers + random participation)
  const teachInitial = useMemo(() => loadTeachSessionSnapshot(), []);

  const [teachRosterCourseOpenId, setTeachRosterCourseOpenId] = useState<string | null>(
    () => teachInitial?.rosterUi?.courseOpenId ?? null
  );
  const [teachRosterCourseStudents, setTeachRosterCourseStudents] = useState<
    Record<string, Student[]>
  >({});
  const [teachRosterCourseAssignments, setTeachRosterCourseAssignments] = useState<
    Record<string, Assignment[]>
  >({});
  const [teachRosterSelectedStudentIds, setTeachRosterSelectedStudentIds] = useState<Set<string>>(
    () => new Set(teachInitial?.rosterUi?.selectedStudentIds ?? [])
  );
  const [teachRosterSelectedAssignmentId, setTeachRosterSelectedAssignmentId] = useState<string>(
    () => teachInitial?.rosterUi?.selectedAssignmentId ?? ''
  );
  const [teachRosterLoadingCourseId, setTeachRosterLoadingCourseId] = useState<string | null>(null);
  const [teachRosterError, setTeachRosterError] = useState<string | null>(null);

  const [teachTimer, setTeachTimer] = useState<TeachTimerState>(
    () => teachInitial?.timer ?? makeIdleCountdown(DEFAULT_TEACH_TIMER_MS)
  );
  const [teachCohort, setTeachCohort] = useState<TeachCohort | null>(
    () => teachInitial?.cohort ?? null
  );
  const [teachLastRandomPick, setTeachLastRandomPick] = useState<TeachLastRandomPick>(
    () => teachInitial?.lastRandomPick ?? null
  );

  useEffect(() => {
    const id = window.setTimeout(() => {
      saveTeachSessionSnapshot({
        v: 1,
        timer: teachTimer,
        cohort: teachCohort,
        lastRandomPick: teachLastRandomPick,
        rosterUi: {
          courseOpenId: teachRosterCourseOpenId,
          selectedAssignmentId: teachRosterSelectedAssignmentId,
          selectedStudentIds: [...teachRosterSelectedStudentIds],
        },
      });
    }, 500);
    return () => window.clearTimeout(id);
  }, [
    teachTimer,
    teachCohort,
    teachLastRandomPick,
    teachRosterCourseOpenId,
    teachRosterSelectedAssignmentId,
    teachRosterSelectedStudentIds,
  ]);

  useEffect(() => {
    if (teachTimer.status !== 'running') return;
    const intervalId = window.setInterval(() => {
      setTeachTimer((prev) => tickCountdown(prev, Date.now()));
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [teachTimer.status, teachTimer.endsAtMs]);

  const onTeachTimerStart = useCallback(() => {
    setTeachTimer((prev) => {
      const next = startCountdown(prev.durationMs, Date.now());
      return { ...next, durationMs: prev.durationMs };
    });
  }, []);

  const onTeachTimerPause = useCallback(() => {
    setTeachTimer((prev) => pauseCountdown(prev, Date.now()));
  }, []);

  const onTeachTimerResume = useCallback(() => {
    setTeachTimer((prev) => {
      if (prev.status !== 'paused' && prev.status !== 'idle') return prev;
      const remainingMs = prev.status === 'paused' ? prev.remainingMs : prev.durationMs;
      const next = startCountdown(remainingMs, Date.now());
      return { ...next, durationMs: prev.durationMs };
    });
  }, []);

  const onTeachTimerReset = useCallback(() => {
    setTeachTimer((prev) => resetCountdown(prev.durationMs));
  }, []);

  const onTeachCohortSelected = useCallback((c: TeachCohort) => {
    setTeachCohort(c);
    setTeachLastRandomPick(null);
    setTeachTimer((prev) => resetCountdown(prev.durationMs));
  }, []);

  const onPickTeachRandomStudent = useCallback(() => {
    if (!teachCohort || teachCohort.students.length === 0) return;
    const idx = Math.floor(Math.random() * teachCohort.students.length);
    const s = teachCohort.students[idx]!;
    setTeachLastRandomPick({
      studentId: s.id,
      studentName: s.name,
      studentEmail: s.email,
    });
  }, [teachCohort]);

  const [attentionExpanded, setAttentionExpanded] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('dg_attention_expanded_v1');
      return raw === null ? true : raw === 'true';
    } catch {
      // Some browsers/settings block storage; default to expanded so the UI still works.
      return true;
    }
  });
  void attentionExpanded;
  void setAttentionExpanded;
  const [courseSearch, setCourseSearch] = useState('');
  const deferredAssignmentSearch = useDeferredValue(assignmentSearchQuery);
  const deferredCourseSearch = useDeferredValue(courseSearch);
  const deferredScheduleSearch = useDeferredValue(scheduleSearch);
  const deferredBatchRosterQuery = useDeferredValue(batchRosterQuery);
  const deferredGlobalSearch = useDeferredValue(globalSearchQuery);
  const [showLast7Details, setShowLast7Details] = useState(false);
  void showLast7Details;
  void setShowLast7Details;
  const [pinnedCourseIds, setPinnedCourseIds] = useState<string[]>(() => {
    const parsed = safeParseJson<unknown>(localStorage.getItem('dg_pinned_courses_v1'), []);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  });

  const [voiceInboxExpanded, setVoiceInboxExpanded] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('dg_voice_inbox_expanded_v1');
      return raw === null ? false : raw === 'true';
    } catch {
      return false;
    }
  });
  void voiceInboxExpanded;
  void setVoiceInboxExpanded;

  useEffect(() => {
    try {
      localStorage.setItem('dg_voice_inbox_expanded_v1', voiceInboxExpanded ? 'true' : 'false');
    } catch {
      // Ignore storage errors; UI should continue to work.
    }
  }, [voiceInboxExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem('dg_attention_expanded_v1', attentionExpanded ? 'true' : 'false');
    } catch {
      // Ignore storage errors; UI should continue to work.
    }
  }, [attentionExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem('dg_pinned_courses_v1', JSON.stringify(pinnedCourseIds));
    } catch {
      // Ignore storage errors; UI should continue to work.
    }
  }, [pinnedCourseIds]);

  useEffect(() => {
    try {
      localStorage.setItem('dg_scan_student_mode_v1', scanStudentMode);
    } catch {
      // Ignore storage errors; UI should continue to work.
    }
  }, [scanStudentMode]);

  useEffect(() => {
    try {
      localStorage.setItem('dg_batch_roster_sort_v1', batchRosterSort);
    } catch {
      // Ignore storage errors; UI should continue to work.
    }
  }, [batchRosterSort]);

  useEffect(() => {
    if (batchRosterSort !== 'manual' && batchRosterOrderIds.length > 0) {
      setBatchRosterOrderIds([]);
    }
  }, [batchRosterSort, batchRosterOrderIds.length]);

  const executeOptionsDataConfirm = useCallback(
    (kind: NonNullable<OptionsConfirmState['kind']>) => {
      setOptionsConfirm({ open: false, kind: null });
      try {
        if (kind === 'clear_classroom') {
          setCourses([]);
          setAssignments([]);
          setStudents([]);
          localStorage.removeItem('dg_cache_courses');
          localStorage.removeItem('dg_cache_assignments');
          localStorage.removeItem('dg_cache_students');
          clearLastGradeSession();
        }
        if (kind === 'clear_grades') {
          clearOfflineGradeSyncIntent();
          setGradedWorks([]);
          setHistory([]);
          localStorage.removeItem('dg_pending_sync');
          localStorage.removeItem('dg_history');
        }
        if (kind === 'clear_schedule') {
          setScheduleItems([]);
          localStorage.removeItem(SCHEDULE_KEY);
        }
        if (kind === 'reset_all') {
          clearOfflineActionQueue();
          clearOfflineGradeSyncIntent();
          setCourses([]);
          setAssignments([]);
          setStudents([]);
          setGradedWorks([]);
          setHistory([]);
          setScheduleItems([]);
          setPinnedCourseIds([]);
          setScanStudentMode('batch');
          setBatchRosterSort('manual');
          setIsDarkMode(false);
          setOptionsDataPanelOpen(false);

          [
            'dg_cache_courses',
            'dg_cache_assignments',
            'dg_cache_students',
            'dg_pending_sync',
            'dg_history',
            SCHEDULE_KEY,
            'dg_schedule_view_v1',
            'dg_pinned_courses_v1',
            'dg_scan_student_mode_v1',
            'dg_batch_roster_sort_v1',
            'dg_dark_mode',
            LAST_GRADE_SESSION_STORAGE_KEY,
            'dg_options_ui_v1',
          ].forEach((k) => {
            try {
              localStorage.removeItem(k);
            } catch {
              // ignore
            }
          });
        }
      } catch {
        // ignore
      }
    },
    [SCHEDULE_KEY]
  );

  const dashboardResults = useMemo(() => {
    const query = deferredGlobalSearch.toLowerCase().trim();
    let filteredCourses = sortItems([...courses], dashboardSort);
    let filteredAssignments = sortItems([...assignments], assignmentSort);

    if (query) {
      filteredCourses = filteredCourses.filter(
        (c) => c.name.toLowerCase().includes(query) || c.period.toLowerCase().includes(query)
      );
      filteredAssignments = filteredAssignments.filter((a) =>
        a.title.toLowerCase().includes(query)
      );
    }

    return { courses: filteredCourses, assignments: filteredAssignments };
  }, [courses, assignments, deferredGlobalSearch, dashboardSort, assignmentSort]);

  const filteredAssignmentsList = useMemo(() => {
    let result = sortItems([...assignments], assignmentSort);
    if (deferredAssignmentSearch.trim()) {
      const query = deferredAssignmentSearch.toLowerCase();
      result = result.filter((assignment) => assignment.title.toLowerCase().includes(query));
    }
    return result;
  }, [assignments, deferredAssignmentSearch, assignmentSort]);

  // Viewport setup to absolutely prevent zooming when typing on mobile
  useEffect(() => {
    let metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) {
      metaViewport = document.createElement('meta');
      metaViewport.setAttribute('name', 'viewport');
      document.head.appendChild(metaViewport);
    }
    metaViewport.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
    );
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(ELITE_FLAGS_KEY, JSON.stringify(eliteFlags));
    } catch {
      // ignore
    }
  }, [eliteFlags]);
  useEffect(() => {
    try {
      localStorage.setItem(ELITE_METRICS_KEY, JSON.stringify(eliteMetrics));
    } catch {
      // ignore
    }
  }, [eliteMetrics]);

  useEffect(
    () => localStorage.setItem('dg_pending_sync', JSON.stringify(gradedWorks)),
    [gradedWorks]
  );
  useEffect(() => {
    try {
      localStorage.setItem(SCHEDULE_KEY, JSON.stringify(scheduleItems));
    } catch {
      // ignore
    }
  }, [scheduleItems]);
  useEffect(() => {
    const id = window.setTimeout(() => {
      saveScheduleUiSnapshot({
        v: 1,
        view: scheduleView,
        cursor: scheduleCursor,
        search: scheduleSearch,
        filterKinds: [...scheduleFilterKinds],
        filterCourseId: scheduleFilterCourseId,
        filterOverdueOnly: scheduleFilterOverdueOnly,
        filterRecurringOnly: scheduleFilterRecurringOnly,
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [
    scheduleView,
    scheduleCursor,
    scheduleSearch,
    scheduleFilterKinds,
    scheduleFilterCourseId,
    scheduleFilterOverdueOnly,
    scheduleFilterRecurringOnly,
  ]);
  useEffect(() => {
    try {
      localStorage.setItem('dg_schedule_view_v1', scheduleView);
    } catch {
      // ignore
    }
  }, [scheduleView]);
  useEffect(() => localStorage.setItem('dg_history', JSON.stringify(history)), [history]);
  useEffect(() => localStorage.setItem('dg_cache_courses', JSON.stringify(courses)), [courses]);
  useEffect(
    () => localStorage.setItem('dg_cache_assignments', JSON.stringify(assignments)),
    [assignments]
  );
  useEffect(() => localStorage.setItem('dg_cache_students', JSON.stringify(students)), [students]);

  useEffect(() => {
    if (phase === AppPhase.GRADING_LOOP) {
      setIsProcessing(false);
      cooldownRef.current = false;
      setScanHealth(0);
      setActiveGeometry(null);
      setIsFlashOn(false);
      setCameraError(null);
      setMultiPageCapture({ croppedDataUrls: [], apiBase64s: [], detectedStudentName: undefined });
      setMultiPageHint(null);
      multiPageCaptureInFlightRef.current = false;
      multiPageLastHashRef.current = null;
    }
  }, [phase]);

  const loadCourses = async (): Promise<boolean> => {
    if (!classroom || !isOnline) return false;
    setIsSyncingClassroom(true);
    try {
      const courseData = await classroom.getCourses();
      setCourses(courseData);
      setSyncStatus('ok');
      setClassroomConfigBlocked(false);
      return true;
    } catch (err) {
      console.error('Failed to load courses:', err);
      const staleOAuth = isStaleOAuthProjectError(err);
      if (staleOAuth) {
        clearAuthSession();
        setAccessToken(null);
        setClassroom(null);
        setClassroomConfigBlocked(false);
      } else if (isClassroomApiConfigError(err)) {
        setClassroomConfigBlocked(true);
      }
      setAuthError(classroomSyncErrorMessage(err));
      setSyncStatus('error');
      return false;
    } finally {
      setIsSyncingClassroom(false);
    }
  };

  /** Refresh Classroom courses + current class assignments/students (header “Retry” on sync error). */
  const retryClassroomSync = () => {
    if (!classroom || !isOnline) return;
    appendSyncLog({
      level: 'info',
      message: t('syncLog.classroomSyncStarted'),
    });
    void (async () => {
      try {
        const coursesOk = await loadCourses();
        if (!coursesOk) return;
        if (selectedCourse && selectedCourse.source !== 'local') {
          const [assignmentData, studentData] = await Promise.all([
            classroom.getAssignments(selectedCourse.id),
            classroom.getStudents(selectedCourse.id),
          ]);
          setAssignments(assignmentData);
          setStudents(studentData);
        }
      } catch (err) {
        console.error('Manual sync failed', err);
        setSyncStatus('error');
      }
    })();
  };

  /** After connectivity returns, refresh Classroom mirror (roadmap §1.3 offline / reconnect). */
  const wasOfflineForClassroomRef = useRef(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );
  useEffect(() => {
    if (!isOnline) {
      wasOfflineForClassroomRef.current = true;
      return;
    }
    if (wasOfflineForClassroomRef.current && classroom) {
      wasOfflineForClassroomRef.current = false;
      retryClassroomSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- retryClassroomSync closes over latest classroom/selection
  }, [isOnline, classroom]);

  /** Focus main landmark after entering major grading / sync phases (a11y roadmap §5.1). */
  const phasesFocusMainContent = useMemo(
    () =>
      new Set<AppPhase>([
        AppPhase.GRADE_COURSE_PICKER,
        AppPhase.ROSTER_VIEW,
        AppPhase.CLASSROOM,
        AppPhase.COURSE_CREATION,
        AppPhase.ASSIGNMENT_SELECT,
        AppPhase.ASSIGNMENT_CREATION,
        AppPhase.GRADE_SCAN_SETUP,
        AppPhase.GRADE_BATCH_ROSTER,
        AppPhase.RUBRIC_SETUP,
        AppPhase.MODE_SELECTION,
        AppPhase.GRADING_LOOP,
        AppPhase.AUDIT,
        AppPhase.GRADE_SYNC_PREFLIGHT,
        AppPhase.SYNCING,
        AppPhase.FINALE,
      ]),
    []
  );
  useEffect(() => {
    if (!phasesFocusMainContent.has(phase)) return;
    const id = window.requestAnimationFrame(() => {
      document.getElementById('main-content')?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [phase, phasesFocusMainContent]);

  useEffect(() => {
    setAuditStudioFocusIdx(null);
  }, [auditFilter]);

  useEffect(() => {
    if (phase !== AppPhase.AUDIT) {
      setAuditStudioFocusIdx(null);
      setAuditQueueSearch('');
    }
  }, [phase]);

  const auditVisibleFiltered = useMemo(() => {
    if (phase !== AppPhase.AUDIT) return [];
    const v = getAuditVisibleRows(gradedWorks, auditFilter);
    return filterVisibleBySearch(v, auditQueueSearch);
  }, [phase, gradedWorks, auditFilter, auditQueueSearch]);

  const auditResolvedFocus = useMemo(() => {
    if (phase !== AppPhase.AUDIT) return null;
    return getAuditResolvedFocus(auditVisibleFiltered, auditStudioFocusIdx, auditEditIndex);
  }, [phase, auditVisibleFiltered, auditStudioFocusIdx, auditEditIndex]);

  const auditStatsBundle = useMemo(() => countAuditStats(gradedWorks), [gradedWorks]);

  const onAuditAutosaveCommit = useCallback((index: number, scoreStr: string, feedback: string) => {
    setGradedWorks((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const parsed = Number(scoreStr);
      const clampedScore = Number.isFinite(parsed)
        ? Math.max(0, Math.min(row.maxScore, parsed))
        : row.score;
      return prev.map((w, i) =>
        i === index ? { ...w, score: clampedScore, feedback, status: 'draft' as const } : w
      );
    });
  }, []);

  // Periodic background sync for courses so changes made directly in Google Classroom stay mirrored
  useEffect(() => {
    if (!classroom || !isOnline || phase !== AppPhase.DASHBOARD || classroomConfigBlocked) return;
    const id = window.setInterval(() => {
      void loadCourses();
    }, 20000);
    return () => window.clearInterval(id);
  }, [classroom, isOnline, phase, classroomConfigBlocked]); // eslint-disable-line react-hooks/exhaustive-deps -- loadCourses not stable; omitting avoids resetting interval every render

  // Attention items for dashboard (used for notification and UI)
  const dashboardAttention = useMemo(() => {
    const studentStats: Record<string, { name: string; totalScore: number; totalMax: number }> = {};
    history.forEach((w) => {
      if (!w.studentId) return;
      if (!studentStats[w.studentId]) {
        studentStats[w.studentId] = {
          name: w.studentName || 'Student',
          totalScore: 0,
          totalMax: 0,
        };
      }
      studentStats[w.studentId].totalScore += w.score;
      studentStats[w.studentId].totalMax += w.maxScore || 100;
    });
    const atRisk = Object.values(studentStats)
      .map((s) => ({ ...s, pct: s.totalMax > 0 ? (s.totalScore / s.totalMax) * 100 : 0 }))
      .filter((s) => s.pct < 70)
      .sort((a, b) => a.pct - b.pct)
      .slice(0, 3);
    return { pendingGrades: gradedWorks.length, atRiskStudents: atRisk };
  }, [history, gradedWorks.length]);

  /** Drafts / rows not yet posted to Classroom (for nav chip + home “waiting to sync” copy). */
  const pendingClassroomSyncCount = useMemo(
    () => gradedWorks.filter((w) => w.status !== 'synced').length,
    [gradedWorks]
  );

  const homeScheduleItemsLite = useMemo<HomeScheduleItemLite[]>(
    () =>
      scheduleItems.map((s) => ({
        id: s.id,
        title: s.title,
        date: s.date,
        kind: s.kind,
        time: s.time,
      })),
    [scheduleItems]
  );

  const homeUrgentAlerts = useMemo(
    () =>
      buildHomeUrgentAlerts({
        syncStatus,
        pendingSyncCount: pendingClassroomSyncCount,
        parentFollowUpsOpen: gradeFollowUps.filter((f) => !f.done).length,
        scheduleItems: homeScheduleItemsLite,
      }),
    [syncStatus, pendingClassroomSyncCount, gradeFollowUps, homeScheduleItemsLite]
  );

  const homeWeekInsights = useMemo(() => buildHomeWeekInsights(history), [history]);

  const homeShortcutOrder = useMemo(
    () => rankHomeShortcuts(shortcutUsageStore, 6),
    [shortcutUsageStore]
  );

  const resumeGradeSessionBanner = useMemo(() => {
    const saved = loadLastGradeSession();
    if (!saved) return null;
    if (!isOnline || !classroom) return null;
    const course = courses.find((c) => c.id === saved.courseId);
    if (!course || course.source === 'local') return null;
    if (selectedCourse?.id === saved.courseId && selectedAssignment?.id === saved.assignmentId) {
      return null;
    }
    return {
      courseName: saved.courseName,
      assignmentTitle: saved.assignmentTitle,
    };
  }, [isOnline, classroom, courses, selectedCourse?.id, selectedAssignment?.id]);

  // Summary for the signed-in home screen (AuthView when isSignedIn)
  const homeSummary = useMemo(() => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sameDay = (isoDate: string) => {
      const d = new Date(isoDate);
      if (Number.isNaN(d.getTime())) return false;
      d.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    };

    // Lesson today: first teacher block on today's schedule
    const teacherBlocksToday = scheduleItems
      .filter((item) => (item.kind ?? 'reminder') === 'teacherBlock' && sameDay(item.date))
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

    let lesson:
      | {
          title: string;
          course?: string;
          timeLabel?: string;
          scheduleItemId?: string;
          scheduleDate?: string;
        }
      | undefined;
    if (teacherBlocksToday.length > 0) {
      const item = teacherBlocksToday[0];
      const courseName = item.courseId
        ? courses.find((c) => c.id === item.courseId)?.name
        : undefined;
      let timeLabel: string | undefined;
      if (item.time) {
        const [hRaw, mRaw] = item.time.split(':');
        const h = Number(hRaw);
        const m = Number(mRaw ?? '0');
        if (!Number.isNaN(h) && !Number.isNaN(m)) {
          const start = new Date();
          start.setHours(h, m, 0, 0);
          const diffMs = start.getTime() - now.getTime();
          if (diffMs > 0) {
            const diffMins = Math.round(diffMs / 60000);
            if (diffMins < 60) {
              timeLabel = `Starts in ${diffMins} minute${diffMins === 1 ? '' : 's'}`;
            } else {
              const diffHours = Math.round(diffMins / 60);
              timeLabel = `Starts in ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
            }
          } else {
            timeLabel = 'Happening now or earlier today';
          }
        }
      }
      if (!timeLabel) {
        timeLabel = 'Any time today';
      }
      lesson = {
        title: item.title,
        course: courseName,
        timeLabel,
        scheduleItemId: item.id,
        scheduleDate: formatLocalYMD(today),
      };
    }

    const assignmentsToGrade = dashboardAttention.pendingGrades;

    const parentsToContact = gradeFollowUps.filter((f) => !f.done).length;

    // Upcoming: next scheduled item in the future (any kind)
    const upcomingCandidates = scheduleItems
      .map((item) => {
        const d = new Date(item.date);
        if (Number.isNaN(d.getTime())) return null;
        if (item.time) {
          const [hRaw, mRaw] = item.time.split(':');
          const h = Number(hRaw);
          const m = Number(mRaw ?? '0');
          if (!Number.isNaN(h) && !Number.isNaN(m)) {
            d.setHours(h, m, 0, 0);
          }
        }
        return { item, when: d };
      })
      .filter(
        (x): x is { item: ScheduleItem; when: Date } => !!x && x.when.getTime() > now.getTime()
      )
      .sort((a, b) => a.when.getTime() - b.when.getTime());

    let upcoming: { title: string; whenLabel: string } | undefined;
    if (upcomingCandidates.length > 0) {
      const { item, when } = upcomingCandidates[0];
      let whenLabel = '';
      const diffMs = when.getTime() - now.getTime();
      const oneDayMs = 24 * 60 * 60 * 1000;
      if (sameDay(when.toISOString().slice(0, 10))) {
        whenLabel = `Later today at ${when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
      } else if (diffMs > 0 && diffMs < oneDayMs * 2) {
        whenLabel = `Tomorrow at ${when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
      } else {
        try {
          whenLabel = when.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
        } catch {
          whenLabel = 'Coming up';
        }
      }
      upcoming = {
        title: item.title,
        whenLabel,
      };
    }

    let todayShortLabel = '';
    try {
      todayShortLabel = new Date().toLocaleDateString(undefined, {
        weekday: 'long',
      });
    } catch {
      todayShortLabel = '';
    }

    return {
      todayShortLabel,
      lesson,
      assignmentsToGrade,
      parentsToContact,
      upcoming,
      scheduleHighlights: buildHomeScheduleHighlights(homeScheduleItemsLite),
    };
  }, [scheduleItems, courses, dashboardAttention, gradeFollowUps, homeScheduleItemsLite]);

  const homeTopPriorityActions = useMemo(
    () =>
      buildHomeTopPriorityActions({
        accessToken,
        syncStatus,
        pendingSyncCount: pendingClassroomSyncCount,
        assignmentsToGrade: homeSummary.assignmentsToGrade,
        parentsToContact: homeSummary.parentsToContact,
        lesson: homeSummary.lesson,
      }),
    [
      accessToken,
      syncStatus,
      pendingClassroomSyncCount,
      homeSummary.assignmentsToGrade,
      homeSummary.parentsToContact,
      homeSummary.lesson,
    ]
  );

  const dashboardNotifiedRef = useRef<{ pending: number; atRisk: number } | null>(null);

  // Push notification when Grade screen has tasks needing attention
  useEffect(() => {
    if (phase !== AppPhase.DASHBOARD) return;
    const { pendingGrades, atRiskStudents } = dashboardAttention;
    const hasAttention = pendingGrades > 0 || atRiskStudents.length > 0;
    if (!hasAttention) return;
    if (
      dashboardNotifiedRef.current &&
      dashboardNotifiedRef.current.pending === pendingGrades &&
      dashboardNotifiedRef.current.atRisk === atRiskStudents.length
    )
      return;
    dashboardNotifiedRef.current = { pending: pendingGrades, atRisk: atRiskStudents.length };

    if (typeof window === 'undefined' || !('Notification' in window)) return;
    const requestAndNotify = () => {
      if (Notification.permission === 'granted') {
        const parts: string[] = [];
        if (pendingGrades > 0)
          parts.push(`${pendingGrades} grade${pendingGrades === 1 ? '' : 's'} ready to sync`);
        if (atRiskStudents.length > 0)
          parts.push(
            `${atRiskStudents.length} student${atRiskStudents.length === 1 ? '' : 's'} to check in on`
          );
        if (parts.length) {
          new Notification('DoneGrading – What needs your attention', {
            body: parts.join(' · '),
            icon: '/donegrading-logo.png',
          });
        }
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((p) => {
          if (p === 'granted') {
            const parts: string[] = [];
            if (pendingGrades > 0)
              parts.push(`${pendingGrades} grade${pendingGrades === 1 ? '' : 's'} ready to sync`);
            if (atRiskStudents.length > 0)
              parts.push(
                `${atRiskStudents.length} student${atRiskStudents.length === 1 ? '' : 's'} to check in on`
              );
            if (parts.length) {
              new Notification('DoneGrading – What needs your attention', {
                body: parts.join(' · '),
                icon: '/donegrading-logo.png',
              });
            }
          }
        });
      }
    };
    const t = window.setTimeout(requestAndNotify, 800);
    return () => window.clearTimeout(t);
  }, [phase, dashboardAttention]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthSuccessMessage(null);
    if (authMode === 'signup' && (!firstName.trim() || !lastName.trim())) {
      setAuthError('Please enter your first and last name.');
      return;
    }
    if (!email.trim() || !password) {
      setAuthError('Please enter your email and password.');
      return;
    }
    if (!isOnline) {
      setAuthError('You are offline. Connect to the internet to sign in.');
      return;
    }
    setAuthError(null);
    try {
      if (authMode === 'signup') {
        const userCred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        // Compose the display name from salutation + first + last so the owner
        // notification emails surface the full name the teacher entered.
        const name = [salutation.trim(), firstName.trim(), lastName.trim()]
          .filter(Boolean)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (name && userCred.user) {
          await updateProfile(userCred.user, { displayName: name });
          void sendSignupAttribution(userCred.user.uid, {
            signupDisplayName: name,
            signupSalutation: salutation,
            signupFirstName: firstName,
            signupLastName: lastName,
          });
          setEducatorName(name);
          localStorage.setItem('dg_educator_name', name);
        }
        setCourses([]);
        setAssignments([]);
        setStudents([]);
        setGradedWorks([]);
        setHistory([]);
        localStorage.removeItem('dg_cache_courses');
        localStorage.removeItem('dg_cache_assignments');
        localStorage.removeItem('dg_cache_students');
        localStorage.removeItem('dg_pending_sync');
        localStorage.removeItem('dg_history');
        logEvent('auth_email_sign_up');
        setPhaseRaw(AppPhase.AUTHENTICATION);
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
        setCourses([]);
        setAssignments([]);
        setStudents([]);
        setGradedWorks([]);
        setHistory([]);
        localStorage.removeItem('dg_cache_courses');
        localStorage.removeItem('dg_cache_assignments');
        localStorage.removeItem('dg_cache_students');
        localStorage.removeItem('dg_pending_sync');
        localStorage.removeItem('dg_history');
        logEvent('auth_email_sign_in');
        setPhaseRaw(AppPhase.AUTHENTICATION);
      }
    } catch (err: unknown) {
      const code = getUnknownErr(err).code;
      if (authMode === 'signin' && code === 'auth/user-not-found') {
        setAuthError('No account found. Switch to "Sign up" to create one.');
      } else if (authMode === 'signup' && code === 'auth/email-already-in-use') {
        setAuthError('This email is already registered. Switch to "Sign in" to log in.');
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setAuthError('Incorrect password. Please try again.');
      } else if (code === 'auth/invalid-email') {
        setAuthError('Please enter a valid email address.');
      } else if (code === 'auth/weak-password') {
        setAuthError('Password should be at least 6 characters.');
      } else {
        setAuthError(
          getUnknownErr(err).message.replace('Firebase: ', '') ||
            'Sign in failed. Please try again.'
        );
      }
    }
  };

  const handlePasswordReset = async () => {
    setAuthSuccessMessage(null);
    if (!email.trim()) {
      setAuthError('Enter your email above to reset your password.');
      return;
    }
    if (!isOnline) {
      setAuthError('You are offline. Connect to the internet to reset your password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setAuthError(null);
      setAuthSuccessMessage('Check your email/spam for a link to reset your password.');
    } catch (err: unknown) {
      setAuthSuccessMessage(null);
      const code = getUnknownErr(err).code;
      if (code === 'auth/user-not-found') {
        setAuthError('No account found with that email.');
      } else if (code === 'auth/invalid-email') {
        setAuthError('Please enter a valid email address.');
      } else if (code === 'auth/too-many-requests') {
        setAuthError('Too many reset attempts. Try again later.');
      } else {
        setAuthError(
          getUnknownErr(err).message.replace('Firebase: ', '') ||
            'Could not send reset email. Please try again.'
        );
      }
    }
  };

  const classroomTokenHooks = useMemo<ClassroomTokenHooks>(
    () => ({
      refreshAccessToken: async () => {
        const clientId = getGoogleOAuthClientId();
        if (!clientId) return null;
        const silent = await requestGoogleAccessToken({ clientId, prompt: '' });
        if (silent) return silent;
        return requestGoogleAccessToken({ clientId, prompt: 'consent' });
      },
      onTokenRotated: (token: string) => {
        setAccessToken(token);
        try {
          const name = localStorage.getItem('dg_educator_name') || '';
          saveAuthSession(token, name);
        } catch {
          /* ignore */
        }
      },
    }),
    []
  );

  // Shared logic: complete sign-in after we have an access token (used by both popup callback and redirect hash)
  const completeGoogleSignIn = useCallback(
    (token: string) => {
      setClassroomConfigBlocked(false);
      setAccessToken(token);
      const service = new ClassroomService(token, classroomTokenHooks);
      setClassroom(service);
      setAuthError(null);
      logEvent('auth_google_sign_in');
      setPhaseRaw(AppPhase.AUTHENTICATION);
      saveAuthSession(token, '');

      fetch('https://classroom.googleapis.com/v1/userProfiles/me', {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((profileData) => {
          if (profileData.name?.fullName) {
            const name = profileData.name.fullName;
            setEducatorName(name);
            localStorage.setItem('dg_educator_name', name);
            saveAuthSession(token, name);
          }
        })
        .catch((e) => console.error('Could not fetch educator profile name', e));

      service
        .getCourses()
        .then((data) => {
          setClassroomConfigBlocked(false);
          setCourses(data);
        })
        .catch((e) => {
          console.error('Could not load courses', e);
          setAuthError(classroomSyncErrorMessage(e));
          setSyncStatus('error');
        });
    },
    [classroomTokenHooks]
  );

  // OAuth return must be read before grading-only forces #/grade.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isOAuthHash = (hash: string) => {
      const body = hash.replace(/^#/, '');
      return body.includes('access_token=') || body.includes('error=');
    };

    const tokenFromUrl = parseOAuthReturnToken();

    if (tokenFromUrl && !GRADING_ONLY_MODE) {
      completeGoogleSignIn(tokenFromUrl);
      window.history.replaceState(
        null,
        '',
        GRADING_ONLY_MODE ? '#/grade' : window.location.pathname + window.location.search
      );
      return;
    }

    if (!GRADING_ONLY_MODE) return;
    // When the app shell is enabled, the dedicated app-shell hash effect below
    // owns hash<->phase sync (accepts #/home, #/grade, #/plan, #/communicate,
    // #/manage). Skip the legacy #/grade forcing so the two don't fight.
    if (APP_SHELL_ENABLED) return;
    if (firebaseAuthLoading || !firebaseAuthVerified) {
      devLogAuthGate('App boot route guard', {
        authLoading: firebaseAuthLoading,
        currentUserExists: !!auth.currentUser,
        currentRoute: currentRouteForLog(),
        redirectDecision: firebaseAuthLoading ? 'show-loading' : 'show-sign-in',
      });
      return;
    }

    const hashBefore = window.location.hash;
    const willWipe = hashBefore !== '#/grade' && !isOAuthHash(hashBefore) && !hasOAuthReturnInUrl();
    if (willWipe) {
      window.history.replaceState(null, '', '#/grade');
    }

    const onHash = () => {
      const hashBefore = window.location.hash;
      if (hashBefore && hashBefore !== '#/grade' && !isOAuthHash(hashBefore)) {
        window.history.replaceState(null, '', '#/grade');
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [completeGoogleSignIn, firebaseAuthLoading, firebaseAuthVerified]);

  // App-shell hash <-> phase sync (only when VITE_APP_SHELL is enabled).
  // Accepts the five app-shell routes; normalizes unknown/empty hashes to
  // #/home. OAuth-return hashes/tokens are preserved (handled first) so the
  // sign-in round-trip is never clobbered.
  useEffect(() => {
    if (!APP_SHELL_ENABLED) return;
    if (typeof window === 'undefined') return;
    if (firebaseAuthLoading || !firebaseAuthVerified) return;

    const isOAuthHash = (hash: string) => {
      const body = hash.replace(/^#/, '');
      return body.includes('access_token=') || body.includes('error=');
    };

    const syncFromHash = () => {
      const hash = window.location.hash;
      if (isOAuthHash(hash) || hasOAuthReturnInUrl()) return;
      // Demo route (Onboarding 1C): only honored when the demo flag is on.
      // Otherwise it falls back safely to Home so a stale/disabled #/demo link
      // never exposes the demo or wedges navigation.
      if (isDemoWorkflowHash(hash)) {
        if (DEMO_WORKFLOW_ENABLED) {
          setPhase(AppPhase.DEMO_WORKFLOW);
        } else {
          if (hash !== '#/home') {
            window.history.replaceState(null, '', '#/home');
          }
          setPhase(AppPhase.HOME);
        }
        return;
      }
      const target = appShellPhaseFromHash(hash);
      if (target === null) {
        if (hash !== '#/home') {
          window.history.replaceState(null, '', '#/home');
        }
        setPhase(AppPhase.HOME);
        return;
      }
      setPhase(target);
    };

    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [firebaseAuthLoading, firebaseAuthVerified, setPhase]);

  // Restore session on load if user signed in within the last week
  useEffect(() => {
    if (parseOAuthReturnToken()) return;
    if (GRADING_ONLY_MODE) return;
    const session = loadAuthSession();
    if (session?.accessToken) {
      setAccessToken(session.accessToken);
      const service = new ClassroomService(session.accessToken, classroomTokenHooks);
      setClassroom(service);
      if (session.educatorName) {
        setEducatorName(session.educatorName);
      }
      void service.getCourses().then(
        (courseData) => {
          setCourses(courseData);
          setSyncStatus('ok');
          setClassroomConfigBlocked(false);
        },
        (err) => {
          const staleOAuth = isStaleOAuthProjectError(err);
          if (staleOAuth) {
            clearAuthSession();
            setAccessToken(null);
            setClassroom(null);
          } else if (isClassroomApiConfigError(err)) {
            setClassroomConfigBlocked(true);
          }
          setAuthError(classroomSyncErrorMessage(err));
          setSyncStatus('error');
        }
      );
    }
  }, [classroomTokenHooks]);

  useEffect(() => {
    if (!hasGoogleRedirectPendingMarker()) {
      setFirebaseRedirectResolving(false);
      return;
    }

    let cancelled = false;
    setFirebaseRedirectResolving(true);
    setAuthError(null);
    setAuthSuccessMessage('Finishing Google sign-in...');

    void (async () => {
      try {
        const result = await completeGoogleRedirectSignIn();
        if (cancelled) return;
        setAuthSuccessMessage(null);
        setFirebaseRedirectResolving(false);
        if (result === 'none' && !auth.currentUser) {
          setFirebaseAuthLoading(false);
          setPhaseRaw(AppPhase.AUTHENTICATION);
        }
      } catch (err) {
        if (cancelled) return;
        setAuthSuccessMessage(null);
        setFirebaseRedirectResolving(false);
        setFirebaseAuthLoading(false);
        const msg = err instanceof Error ? err.message : String(err);
        setAuthError(msg.replace('Firebase: ', '') || 'Google sign-in could not finish.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Sync Firebase Auth state and verify a fresh ID token before unlocking protected routes.
  useEffect(() => {
    let generation = 0;
    const unsub = onAuthStateChanged(auth, (user) => {
      const authGeneration = ++generation;
      setFirebaseAuthLoading(true);
      setFirebaseUser(user);
      setFirebaseIdTokenVerified(false);
      setVerifiedFirebaseTokenLength(0);
      devLogAuthGate('App boot auth state', {
        authLoading: true,
        currentUserExists: !!user,
        currentRoute: currentRouteForLog(),
        redirectDecision: user ? 'verify-firebase-token' : 'show-sign-in',
      });
      if (!user) {
        if (hasGoogleRedirectPendingMarker()) {
          setFirebaseAuthLoading(true);
          return;
        }
        setFirebaseAuthLoading(false);
        setPhaseRaw(AppPhase.AUTHENTICATION);
        devLogAuthGate('App boot auth state', {
          authLoading: false,
          currentUserExists: false,
          currentRoute: currentRouteForLog(),
          redirectDecision: 'show-sign-in',
        });
        return;
      }

      void (async () => {
        try {
          const idToken = await user.getIdToken(true);
          if (authGeneration !== generation) return;
          setFirebaseUser(user);
          setFirebaseIdTokenVerified(true);
          setVerifiedFirebaseTokenLength(idToken.length);
          setFirebaseAuthLoading(false);
          const name = user.displayName || user.email?.split('@')[0] || 'Teacher';
          setEducatorName(name);
          localStorage.setItem('dg_educator_name', name);
          // Best-effort: deliver captured marketing attribution for the owner
          // signup notification. Never blocks auth.
          void sendSignupAttribution(user.uid);
          // Activation (Phase 3): a daily heartbeat drives returning-day
          // milestones and recency-based churn. Deduped to one event per UTC
          // day per user so reloads don't spam the pipeline.
          void recordActivationEvent(
            'DAY_ACTIVE',
            {},
            {
              clientEventId: `day:${user.uid}:${new Date().toISOString().slice(0, 10)}`,
            }
          );
          devLogAuthGate('App boot auth state', {
            authLoading: false,
            currentUserExists: true,
            currentRoute: currentRouteForLog(),
            redirectDecision: 'allow-navigation',
          });
        } catch {
          if (authGeneration !== generation) return;
          setFirebaseUser(null);
          setFirebaseIdTokenVerified(false);
          setVerifiedFirebaseTokenLength(0);
          setFirebaseAuthLoading(false);
          setAuthError('Please sign in to DoneGrading again.');
          setPhaseRaw(AppPhase.AUTHENTICATION);
          devLogAuthGate('App boot auth state', {
            authLoading: false,
            currentUserExists: true,
            currentRoute: currentRouteForLog(),
            redirectDecision: 'token-failed-show-sign-in',
          });
          void firebaseSignOut(auth);
        }
      })();
    });
    return () => {
      generation += 1;
      unsub();
    };
  }, []);

  useEffect(() => {
    if (DEMO_GRADE_ONLY || !GRADING_ONLY_MODE || firebaseAuthLoading || firebaseAuthVerified) {
      return;
    }
    if (phase !== AppPhase.AUTHENTICATION) {
      devLogAuthGate('App boot route guard', {
        authLoading: false,
        currentUserExists: !!auth.currentUser,
        currentRoute: currentRouteForLog(),
        redirectDecision: 'show-sign-in',
      });
      setPhaseRaw(AppPhase.AUTHENTICATION);
    }
  }, [firebaseAuthLoading, firebaseAuthVerified, phase]);

  useEffect(() => {
    if (phase !== AppPhase.SCAN_WORKFLOW || !firebaseAuthVerified) return;
    devLogAuthGate('Before Grade render', {
      currentUserExists: !!auth.currentUser,
      getIdTokenSuccess: true,
      tokenLength: verifiedFirebaseTokenLength,
    });
  }, [firebaseAuthVerified, phase, verifiedFirebaseTokenLength]);

  // Extend session on activity (phase change, visibility) so 7‑day window resets with use
  useEffect(() => {
    if (!accessToken) return;
    touchAuthSession();
  }, [accessToken, phase]);
  useEffect(() => {
    if (!accessToken) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') touchAuthSession();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [accessToken]);

  const handleFirebaseGoogleSignIn = useCallback(async () => {
    if (!isOnline) {
      setAuthError('You are offline. Connect to the internet to sign in with Google.');
      return;
    }
    setAuthError(null);
    setAuthSuccessMessage(null);
    try {
      const result = await signInWithGoogleFirebase({
        onRedirectStart: () => {
          setAuthError(null);
          setAuthSuccessMessage('Redirecting to Google to finish sign-in...');
        },
      });
      if (result === 'popup') {
        logEvent('auth_google_sign_in');
        setPhaseRaw(AppPhase.AUTHENTICATION);
      }
    } catch (err: unknown) {
      setAuthSuccessMessage(null);
      const msg = err instanceof Error ? err.message : String(err);
      if (/popup_closed|cancelled|canceled|user_cancel/i.test(msg)) {
        setAuthError(null);
        return;
      }
      setAuthError(msg.replace('Firebase: ', '') || 'Google sign-in failed.');
    }
  }, [isOnline]);

  const handleGoogleLogin = () => {
    if (GRADING_ONLY_MODE) {
      void handleFirebaseGoogleSignIn();
      return;
    }
    if (!isOnline) {
      setAuthError('You are offline. Connect to the internet to sign in with Google.');
      return;
    }
    const oauthClientId = getGoogleOAuthClientId();
    if (!oauthClientId) {
      setAuthError(GOOGLE_CLIENT_ID_MISSING_MESSAGE);
      return;
    }
    try {
      type GoogleTokenClient = { requestAccessToken: () => void };
      type GoogleTokenOpts = {
        client_id: string;
        scope: string;
        prompt?: string;
        ux_mode?: string;
        redirect_uri?: string;
        error_callback?: (err: { type?: string }) => void;
        callback?: (response: { access_token?: string }) => void;
      };
      const g = (
        window as Window & {
          google?: {
            accounts?: {
              oauth2?: { initTokenClient: (opts: GoogleTokenOpts) => GoogleTokenClient };
            };
          };
        }
      ).google;
      if (!g?.accounts?.oauth2?.initTokenClient) {
        setAuthError('Google sign-in SDK is still loading. Please wait a moment and try again.');
        return;
      }

      const redirectUri = `${window.location.origin}${window.location.pathname || '/'}`;
      if (import.meta.env.DEV) {
        console.info(
          '[DoneGrading] Google OAuth (register these in GCP → Credentials → Web client)',
          {
            javascript_origin: window.location.origin,
            redirect_uri: redirectUri,
            href: window.location.href,
          }
        );
      }

      const client = g.accounts.oauth2.initTokenClient({
        client_id: oauthClientId,
        scope:
          'https://www.googleapis.com/auth/classroom.courses https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.coursework.me https://www.googleapis.com/auth/classroom.rosters.readonly https://www.googleapis.com/auth/classroom.coursework.students https://www.googleapis.com/auth/classroom.profile.emails https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/spreadsheets',
        prompt: 'consent',
        ux_mode: import.meta.env.DEV ? 'popup' : 'redirect',
        redirect_uri: redirectUri,
        error_callback: (err: { type?: string }) => {
          const type = (err?.type || '').toLowerCase();
          // User canceled/closed popup: don't show a scary auth error on Home.
          if (type === 'popup_closed' || type === 'access_denied') {
            setAuthError(null);
            return;
          }
          if (type === 'popup_failed_to_open') {
            setAuthError('Google sign-in popup was blocked. Enable popups and try again.');
            return;
          }
          const msg = err?.type ? `Google sign-in error: ${err.type}` : 'Google sign-in failed.';
          setAuthError(msg);
        },
        callback: (response: { access_token?: string }) => {
          if (response?.access_token) completeGoogleSignIn(response.access_token);
        },
      });
      client.requestAccessToken();
    } catch (err: unknown) {
      setAuthError(`OAuth Failure: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const selectCourse = async (course: Course) => {
    setCourses(courses.map((c) => (c.id === course.id ? { ...c, lastUsed: Date.now() } : c)));
    setSelectedCourse(course);
    logEvent('course_select', { courseId: course.id, source: course.source || 'local' });
    setAssignmentSearchQuery('');
    setPhase(AppPhase.ASSIGNMENT_SELECT);

    // If this is a real Google Classroom course, load its assignments and students.
    if (classroom && isOnline && course.source !== 'local') {
      try {
        const [assignmentData, studentData] = await Promise.all([
          classroom.getAssignments(course.id),
          classroom.getStudents(course.id),
        ]);
        setAssignments(assignmentData);
        setStudents(studentData);
        setSyncStatus('ok');
      } catch (err) {
        console.error('Failed to load assignments/students for course', course.id, err);
        // Make sure we don't accidentally show assignments from a different course
        setAssignments([]);
        setStudents([]);
        setSyncStatus('error');
      }
    } else {
      // Local-only courses: start with a clean slate
      setAssignments([]);
      setStudents([]);
    }
  };

  const handleConnectClassroomForGrade = () => {
    if (classroom && isOnline) {
      void loadCourses();
      return;
    }
    handleGoogleLogin();
  };

  const skipGradeWorkspaceSetup = () => {
    const newCourse: Course = {
      id: `local-${Date.now()}`,
      name: t('gradingOnly.workspaceEmpty.skipClassName'),
      period: t('gradingOnly.workspaceEmpty.skipClassName'),
      source: 'local',
      lastUsed: Date.now(),
    };
    setCourses((prev) => [newCourse, ...prev]);
    void selectCourse(newCourse);
  };

  const resumeLastGradeSession = useCallback(async (): Promise<void> => {
    const saved = loadLastGradeSession() ?? loadGradeHubSessionMeta();
    if (!saved || !classroom || !isOnline) return;
    const course = courses.find((c) => c.id === saved.courseId);
    if (!course || course.source === 'local') return;

    setIsResumingGradeSession(true);
    try {
      setCourses((prev) =>
        prev.map((c) => (c.id === course.id ? { ...c, lastUsed: Date.now() } : c))
      );
      setSelectedCourse(course);
      setAssignmentSearchQuery('');
      const [assignmentData, studentData] = await Promise.all([
        classroom.getAssignments(course.id),
        classroom.getStudents(course.id),
      ]);
      setAssignments(assignmentData);
      setStudents(studentData);
      setSyncStatus('ok');
      const asn = assignmentData.find((a) => a.id === saved.assignmentId) ?? null;
      if (asn) {
        setSelectedAssignment(asn);
        setPhase(AppPhase.DASHBOARD);
        logEvent('grade_session_resume', { courseId: course.id, assignmentId: asn.id });
      } else {
        setSelectedAssignment(null);
        setPhase(AppPhase.ASSIGNMENT_SELECT);
      }
      return;
    } catch (err) {
      console.error('Resume grade session failed', err);
      setSyncStatus('error');
    } finally {
      setIsResumingGradeSession(false);
    }
  }, [classroom, isOnline, courses, setPhase]);

  const openRecentGradeTarget = useCallback(
    async (target: RecentGradeTarget) => {
      const course = courses.find((c) => c.id === target.courseId);
      if (!course) {
        setPhase(AppPhase.GRADE_COURSE_PICKER);
        return;
      }
      setSelectedCourse(course);
      setAssignmentSearchQuery('');
      if (course.source === 'local') {
        setPhase(AppPhase.ASSIGNMENT_SELECT);
        return;
      }
      if (!classroom || !isOnline) {
        setPhase(AppPhase.ASSIGNMENT_SELECT);
        return;
      }
      try {
        const [assignmentData, studentData] = await Promise.all([
          classroom.getAssignments(course.id),
          classroom.getStudents(course.id),
        ]);
        setAssignments(assignmentData);
        setStudents(studentData);
        setSyncStatus('ok');
        const asn = assignmentData.find((a) => a.id === target.assignmentId);
        if (asn) {
          setSelectedAssignment(asn);
          setPhase(AppPhase.DASHBOARD);
        } else {
          setSelectedAssignment(null);
          setPhase(AppPhase.ASSIGNMENT_SELECT);
        }
      } catch (err) {
        console.error('Open recent grade target failed', err);
        setSyncStatus('error');
        setPhase(AppPhase.ASSIGNMENT_SELECT);
      }
    },
    [classroom, isOnline, courses, setPhase]
  );

  const startGrading = useCallback(
    (mode: GradingMode) => {
      setGradingMode(mode);
      if (scanStudentMode === 'batch') {
        const selectedSet = batchSelectedStudentIds;
        const ordered =
          batchStudentOrderRef.current.length > 0
            ? batchStudentOrderRef.current.filter((id) => selectedSet.has(id))
            : students.filter((s) => selectedSet.has(s.id)).map((s) => s.id);
        batchStudentOrderRef.current = ordered;
        batchNextIndexRef.current = 0;
      } else {
        batchStudentOrderRef.current = [];
        batchNextIndexRef.current = 0;
      }
      setPhase(AppPhase.GRADING_LOOP);
    },
    [scanStudentMode, batchSelectedStudentIds, students, setPhase]
  );

  const gradeHubAutoRestoreRef = useRef(false);

  const hubTrustStatus = useMemo(
    () =>
      deriveHubTrustStatus({
        isOnline,
        syncStatus,
        pendingDraftCount: gradedWorks.length,
      }),
    [isOnline, syncStatus, gradedWorks.length]
  );

  const nextHubAssignment = useMemo(() => {
    if (!selectedAssignment || assignments.length < 2) return null;
    const idx = assignments.findIndex((a) => a.id === selectedAssignment.id);
    if (idx < 0) return assignments[0] ?? null;
    return assignments[idx + 1] ?? assignments[0] ?? null;
  }, [assignments, selectedAssignment]);

  const launchFastScan = useCallback(
    (course: Course | null, assignment: Assignment | null) => {
      const next = resolveFastScanStep({
        cameraFirstLaunch: eliteFlags.cameraFirstLaunch,
        selectedCourse: course,
        selectedAssignment: assignment,
        customRubric,
        gradingMode,
      });
      if (next.action === 'start') {
        startGrading(next.mode);
      } else {
        setPhase(next.phase);
      }
    },
    [eliteFlags.cameraFirstLaunch, customRubric, gradingMode, setPhase, startGrading]
  );

  const continueGradingFromHub = useCallback(async () => {
    recordFirstUseIfNeeded();
    logEvent('grading_session_start', { surface: 'grade_hub_continue' });
    const course = selectedCourse;
    const assignment = selectedAssignment;
    if (!course || !assignment) {
      await resumeLastGradeSession();
      return;
    }
    launchFastScan(course, assignment);
  }, [selectedCourse, selectedAssignment, resumeLastGradeSession, launchFastScan]);

  const hubNextStudent = useCallback(() => {
    recordFirstUseIfNeeded();
    if (!selectedCourse || !selectedAssignment) {
      setPhase(AppPhase.GRADE_COURSE_PICKER);
      return;
    }
    const nextStudent = findNextUngradedStudent({
      students,
      history,
      courseId: selectedCourse.id,
      assignmentId: selectedAssignment.id,
      batchOrder: scanStudentMode === 'batch' ? batchStudentOrderRef.current : undefined,
      batchStudentIds: scanStudentMode === 'batch' ? batchSelectedStudentIds : undefined,
    });
    if (!nextStudent) {
      setPhase(AppPhase.AUDIT);
      return;
    }
    if (scanStudentMode === 'batch') {
      const order = batchStudentOrderRef.current;
      const idx = order.indexOf(nextStudent.id);
      if (idx >= 0) {
        batchNextIndexRef.current = idx;
      } else {
        batchStudentOrderRef.current = [...order, nextStudent.id];
        batchNextIndexRef.current = batchStudentOrderRef.current.length - 1;
      }
      setBatchSelectedStudentIds((prev) => new Set([...prev, nextStudent.id]));
    }
    launchFastScan(selectedCourse, selectedAssignment);
  }, [
    selectedCourse,
    selectedAssignment,
    students,
    history,
    scanStudentMode,
    batchSelectedStudentIds,
    launchFastScan,
    setPhase,
  ]);

  const hubStartNextAssignment = useCallback(() => {
    recordFirstUseIfNeeded();
    if (!selectedCourse || !nextHubAssignment) {
      setPhase(AppPhase.ASSIGNMENT_SELECT);
      return;
    }
    setSelectedAssignment(nextHubAssignment);
    setPhase(AppPhase.RUBRIC_SETUP);
  }, [selectedCourse, nextHubAssignment, setPhase]);

  useEffect(() => {
    if (phase !== AppPhase.DASHBOARD || !isSignedIn) return;
    recordFirstUseIfNeeded();
    if (gradeHubAutoRestoreRef.current || selectedCourse) return;
    const saved = loadGradeHubSessionMeta() ?? loadLastGradeSession();
    if (!saved || !classroom || !isOnline) return;
    gradeHubAutoRestoreRef.current = true;
    void resumeLastGradeSession();
  }, [phase, isSignedIn, selectedCourse, classroom, isOnline, resumeLastGradeSession]);

  // (Removed) old modal voice capture flow.

  const handleScanPaperRubric = () => {
    setCameraError(null);
    setRubricAutoAttempts(0);
    setIsScanningRubric(true);
  };

  // Periodic background sync for assignments/students for the currently selected Google Classroom course
  useEffect(() => {
    if (
      !classroom ||
      !isOnline ||
      !selectedCourse ||
      selectedCourse.source === 'local' ||
      phase !== AppPhase.ASSIGNMENT_SELECT
    ) {
      return;
    }
    const courseId = selectedCourse.id;
    const id = window.setInterval(() => {
      Promise.all([classroom.getAssignments(courseId), classroom.getStudents(courseId)])
        .then(([assignmentData, studentData]) => {
          setAssignments(assignmentData);
          setStudents(studentData);
        })
        .catch((err) => {
          console.error('Auto-sync assignments/students failed', err);
        });
    }, 20000);
    return () => window.clearInterval(id);
  }, [classroom, isOnline, selectedCourse, phase]);

  const handleGenerateRubric = async () => {
    if (!selectedAssignment) return;
    setIsGeneratingRubric(true);
    try {
      const text = await generateRubric(
        selectedAssignment.title,
        selectedAssignment.rubric,
        selectedAssignment.maxScore
      );
      if (text) {
        setCustomRubric(text);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsGeneratingRubric(false);
    }
  };

  const handleOpenAsnCreation = (course: Course, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreationCourse(course);
    setPhase(AppPhase.ASSIGNMENT_CREATION);
    setNewAsnTitle('');
    setNewAsnDesc('');
    setNewAsnMaxScore(100);
    setCreationError(null);
  };

  const handleShareApp = async () => {
    const shareUrl = window.location.origin;
    const shareText =
      'Checkout DoneGrading! An app made for educators to plan lessons, teach with timers, grade with AI, schedule classes, and communicate with students—all in one place.';
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({
          title: 'DoneGrading',
          text: shareText,
          url: shareUrl,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
        alert('Message and link copied to clipboard!');
      }
    } catch (e) {
      console.error('Share failed', e);
    }
  };

  const handleSignOut = async () => {
    const googleTokenSnapshot = accessToken;
    if (googleTokenSnapshot) {
      await revokeGoogleOAuthAccessToken(googleTokenSnapshot);
    }
    clearAuthSession();
    if (firebaseUser) {
      await firebaseSignOut(auth);
      setFirebaseUser(null);
    }
    setAccessToken(null);
    setClassroom(null);
    clearLastGradeSession();
    setSelectedCourse(null);
    setSelectedAssignment(null);
    setAuthError(null);
    setCourses([]);
    setAssignments([]);
    setStudents([]);
    setGradedWorks([]);
    setHistory([]);
    localStorage.removeItem('dg_cache_courses');
    localStorage.removeItem('dg_cache_assignments');
    localStorage.removeItem('dg_cache_students');
    localStorage.removeItem('dg_pending_sync');
    localStorage.removeItem('dg_history');
    setPhaseRaw(AppPhase.AUTHENTICATION);
  };

  const handleDeleteAccount = useCallback(
    async (req: AccountDeletionRequest): Promise<AccountDeletionResult> => {
      const tokenSnapshot = accessToken;

      const finishDeletingLocalSession = async () => {
        if (tokenSnapshot) await revokeGoogleOAuthAccessToken(tokenSnapshot);
        // Privacy 6E-3: also delete the local scan IndexedDB. Best-effort — account
        // deletion still completes if IndexedDB is blocked after server/Firebase delete.
        await clearLocalUserDataAsync();
        try {
          await firebaseSignOut(auth);
        } catch {
          /* user may already be deleted / signed out */
        }
        setFirebaseUser(null);
        setAccessToken(null);
        setClassroom(null);
        clearLastGradeSession();
        setSelectedCourse(null);
        setSelectedAssignment(null);
        setAuthError(null);
        setCourses([]);
        setAssignments([]);
        setStudents([]);
        setGradedWorks([]);
        setHistory([]);
        setPhaseRaw(AppPhase.AUTHENTICATION);
      };

      const hasPasswordProvider = (): boolean =>
        !!auth.currentUser?.providerData?.some((p) => p.providerId === 'password');
      const hasAppleProvider = (): boolean =>
        !!auth.currentUser?.providerData?.some((p) => p.providerId === 'apple.com');

      try {
        // STEP 1 (Phase 1 compliance): purge the user's server-side data
        // BEFORE any irreversible Firebase deletion or local-state clearing.
        // If the backend purge fails, surface a retryable error and bail —
        // we must NOT report deletion complete or delete the Firebase
        // account while the teacher's data may still live on the server.
        // The purge is idempotent, so re-invocations (need_password /
        // need_apple) safely re-run it.
        if (auth.currentUser) {
          const purge = await purgeServerAccountData();
          if (!purge.ok) {
            return { status: 'error', message: purge.message };
          }
        }

        if (req.step === 'password') {
          const user = auth.currentUser;
          if (!user?.email || !req.password?.trim()) {
            return { status: 'error', message: 'Enter your account password to confirm deletion.' };
          }
          const cred = EmailAuthProvider.credential(user.email, req.password.trim());
          await reauthenticateWithCredential(user, cred);
          await deleteUser(user);
          await finishDeletingLocalSession();
          try {
            logEvent('account_delete_completed', { method: 'email_reauth' });
          } catch {
            /* ignore */
          }
          return { status: 'success' };
        }

        if (req.step === 'apple') {
          const user = auth.currentUser;
          if (!user) {
            return {
              status: 'error',
              message: 'Your session expired. Sign in again, then delete your account.',
            };
          }
          const clientId = (import.meta.env.VITE_APPLE_CLIENT_ID ?? '').trim();
          if (!clientId) {
            return {
              status: 'error',
              message:
                'Apple Sign-In is not configured for this build. Use the support link in Options to request account deletion.',
            };
          }
          const redirectURI = `${window.location.origin}${window.location.pathname || '/'}`;
          const { idToken, rawNonce } = await getAppleIdTokenForFirebaseAuth({
            clientId,
            redirectURI,
          });
          const provider = new OAuthProvider('apple.com');
          const credential = provider.credential({ idToken, rawNonce });
          await reauthenticateWithCredential(user, credential);
          await deleteUser(user);
          await finishDeletingLocalSession();
          try {
            logEvent('account_delete_completed', { method: 'apple_reauth' });
          } catch {
            /* ignore */
          }
          return { status: 'success' };
        }

        const user = auth.currentUser;
        if (!user) {
          await finishDeletingLocalSession();
          try {
            logEvent('account_delete_completed', { method: 'google_or_local' });
          } catch {
            /* ignore */
          }
          return { status: 'success' };
        }

        try {
          await deleteUser(user);
        } catch (err: unknown) {
          const code = getUnknownErr(err).code;
          if (code === 'auth/requires-recent-login') {
            if (hasPasswordProvider()) return { status: 'need_password' };
            if (hasAppleProvider()) return { status: 'need_apple' };
            return {
              status: 'error',
              message:
                'For your security, sign out, sign in again, then return here to delete your account.',
            };
          }
          return {
            status: 'error',
            message:
              getUnknownErr(err).message.replace('Firebase: ', '') || 'Could not delete account.',
          };
        }

        await finishDeletingLocalSession();
        try {
          logEvent('account_delete_completed', { method: 'firebase_direct' });
        } catch {
          /* ignore */
        }
        return { status: 'success' };
      } catch (err: unknown) {
        return {
          status: 'error',
          message:
            getUnknownErr(err).message.replace('Firebase: ', '') || 'Account deletion failed.',
        };
      }
    },
    [accessToken]
  );

  const handleCreateCourseLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourseName.trim()) return;
    setIsCreatingCourse(true);
    setCreationError(null);
    try {
      let newCourse: Course;
      if (classroom && isOnline) {
        // Create in Google Classroom so the course exists everywhere
        newCourse = await classroom.createCourse(newCourseName, 'New Course');
        setSyncStatus('ok');
      } else {
        // Fallback: local-only course inside the app
        newCourse = {
          id: Date.now().toString(),
          name: newCourseName,
          period: 'New Course',
          source: 'local',
          lastUsed: Date.now(),
        };
      }
      setCourses([newCourse, ...courses]);
      setPhase(AppPhase.DASHBOARD);
    } catch (err: unknown) {
      console.error('Failed to create course', err);
      setCreationError(getUnknownErr(err).message || 'Failed to create course.');
      setSyncStatus('error');
    } finally {
      setIsCreatingCourse(false);
    }
  };

  const handleCreateAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!classroom || !creationCourse || !newAsnTitle.trim()) return;
    setIsCreatingAssignment(true);
    setCreationError(null);
    try {
      await classroom.createAssignment(creationCourse.id, newAsnTitle, newAsnDesc, newAsnMaxScore);
      setPhase(AppPhase.DASHBOARD);
      await loadCourses();
    } catch (err: unknown) {
      console.error(err);
      setCreationError(getUnknownErr(err).message || 'Failed to create assignment.');
      setSyncStatus('error');
    } finally {
      setIsCreatingAssignment(false);
    }
  };

  const handleDeleteScan = (index: number) => {
    setGradedWorks((prev) => {
      const work = prev[index];
      if (work) setScanLastDeleted({ work, index });
      return prev.filter((_, i) => i !== index);
    });
    setAuditSelectedIndexes(new Set());
    setAuditEditSelectedOnly(false);
    window.setTimeout(() => setScanLastDeleted((prev) => (prev ? null : prev)), 6000);
  };

  const handleRescan = (index: number) => {
    // If we are in batch mode, re-align the next scan to the same student.
    if (scanStudentMode === 'batch') {
      const targetStudentId = gradedWorks[index]?.studentId;
      const order = batchStudentOrderRef.current;
      if (targetStudentId) {
        const idxInOrder = order.indexOf(targetStudentId);
        if (idxInOrder >= 0) batchNextIndexRef.current = idxInOrder;
      }
    }
    handleDeleteScan(index);
    setAuditSelectedIndexes(new Set());
    setAuditEditSelectedOnly(false);
    setPhase(AppPhase.GRADING_LOOP);
  };

  useEffect(() => {
    let currentStream: MediaStream | null = null;
    const setupCamera = async () => {
      if (phase !== AppPhase.GRADING_LOOP && !isScanningRubric) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        currentStream = stream;
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () =>
            videoRef.current?.play().catch((e) => console.error(e));
        }
      } catch (err: unknown) {
        console.error('Camera access denied:', err);
        setCameraError('Camera access denied or unavailable. Please check device settings.');
      }
    };
    setupCamera();
    return () => {
      if (currentStream) currentStream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, [phase, isScanningRubric]);

  const toggleFlash = async () => {
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (track && typeof track.getCapabilities === 'function') {
        const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
          torch?: boolean;
        };
        if (capabilities?.torch) {
          try {
            await track.applyConstraints({
              advanced: [{ torch: !isFlashOn } as unknown as MediaTrackConstraintSet],
            });
            setIsFlashOn(!isFlashOn);
            return;
          } catch (e) {
            console.warn('Torch apply failed', e);
          }
        }
      }
      alert('Camera flash is not supported on this device/browser.');
    }
  };

  const captureFrame = useCallback((quality: number = 0.8): string | null => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    if (video.readyState < 2) return null;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', quality).split(',')[1];
  }, []);

  const handleRubricSnap = useCallback(async () => {
    if (isProcessing || !isScanningRubric || cameraError) return;

    setRubricScanError(null);

    const base64 = captureFrame(0.5);
    if (!base64) return;

    setIsProcessing(true);
    setRubricScanProgress(0);

    const progressInterval = setInterval(() => {
      setRubricScanProgress((prev) => {
        if (prev >= 98) {
          clearInterval(progressInterval);
          return 98;
        }
        return prev + 2;
      });
    }, 40);

    try {
      const text = await extractRubricFromImage(base64);
      const trimmed = (text || '').trim();
      const upper = trimmed.toUpperCase();
      // Consider it a failure only if the model explicitly says NO RUBRIC or returns almost nothing
      const isFailure = !trimmed || trimmed.length < 5 || upper === 'NO RUBRIC';

      if (!isFailure) {
        clearInterval(progressInterval);
        setRubricScanProgress(100);

        setTimeout(() => {
          setCustomRubric(trimmed);
          setIsScanningRubric(false);
          setIsProcessing(false);
          setRubricSuccess(true);
          setTimeout(() => setRubricSuccess(false), 5000);
        }, 600);
      } else {
        clearInterval(progressInterval);
        setRubricScanProgress(0);
        setIsProcessing(false);
        setRubricScanError(
          'Could not clearly read the document. Please ensure it is well-lit and try again.'
        );
        setTimeout(() => setRubricScanError(null), 4000);
      }
    } catch (err) {
      console.error('Rubric scan error', err);
      clearInterval(progressInterval);
      setRubricScanProgress(0);
      setIsProcessing(false);
      setRubricScanError('Network error occurred. Please try again.');
      setTimeout(() => setRubricScanError(null), 4000);
    }
  }, [isProcessing, isScanningRubric, cameraError, captureFrame]);

  const computeAHash = useCallback((base64: string, size: number = 8): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (!ctx) return resolve(null);
            ctx.drawImage(img, 0, 0, size, size);
            const data = ctx.getImageData(0, 0, size, size).data;
            const grays: number[] = [];
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i] || 0;
              const g = data[i + 1] || 0;
              const b = data[i + 2] || 0;
              grays.push(0.299 * r + 0.587 * g + 0.114 * b);
            }
            const avg = grays.reduce((a, v) => a + v, 0) / grays.length;
            const bits = grays.map((v) => (v >= avg ? '1' : '0')).join('');
            resolve(bits);
          } catch {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = `data:image/jpeg;base64,${base64}`;
      } catch {
        resolve(null);
      }
    });
  }, []);

  const hammingDistance = useCallback((a: string, b: string): number => {
    const n = Math.min(a.length, b.length);
    let d = 0;
    for (let i = 0; i < n; i++) if (a[i] !== b[i]) d++;
    return d + Math.abs(a.length - b.length);
  }, []);

  // Auto-snap helper for rubric view: take a few automatic shots while scanning is active
  useEffect(() => {
    if (!isScanningRubric || !isOnline || cameraError) return;
    if (isProcessing) return;
    if (rubricAutoAttempts >= 3) return; // avoid spamming the API

    const timeoutId = window.setTimeout(() => {
      setRubricAutoAttempts((prev) => prev + 1);
      void handleRubricSnap();
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, [isScanningRubric, isOnline, cameraError, isProcessing, rubricAutoAttempts, handleRubricSnap]);

  const handleAutoSnap = useCallback(async () => {
    if (cooldownRef.current || showQuickPick || cameraError) return;
    if (gradingMode === GradingMode.MULTI_PAGE && multiPageCaptureInFlightRef.current) return;

    const optimalHighResBase64 = captureFrame(0.9);
    const apiBase64 = captureFrame(0.4);

    if (!optimalHighResBase64 || !apiBase64 || !selectedAssignment) return;

    if (frameAssessInFlightRef.current) return;
    frameAssessInFlightRef.current = true;

    try {
      const assess = await assessFrame(apiBase64);
      if (assess) {
        setScanHealth(assess.scanHealth || 0);
        setActiveGeometry(assess.corners ?? null);
      }

      const captureThreshold = gradingMode === GradingMode.MULTI_PAGE ? 88 : 80;
      const assessHealth = assess?.scanHealth ?? 0;
      if (!assess || assessHealth < captureThreshold) return;

      // Capture accepted: crop using assessed corners (if any)
      const corners = assess.corners ?? null;
      const croppedBase64 = await cropImageToBoundingBox(optimalHighResBase64, corners);

      if (gradingMode === GradingMode.MULTI_PAGE) {
        multiPageCaptureInFlightRef.current = true;
        const hash = await computeAHash(apiBase64, 8);
        const lastHash = multiPageLastHashRef.current;
        const distance = hash && lastHash ? hammingDistance(hash, lastHash) : null;

        // If the new capture looks too similar to the last accepted page, skip it.
        // (64-bit aHash: distance < ~8 is usually the same page.)
        if (distance !== null && distance < 8) {
          setMultiPageHint('Same page detected — turn the page or move to the next page.');
          window.setTimeout(() => setMultiPageHint(null), 1400);
          cooldownRef.current = true;
          setTimeout(() => {
            cooldownRef.current = false;
          }, 900);
          multiPageCaptureInFlightRef.current = false;
          return;
        }

        if (hash) multiPageLastHashRef.current = hash;

        setMultiPageCapture((prev) => {
          const nextUrls = [
            ...prev.croppedDataUrls,
            `data:image/jpeg;base64,${croppedBase64}`,
          ].slice(0, 10);
          const nextApi = [...prev.apiBase64s, apiBase64].slice(0, 10);
          return {
            croppedDataUrls: nextUrls,
            apiBase64s: nextApi,
            detectedStudentName: prev.detectedStudentName || assess.transcription || undefined,
          };
        });
        lightHaptic();
        setMultiPageHint('Captured. Turn the page, then keep scanning.');
        window.setTimeout(() => setMultiPageHint(null), 1200);
        cooldownRef.current = true;
        setTimeout(() => {
          cooldownRef.current = false;
        }, 2500);
        multiPageCaptureInFlightRef.current = false;
        return;
      }

      // Single-page: enqueue for background grading + later review.
      const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const dataUrl = `data:image/jpeg;base64,${croppedBase64}`;
      scanQueueRef.current.push({
        id,
        base64: croppedBase64,
        dataUrl,
        createdAt: Date.now(),
        scanHealth: assessHealth,
        corners,
        transcription: assess.transcription || undefined,
      });
      setScanQueueCount(scanQueueRef.current.length);
      lightHaptic();
      setScanQueueHint(`Queued (${scanQueueRef.current.length}). Keep scanning.`);
      window.setTimeout(() => setScanQueueHint(null), 1200);
      cooldownRef.current = true;
      setTimeout(() => {
        cooldownRef.current = false;
      }, 1200);
    } catch (err) {
      console.error(err);
    } finally {
      frameAssessInFlightRef.current = false;
    }
  }, [
    cameraError,
    captureFrame,
    selectedAssignment,
    showQuickPick,
    gradingMode,
    computeAHash,
    hammingDistance,
  ]);

  useEffect(() => {
    let interval: number | null = null;
    if (
      phase === AppPhase.GRADING_LOOP &&
      isOnline &&
      !showQuickPick &&
      !isProcessing &&
      !cameraError
    ) {
      interval = window.setInterval(() => handleAutoSnap(), 500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [phase, isOnline, showQuickPick, isProcessing, cameraError, handleAutoSnap]);

  const openNextQueuedReview = useCallback(() => {
    if (showQuickPick) return;
    const next = scanReviewRef.current.shift();
    setScanReviewQueueCount(scanReviewRef.current.length);
    if (!next) return;

    setPendingWork({ ...next.result, imageUrls: [next.dataUrl] });
    setManualScore(next.result.score?.toString() || '');
    setManualFeedback(next.result.feedback || '');

    const detectedIds = new Set<string>();
    const candidateStudents =
      scanStudentMode === 'batch' && batchSelectedStudentIds.size > 0
        ? students.filter((s) => batchSelectedStudentIds.has(s.id))
        : students;

    if (next.result.studentName) {
      const lowerDetected = next.result.studentName.toLowerCase().replace(/[^a-z]/g, '');
      if (lowerDetected.length > 2) {
        const match = candidateStudents.find((s) => {
          const sName = s.name.toLowerCase().replace(/[^a-z]/g, '');
          return sName.includes(lowerDetected) || lowerDetected.includes(sName);
        });
        if (match) detectedIds.add(match.id);
      }
    }

    // If we are in batch mode and detection found nothing, attribute this scan to the next student.
    if (scanStudentMode === 'batch' && detectedIds.size === 0) {
      const nextId = batchStudentOrderRef.current[batchNextIndexRef.current];
      if (nextId) detectedIds.add(nextId);
    }
    setSelectedQuickPickIds(detectedIds);
    setGuardrailOverride(false);
    setShowQuickPick(true);
  }, [showQuickPick, students, scanStudentMode, batchSelectedStudentIds]);

  // Background worker: grade queued single-page captures while teacher keeps scanning.
  useEffect(() => {
    if (gradingMode !== GradingMode.SINGLE_PAGE) return;
    if (!isOnline) return;
    if (!selectedAssignment) return;

    let cancelled = false;
    const workerInFlightRef = { current: false };

    const tick = async () => {
      if (cancelled) return;
      if (showQuickPick) return;
      if (workerInFlightRef.current) return;
      if (scanQueueRef.current.length === 0) return;

      workerInFlightRef.current = true;
      const job = scanQueueRef.current[0];
      try {
        const result = await analyzePaper(
          job.base64,
          customRubric || selectedAssignment.rubric,
          selectedAssignment.maxScore,
          students.map((s) => s.name),
          false
        );
        const finalResult: GradingResponse =
          result ||
          ({
            detected: true,
            studentName: '',
            score: 0,
            feedback:
              'AI unavailable right now. Your scan is saved in the review queue so you can match the student and fill in a score/feedback manually.',
            confidence: 0,
            scanHealth: job.scanHealth,
            alignment: 'IN_FRAME',
            corners: job.corners || undefined,
            transcription: job.transcription,
          } as GradingResponse);

        scanReviewRef.current.push({ id: job.id, result: finalResult, dataUrl: job.dataUrl });
        setScanReviewQueueCount(scanReviewRef.current.length);
        setScanSessionStats((prev) => ({
          ...prev,
          gradedCount: prev.gradedCount + 1,
          totalGradeMs: prev.totalGradeMs + Math.max(0, Date.now() - job.createdAt),
        }));
        setScanSessionStats((prev) => ({
          ...prev,
          gradedCount: prev.gradedCount + 1,
          totalGradeMs: prev.totalGradeMs + Math.max(0, Date.now() - job.createdAt),
        }));
      } catch (e) {
        console.error('Queued grading failed', e);
        const fallback = {
          detected: true,
          studentName: '',
          score: 0,
          feedback:
            'Grading failed due to a network/API error. Your scan is saved in the review queue so you can match the student and fill in a score/feedback manually.',
          confidence: 0,
          scanHealth: job.scanHealth,
          alignment: 'IN_FRAME',
          corners: job.corners || undefined,
          transcription: job.transcription,
        } as GradingResponse;
        scanReviewRef.current.push({ id: job.id, result: fallback, dataUrl: job.dataUrl });
        setScanReviewQueueCount(scanReviewRef.current.length);
        setScanSessionStats((prev) => ({
          ...prev,
          gradedCount: prev.gradedCount + 1,
          totalGradeMs: prev.totalGradeMs + Math.max(0, Date.now() - job.createdAt),
        }));
        setScanSessionStats((prev) => ({
          ...prev,
          gradedCount: prev.gradedCount + 1,
          totalGradeMs: prev.totalGradeMs + Math.max(0, Date.now() - job.createdAt),
        }));
      } finally {
        scanQueueRef.current.shift();
        setScanQueueCount(scanQueueRef.current.length);
        workerInFlightRef.current = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void tick();
    }, 450);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [gradingMode, isOnline, selectedAssignment, customRubric, students, showQuickPick]);

  const confirmQuickPickStudents = () => {
    if (!pendingWork || !selectedAssignment || selectedQuickPickIds.size === 0) return;
    trackEliteEvent('quick_match_confirm', {
      selectedCount: selectedQuickPickIds.size,
      autoAdvance: eliteFlags.autoAdvanceAfterConfirm,
      guardrailEnabled: eliteFlags.qualityGuardrailMode,
      overrideUsed: guardrailOverride,
    });
    const selectedStudents = students.filter((s) => selectedQuickPickIds.has(s.id));
    selectedStudents.forEach((student) => {
      const newWork: GradedWork = {
        studentId: student.id,
        studentName: student.name,
        studentEmail: student.email,
        score: parseFloat(manualScore) || (pendingWork.score ?? 0),
        maxScore: selectedAssignment.maxScore,
        feedback: manualFeedback || pendingWork.feedback || '',
        imageUrls: pendingWork.imageUrls || [],
        status: 'draft',
        timestamp: Date.now(),
        courseName: selectedCourse?.name || '',
        assignmentName: selectedAssignment.title,
        courseId: selectedCourse?.id || '',
        assignmentId: selectedAssignment.id,
        scanHealth: pendingWork.scanHealth,
        transcription: pendingWork.transcription,
        geometry: pendingWork.corners,
      };
      setGradedWorks((prev) => [...prev, newWork]);
      bumpSubmissionCount();
      setScanSessionStats((prev) => ({
        ...prev,
        manualCorrections: prev.manualCorrections + 1,
      }));
    });

    // Advance batch pointer only after the teacher confirms which student this scan belongs to.
    if (scanStudentMode === 'batch') {
      const selectedId = Array.from(selectedQuickPickIds)[0];
      const order = batchStudentOrderRef.current;
      const idx = selectedId ? order.indexOf(selectedId) : -1;
      if (idx >= 0) batchNextIndexRef.current = idx + 1;
      else batchNextIndexRef.current = batchNextIndexRef.current + 1;
    }

    setStudents(
      students.map((s) => (selectedQuickPickIds.has(s.id) ? { ...s, lastUsed: Date.now() } : s))
    );
    if (eliteFlags.hapticsAudio && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(12);
    }
    successHaptic();
    if (eliteFlags.autoAdvanceAfterConfirm) {
      setShowQuickPick(false);
      setPendingWork(null);
      setActiveGeometry(null);
      setScanHealth(0);
      setOneWordCommand(null);
      setGuardrailOverride(false);
    } else {
      setSelectedQuickPickIds(new Set());
    }
    cooldownRef.current = true;
    setTimeout(() => {
      cooldownRef.current = false;
    }, 1000);
  };

  const handleManualCaptureSinglePage = useCallback(async () => {
    if (gradingMode !== GradingMode.SINGLE_PAGE) return;
    if (cooldownRef.current || showQuickPick || cameraError) return;
    if (!selectedAssignment) return;

    const highRes = captureFrame(0.9);
    if (!highRes) return;

    try {
      const croppedBase64 = await cropImageToBoundingBox(highRes, activeGeometry || null);
      const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const dataUrl = `data:image/jpeg;base64,${croppedBase64}`;
      scanQueueRef.current.push({
        id,
        base64: croppedBase64,
        dataUrl,
        createdAt: Date.now(),
        scanHealth: Math.max(0, Math.min(100, Math.round(scanHealth))),
        corners: activeGeometry || null,
      });
      setScanSessionStats((prev) => ({ ...prev, captured: prev.captured + 1 }));
      setScanQueueCount(scanQueueRef.current.length);
      setScanSessionStats((prev) => ({ ...prev, captured: prev.captured + 1 }));
      logEvent('scan_capture', {
        mode: gradingMode,
        queueDepth: scanQueueRef.current.length,
      });
      lightHaptic();
      setScanQueueHint(
        `Captured (${scanQueueRef.current.length}). ${isOnline ? 'Grading in background.' : 'Will grade when online.'}`
      );
      window.setTimeout(() => setScanQueueHint(null), 1200);
      cooldownRef.current = true;
      setTimeout(() => {
        cooldownRef.current = false;
      }, 900);
    } catch (e) {
      console.error(e);
    }
  }, [
    gradingMode,
    cooldownRef,
    showQuickPick,
    cameraError,
    selectedAssignment,
    captureFrame,
    activeGeometry,
    isOnline,
    scanHealth,
  ]);

  const finalizeMultiPage = async () => {
    if (isProcessing || showQuickPick) return;
    if (!selectedAssignment) return;
    if (multiPageCapture.apiBase64s.length === 0) return;
    if (!isOnline) return;

    setIsProcessing(true);
    try {
      const result = await analyzeMultiPagePaper(
        multiPageCapture.apiBase64s,
        customRubric || selectedAssignment.rubric,
        selectedAssignment.maxScore,
        students.map((s) => s.name)
      );
      if (!result) return;

      const merged: Partial<GradingResponse> & { imageUrls: string[] } = {
        ...result,
        imageUrls: multiPageCapture.croppedDataUrls,
        scanHealth: scanHealth || result.scanHealth,
      };

      setPendingWork(merged);
      setManualScore(result.score?.toString() || '');
      setManualFeedback(result.feedback || '');

      const detectedName = result.studentName || multiPageCapture.detectedStudentName || '';
      const detectedIds = new Set<string>();
      const candidateStudents =
        scanStudentMode === 'batch' && batchSelectedStudentIds.size > 0
          ? students.filter((s) => batchSelectedStudentIds.has(s.id))
          : students;

      if (detectedName) {
        const lowerDetected = detectedName.toLowerCase().replace(/[^a-z]/g, '');
        if (lowerDetected.length > 2) {
          const match = candidateStudents.find((s) => {
            const sName = s.name.toLowerCase().replace(/[^a-z]/g, '');
            return sName.includes(lowerDetected) || lowerDetected.includes(sName);
          });
          if (match) detectedIds.add(match.id);
        }
      }

      // If detection found nothing and we are batching, attribute to the next student.
      if (scanStudentMode === 'batch' && detectedIds.size === 0) {
        const nextId = batchStudentOrderRef.current[batchNextIndexRef.current];
        if (nextId) detectedIds.add(nextId);
      }

      setSelectedQuickPickIds(detectedIds);
      setGuardrailOverride(false);
      setShowQuickPick(true);
    } catch (e) {
      console.error('Multi-page finalize failed', e);
    } finally {
      setIsProcessing(false);
    }
  };

  const startSyncProcess = async (indexesToSync?: number[]) => {
    if (!classroom || !isOnline || !accessToken) return;
    if (gradedWorks.length === 0) return;

    const currentWorks = [...gradedWorks];
    const indexSet = indexesToSync && indexesToSync.length > 0 ? new Set(indexesToSync) : null;
    const worksToSync = indexSet
      ? (indexesToSync ?? []).map((i) => currentWorks[i]).filter(Boolean)
      : currentWorks;
    if (worksToSync.length === 0) return;
    if (!isPaid) {
      setPhase(AppPhase.PAYWALL);
      return;
    }

    clearOfflineGradeSyncIntent();
    setOfflineGradeSyncIntent(null);

    const doPostToClassroom = gradeSyncPostToClassroom;
    const doEmailStudents = gradeSyncEmailStudents;
    const doSaveToDrive = gradeSyncSaveToDrive;

    logEvent('sync_start', { count: worksToSync.length });
    setPhase(AppPhase.SYNCING);
    setSyncReceipts([]);
    setSyncFailedKeys(new Set());
    setSyncProgress({
      current: 0,
      total: worksToSync.length,
      message: doSaveToDrive ? 'Preparing Google Drive...' : 'Preparing sync...',
      successes: 0,
      failures: 0,
      emailSuccesses: 0,
      emailFailures: 0,
    });

    let targetFolderId: string | null = null;

    if (doSaveToDrive) {
      try {
        const rootFolderId = await getOrCreateDriveFolder(accessToken, 'DoneGrading Scans');
        const safeAssignmentFolderName = worksToSync[0]?.assignmentName
          ? worksToSync[0].assignmentName.replace(/[^a-zA-Z0-9 ]/g, '').trim()
          : 'Misc Scans';
        targetFolderId = await getOrCreateDriveFolder(
          accessToken,
          safeAssignmentFolderName,
          rootFolderId
        );
      } catch (e) {
        console.error(
          'Could not set up Drive folders. We will skip Drive upload for this sync.',
          e
        );
      }
    }

    const workKey = (w: GradedWork) =>
      `${w.courseId}::${w.assignmentId}::${w.studentId}::${w.timestamp}`;
    const receiptList: Array<{
      key: string;
      studentName: string;
      ok: boolean;
      error?: string;
      emailOk?: boolean;
      emailSkipped?: boolean;
      emailMissing?: boolean;
      drivePagesSaved?: number;
      driveSkipped?: boolean;
      classroomSkipped?: boolean;
    }> = [];
    const successKeys = new Set<string>();
    const failedKeys = new Set<string>();

    // Important: worksToSync is derived from either selected indexes or the full pending list.
    let successes = 0,
      failures = 0;

    for (let i = 0; i < worksToSync.length; i++) {
      const work = worksToSync[i];
      setSyncProgress((prev) => ({
        ...prev,
        current: i + 1,
        message: t('gradeSync.syncing.syncingGradeFor', { name: work.studentName }),
      }));

      try {
        if (doPostToClassroom) {
          await classroom.postGrade(
            work.courseId,
            work.assignmentId,
            work.studentId,
            work.score,
            work.feedback
          );
        }

        const base64Images =
          (doSaveToDrive || doEmailStudents) && work.imageUrls && work.imageUrls.length > 0
            ? work.imageUrls
                .map((u) => {
                  const parts = u.split(',');
                  return parts.length > 1 ? parts[1] : '';
                })
                .filter(Boolean)
                .slice(0, 10)
            : [];

        let driveSaved = 0;
        const driveSkipped = !doSaveToDrive;
        if (!driveSkipped && targetFolderId && base64Images.length > 0) {
          setSyncProgress((prev) => ({
            ...prev,
            message:
              base64Images.length > 1
                ? t('gradeSync.syncing.savingScansToDrive', { name: work.studentName })
                : t('gradeSync.syncing.savingScanToDrive', { name: work.studentName }),
          }));
          for (let p = 0; p < base64Images.length; p++) {
            const b64 = base64Images[p];
            await uploadImageToDrive(
              accessToken,
              b64,
              `${work.studentName}_${work.assignmentName}_p${p + 1}.jpg`,
              targetFolderId
            );
            driveSaved++;
          }
        }

        // Send email to student with grade, feedback, and attached scan (independent of Drive upload)
        let emailOk: boolean | undefined = undefined;
        const emailSkipped = !doEmailStudents;
        let emailMissing = false;

        if (!emailSkipped) {
          if (!work.studentEmail) {
            emailMissing = true;
          } else {
            // Subject from student perspective
            const subject = `${work.assignmentName} grade & feedback`;

            // Plain continuous feedback text, no explicit line breaks
            const shortFeedback = (work.feedback || '').trim();
            const normalizedFeedback = shortFeedback.replace(/\s+/g, ' ');
            const feedbackSentence = normalizedFeedback
              ? // ensure it ends with a period
                normalizedFeedback.endsWith('.')
                ? normalizedFeedback
                : `${normalizedFeedback}.`
              : '';

            // Example: "In Photosynthesis Lab in Biology you scored 18/20. You explained ... A scan of your work is attached."
            const bodyParts: string[] = [];
            bodyParts.push(
              `In ${work.assignmentName} in ${work.courseName} you scored ${work.score}/${work.maxScore}.`
            );
            if (feedbackSentence) {
              bodyParts.push(feedbackSentence);
            }
            if (base64Images.length > 0) {
              bodyParts.push(
                base64Images.length === 1
                  ? 'A scan of your work is attached.'
                  : 'Scans of your work are attached.'
              );
            } else {
              bodyParts.push(
                doPostToClassroom
                  ? 'Your grade and feedback have been updated in Google Classroom.'
                  : 'Your grade and feedback are included in this email.'
              );
            }

            const body = bodyParts.join(' ');

            try {
              await classroom.sendGradeEmail(
                work.studentEmail,
                subject,
                body,
                base64Images.length > 0 ? base64Images : undefined
              );
              setSyncProgress((prev) => ({
                ...prev,
                emailSuccesses: prev.emailSuccesses + 1,
              }));
              emailOk = true;
              logEvent('sync_email_success', {
                studentEmail: work.studentEmail,
                assignmentId: work.assignmentId,
              });
            } catch (e) {
              console.warn(`Grade posted but email to ${work.studentEmail} failed:`, e);
              setSyncProgress((prev) => ({
                ...prev,
                emailFailures: prev.emailFailures + 1,
              }));
              emailOk = false;
              logEvent('sync_email_error', {
                studentEmail: work.studentEmail,
                assignmentId: work.assignmentId,
                error: String(e),
              });
            }
          }
        }

        successes++;
        const key = workKey(work);
        successKeys.add(key);
        receiptList.push({
          key,
          studentName: work.studentName,
          ok: true,
          emailOk,
          emailSkipped,
          emailMissing,
          drivePagesSaved: driveSaved,
          driveSkipped,
          classroomSkipped: !doPostToClassroom,
        });
      } catch (err) {
        console.error(err);
        failures++;
        const key = workKey(work);
        failedKeys.add(key);
        receiptList.push({
          key,
          studentName: work.studentName,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    setSyncProgress((prev) => ({
      ...prev,
      message: t('gradeSync.syncing.finalizing'),
      successes,
      failures,
    }));
    if (failures === 0) {
      logEvent('sync_success', { total: worksToSync.length });
    } else {
      logEvent('sync_error', { total: worksToSync.length, successes, failures });
    }
    const successesToArchive = worksToSync.filter((w) => successKeys.has(workKey(w)));
    setHistory((prev) => [...successesToArchive, ...prev]);
    setGradedWorks(() => {
      // Remove only successful items from the local queue; keep failures for retry.
      if (indexSet) {
        return currentWorks.filter((w, i) => {
          if (!indexSet.has(i)) return true;
          return !successKeys.has(workKey(w));
        });
      }
      return currentWorks.filter((w) => !successKeys.has(workKey(w)));
    });
    setSyncReceipts(receiptList);
    setSyncFailedKeys(failedKeys);
    setTimeout(() => setPhase(AppPhase.FINALE), 1000);
  };

  const bumpNavUsage = useCallback((key: 'plan' | 'grade' | 'teach' | 'communicate') => {
    setNavUsage((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
  }, []);

  const openScheduleToItem = useCallback(
    (itemId: string, dateISO: string) => {
      bumpNavUsage('teach');
      setScheduleCursor(dateISO);
      setScheduleView('agenda');
      setScheduleFocusRequest({ itemId, dateISO });
      setPhase(AppPhase.SCHEDULE);
    },
    [bumpNavUsage]
  );

  useEffect(() => {
    if (phase !== AppPhase.SCHEDULE || !scheduleFocusRequest) return;
    const { itemId, dateISO } = scheduleFocusRequest;
    const esc =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape
        : (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const run = () => {
      let el = document.querySelector(
        `[data-schedule-focus-id="${esc(itemId)}"][data-schedule-focus-date="${esc(dateISO)}"]`
      ) as HTMLElement | null;
      if (!el) {
        el = document.querySelector(
          `[data-schedule-focus-id="${esc(itemId)}"]`
        ) as HTMLElement | null;
      }
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.add(
          'ring-2',
          'ring-indigo-400',
          'ring-offset-2',
          'ring-offset-white',
          'dark:ring-offset-slate-900'
        );
        window.setTimeout(() => {
          el.classList.remove(
            'ring-2',
            'ring-indigo-400',
            'ring-offset-2',
            'ring-offset-white',
            'dark:ring-offset-slate-900'
          );
        }, 2200);
      }
      setScheduleFocusRequest(null);
    };
    const t = window.setTimeout(run, 450);
    return () => window.clearTimeout(t);
  }, [phase, scheduleFocusRequest]);

  const showMobileShellNav =
    isSignedIn && phase !== AppPhase.CLASS_PRESENTER && !hideNonGradeHubPhases;

  const appContextValue: import('./context/AppContext').AppContextValue = {
    phase,
    setPhase,
    isSignedIn,
    accessToken,
    educatorName,
    todayLabel,
    isOnline,
    isDarkMode,
    setIsDarkMode,
    syncStatus,
    navUsage,
    bumpNavUsage,
    recordShortcutUse,
    homeUrgentAlerts,
    homeTopPriorityActions,
    homeWeekInsights,
    homeShortcutOrder,
    openScheduleToItem,
    homeSummary,
    handleShareApp,
    handleSignOut,
    authError,
    authSuccessMessage,
    handleGoogleLogin,
    handlePasswordReset,
    showMoreAuthOptions,
    setShowMoreAuthOptions,
    authMode,
    setAuthMode,
    handleEmailLogin,
    email,
    setEmail,
    password,
    setPassword,
    fullName,
    setFullName,
    salutation,
    setSalutation,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    gradingOnlyMode: GRADING_ONLY_MODE,
    openGradingSettings: GRADING_ONLY_MODE ? () => setGradingOnlySettingsOpen(true) : undefined,
    openGradingBilling: GRADING_ONLY_MODE
      ? () => setPhase(isPaid ? AppPhase.DASHBOARD : AppPhase.PAYWALL)
      : undefined,
    showMobileShellNav,
    mobileShellNavOpen,
    setMobileShellNavOpen,
  };

  const renderGradeCoursePicker = () => {
    const atRiskCourseIds = new Set<string>();
    history.forEach((w) => {
      if (!w.courseId || !w.maxScore) return;
      const pct = w.maxScore ? (w.score / w.maxScore) * 100 : 0;
      if (pct < 70) atRiskCourseIds.add(w.courseId);
    });

    const pendingByCourseId: Record<string, number> = {};
    gradedWorks.forEach((w) => {
      if (!w.courseId) return;
      pendingByCourseId[w.courseId] = (pendingByCourseId[w.courseId] || 0) + 1;
    });

    const coursesFilteredForUI = dashboardResults.courses.filter((course) =>
      deferredCourseSearch.trim()
        ? course.name.toLowerCase().includes(deferredCourseSearch.toLowerCase())
        : true
    );
    const pinnedSet = new Set(pinnedCourseIds);
    const isSearching = !!deferredCourseSearch.trim();
    const pinned = coursesFilteredForUI.filter((c) => pinnedSet.has(c.id));
    const recent = coursesFilteredForUI
      .filter((c) => !pinnedSet.has(c.id))
      .sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0));
    const flat = showCourses ? coursesFilteredForUI : coursesFilteredForUI.slice(0, 8);

    const togglePin = (courseId: string) => {
      setPinnedCourseIds((prev) =>
        prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [courseId, ...prev]
      );
    };

    const renderCourseRow = (course: Course) => {
      const active = selectedCourse?.id === course.id;
      const pending = pendingByCourseId[course.id] || 0;
      const atRisk = atRiskCourseIds.has(course.id);
      const isPinned = pinnedSet.has(course.id);
      const sourceLabel = course.source === 'local' ? 'Local' : 'Google';
      return (
        <button
          key={course.id}
          type="button"
          onClick={() => void selectCourse(course)}
          className={`w-full p-3 rounded-xl border flex items-center justify-between gap-3 text-left mb-2 ${
            active
              ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400/60'
              : 'bg-white dark:bg-slate-800 border-slate-200/70 dark:border-slate-700/60 hover:opacity-90'
          }`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-black text-sm truncate text-slate-900 dark:text-white">
                {course.name}
              </div>
              {isPinned && (
                <span className="px-2 py-0.5 rounded-full bg-indigo-600/10 border border-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-[9px] font-black uppercase tracking-widest shrink-0">
                  Pinned
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-slate-900/5 dark:bg-white/10 border border-slate-200/60 dark:border-slate-700/60 text-[9px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-200">
                {sourceLabel}
              </span>
              {pending > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                  Queue · {pending}
                </span>
              )}
              {atRisk && (
                <span className="px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-[9px] font-black uppercase tracking-widest text-rose-600 dark:text-rose-300">
                  At‑risk
                </span>
              )}
              <span className="text-[11px] text-slate-500 dark:text-slate-300 truncate">
                {course.period}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              togglePin(course.id);
            }}
            className={`p-2 rounded-xl border shrink-0 transition-colors ${
              isPinned
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white/60 dark:bg-slate-900/40 text-slate-600 dark:text-slate-200 border-slate-200/70 dark:border-slate-700/60 hover:bg-white/80 dark:hover:bg-slate-900/60'
            }`}
            title={isPinned ? t('grade.flow.unpinCourse') : t('grade.flow.pinCourse')}
          >
            <Pin className="w-4 h-4" />
          </button>
        </button>
      );
    };

    return (
      <PageWrapper
        headerTitle={t('grade.flow.headers.selectCourse')}
        headerSubtitle={todayLabel || undefined}
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        onSyncClick={retryClassroomSync}
        onBack={() => setPhase(AppPhase.DASHBOARD)}
      >
        <GradeFlowShell
          step={t('grade.flow.coursePicker.shellStep')}
          title={t('grade.flow.coursePicker.shellTitle')}
          subtitle={t('grade.flow.coursePicker.shellSubtitle')}
        >
          <GradeStepHeader
            label={t('grade.flow.coursePicker.stepLabel')}
            title={t('grade.flow.coursePicker.stepTitle')}
            courseName={selectedCourse?.name}
            assignmentTitle={selectedAssignment?.title}
          />
          <GradePipelineSteps activeIndex={0} className="mb-1" />
          <div className="flex flex-col gap-3">
            <div className="bg-white/70 dark:bg-slate-800/55 border border-slate-200/70 dark:border-slate-700/60 rounded-2xl p-4 shadow-sm">
              <input
                type="search"
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
                placeholder={t('grade.flow.coursePicker.searchPlaceholder')}
                className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/60 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none"
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-black uppercase tracking-widest text-slate-500">
                  {t(
                    coursesFilteredForUI.length === 1
                      ? 'grade.flow.coursePicker.coursesCountOne'
                      : 'grade.flow.coursePicker.coursesCountMany',
                    { count: coursesFilteredForUI.length }
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="text-[11px] font-semibold uppercase tracking-widest text-indigo-600 dark:text-indigo-400 hover:opacity-90"
                    onClick={() => setPhase(AppPhase.COURSE_CREATION)}
                  >
                    {t('grade.flow.coursePicker.newCourseLink')}
                  </button>
                  <button
                    type="button"
                    className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 dark:text-slate-300 hover:opacity-90"
                    onClick={() => setShowCourses((prev) => !prev)}
                  >
                    {showCourses
                      ? t('grade.flow.coursePicker.showLess')
                      : t('grade.flow.coursePicker.showAll')}
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200/70 bg-white/60 p-2 shadow-sm dark:border-slate-700/60 dark:bg-slate-800/40">
              {coursesFilteredForUI.length === 0 && (
                <div className="py-10 text-center text-slate-500 text-sm font-bold">
                  {t('grade.flow.coursePicker.noMatch')}
                </div>
              )}
              {isSearching ? (
                flat.map(renderCourseRow)
              ) : (
                <>
                  {pinned.length > 0 && (
                    <div className="px-2 pt-2 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {t('grade.flow.coursePicker.pinned')}
                    </div>
                  )}
                  {pinned.map(renderCourseRow)}
                  {recent.length > 0 && (
                    <div className="px-2 pt-4 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {t('grade.flow.coursePicker.recent')}
                    </div>
                  )}
                  {(showCourses ? recent : recent.slice(0, Math.max(0, 8 - pinned.length))).map(
                    renderCourseRow
                  )}
                </>
              )}
            </div>
          </div>
        </GradeFlowShell>
      </PageWrapper>
    );
  };

  const ensureRosterData = async (course: Course) => {
    setRosterError(null);
    if (rosterCourseStudents[course.id] && rosterCourseAssignments[course.id]) return;
    if (!classroom || !isOnline || course.source === 'local') {
      setRosterCourseStudents((prev) =>
        prev[course.id] ? prev : { ...prev, [course.id]: prev[course.id] ?? [] }
      );
      setRosterCourseAssignments((prev) =>
        prev[course.id] ? prev : { ...prev, [course.id]: prev[course.id] ?? [] }
      );
      return;
    }
    try {
      setRosterLoadingCourseId(course.id);
      const [studentsData, assignmentsData] = await Promise.all([
        classroom.getStudents(course.id),
        classroom.getAssignments(course.id),
      ]);
      setRosterCourseStudents((prev) => ({ ...prev, [course.id]: studentsData }));
      setRosterCourseAssignments((prev) => ({ ...prev, [course.id]: assignmentsData }));
    } catch (e) {
      console.error('Failed to load roster data', e);
      setRosterError(t('grade.flow.errors.rosterLoadFailed'));
    } finally {
      setRosterLoadingCourseId(null);
    }
  };

  const ensureTeachRosterData = async (course: Course) => {
    setTeachRosterError(null);
    if (teachRosterCourseStudents[course.id] && teachRosterCourseAssignments[course.id]) return;

    if (!classroom || !isOnline || course.source === 'local') {
      setTeachRosterCourseStudents((prev) =>
        prev[course.id] ? prev : { ...prev, [course.id]: prev[course.id] ?? [] }
      );
      setTeachRosterCourseAssignments((prev) =>
        prev[course.id] ? prev : { ...prev, [course.id]: prev[course.id] ?? [] }
      );
      return;
    }

    try {
      setTeachRosterLoadingCourseId(course.id);
      const [studentsData, assignmentsData] = await Promise.all([
        classroom.getStudents(course.id),
        classroom.getAssignments(course.id),
      ]);
      setTeachRosterCourseStudents((prev) => ({ ...prev, [course.id]: studentsData }));
      setTeachRosterCourseAssignments((prev) => ({ ...prev, [course.id]: assignmentsData }));
    } catch (e) {
      console.error('Failed to load teach roster data', e);
      setTeachRosterError(t('grade.flow.errors.rosterLoadFailed'));
    } finally {
      setTeachRosterLoadingCourseId(null);
    }
  };

  useEffect(() => {
    const was = previousPhaseForTeachPrefillRef.current;
    previousPhaseForTeachPrefillRef.current = phase;

    if (phase !== AppPhase.CLASSROOM) return;
    if (was === AppPhase.CLASS_PRESENTER) return;
    if (was === AppPhase.CLASSROOM) return;
    if (!selectedCourse || !selectedAssignment) return;

    setTeachRosterCourseOpenId(selectedCourse.id);
    setTeachRosterSelectedAssignmentId(selectedAssignment.id);
    setTeachRosterSelectedStudentIds(new Set(students.map((s) => s.id)));

    if (selectedCourse.source === 'local') {
      setTeachRosterCourseStudents((p) => ({ ...p, [selectedCourse.id]: students }));
      setTeachRosterCourseAssignments((p) => ({ ...p, [selectedCourse.id]: assignments }));
      return;
    }
    void ensureTeachRosterData(selectedCourse);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill only on phase transition; ensureTeachRosterData is stable enough for this call site
  }, [phase, selectedCourse, selectedAssignment, students, assignments]);

  const renderRosterView = () => {
    const openCourse = rosterCourseOpenId ? courses.find((c) => c.id === rosterCourseOpenId) : null;
    const openStudents = openCourse ? (rosterCourseStudents[openCourse.id] ?? []) : [];
    const openAssignments = openCourse ? (rosterCourseAssignments[openCourse.id] ?? []) : [];
    const selectedAssignment =
      openAssignments.find((a) => a.id === rosterSelectedAssignmentId) ?? null;
    const canAdd = !!openCourse && !!selectedAssignment && rosterSelectedStudentIds.size > 0;

    return (
      <PageWrapper
        headerTitle={educatorName || t('grade.flow.headers.rosterFallback')}
        headerSubtitle={todayLabel || undefined}
        onBack={() => setPhase(AppPhase.DASHBOARD)}
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        onSyncClick={retryClassroomSync}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden pb-3">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            {t('grade.flow.rosterManual.introLead')}{' '}
            <span className="font-semibold">{t('grade.flow.rosterManual.introBold')}</span>{' '}
            {t('grade.flow.rosterManual.introTrail')}
          </div>

          {rosterError && (
            <div className="px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-300 text-sm">
              {rosterError}
            </div>
          )}

          <div className="space-y-2">
            {courses.map((course) => {
              const isOpen = rosterCourseOpenId === course.id;
              const isLoading = rosterLoadingCourseId === course.id;
              const count = rosterCourseStudents[course.id]?.length;
              return (
                <div
                  key={course.id}
                  className="border border-slate-200/60 dark:border-slate-700/60 rounded-xl overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => {
                      const nextOpen = isOpen ? null : course.id;
                      setRosterCourseOpenId(nextOpen);
                      setRosterSelectedStudentIds(new Set());
                      setRosterSelectedAssignmentId('');
                      if (!isOpen) void ensureRosterData(course);
                    }}
                    className="w-full flex items-center justify-between px-3 py-3 bg-white/40 dark:bg-slate-900/40 hover:bg-white/60 dark:hover:bg-slate-900/60 transition-colors"
                  >
                    <div className="min-w-0 text-left">
                      <div className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {course.name}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {course.period}
                        {typeof count === 'number'
                          ? t('grade.flow.rosterManual.studentCount', { count })
                          : ''}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
                      {isLoading
                        ? t('grade.flow.rosterManual.loading')
                        : isOpen
                          ? t('grade.flow.rosterManual.hide')
                          : t('grade.flow.rosterManual.open')}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-3 py-3 space-y-3 bg-white/20 dark:bg-slate-900/20">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {t('grade.flow.rosterManual.assignmentLabel')}
                        </label>
                        <select
                          value={rosterSelectedAssignmentId}
                          onChange={(e) => setRosterSelectedAssignmentId(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 text-sm text-slate-800 dark:text-slate-100 outline-none"
                        >
                          <option value="">{t('grade.flow.rosterManual.selectAssignment')}</option>
                          {openAssignments.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.title} ({a.maxScore})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            {t('grade.flow.rosterManual.studentsLabel')}
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              if (openStudents.length === 0) return;
                              const all = rosterSelectedStudentIds.size !== openStudents.length;
                              setRosterSelectedStudentIds(
                                all ? new Set(openStudents.map((s) => s.id)) : new Set()
                              );
                            }}
                            className="text-xs font-semibold text-slate-500 dark:text-slate-400 underline underline-offset-2"
                          >
                            {openStudents.length === 0
                              ? ''
                              : rosterSelectedStudentIds.size === openStudents.length
                                ? t('grade.flow.rosterManual.clear')
                                : t('grade.flow.rosterManual.selectAll')}
                          </button>
                        </div>

                        {openStudents.length === 0 ? (
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {course.source === 'local'
                              ? t('grade.flow.rosterManual.localNoRoster')
                              : isOnline
                                ? t('grade.flow.rosterManual.noStudents')
                                : t('grade.flow.rosterManual.offlineRoster')}
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto custom-scrollbar border border-slate-200/60 dark:border-slate-700/60 rounded-xl bg-white/50 dark:bg-slate-900/50">
                            {openStudents.map((s) => {
                              const checked = rosterSelectedStudentIds.has(s.id);
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => {
                                    setRosterSelectedStudentIds((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(s.id)) next.delete(s.id);
                                      else next.add(s.id);
                                      return next;
                                    });
                                  }}
                                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/60 dark:hover:bg-slate-900/60 transition-colors"
                                >
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                                      {s.name}
                                    </div>
                                    {s.email && (
                                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                        {s.email}
                                      </div>
                                    )}
                                  </div>
                                  <div
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${checked ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300 dark:border-slate-600'}`}
                                  >
                                    {checked && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        <button
                          type="button"
                          disabled={!canAdd}
                          onClick={() => {
                            if (!openCourse || !selectedAssignment) return;
                            const picked = openStudents.filter((s) =>
                              rosterSelectedStudentIds.has(s.id)
                            );
                            if (picked.length === 0) return;
                            const nowTs = Date.now();
                            const newWorks: GradedWork[] = picked.map((student) => ({
                              studentId: student.id,
                              studentName: student.name,
                              studentEmail: student.email,
                              score: 0,
                              maxScore: selectedAssignment.maxScore,
                              feedback: '',
                              imageUrls: [],
                              status: 'draft',
                              timestamp: nowTs,
                              courseName: openCourse.name,
                              assignmentName: selectedAssignment.title,
                              courseId: openCourse.id,
                              assignmentId: selectedAssignment.id,
                            }));
                            setGradedWorks((prev) => [...prev, ...newWorks]);
                            setSelectedCourse(openCourse);
                            setSelectedAssignment(selectedAssignment);
                            setAssignments(openAssignments);
                            setStudents(openStudents);
                            setPhase(AppPhase.AUDIT);
                          }}
                          className={`w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-colors ${
                            canAdd
                              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                              : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-500 cursor-not-allowed'
                          }`}
                        >
                          {t('grade.flow.rosterManual.addToReview', {
                            count: rosterSelectedStudentIds.size || 0,
                          })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </PageWrapper>
    );
  };

  const renderAssignmentSelect = () => (
    <PageWrapper
      headerTitle={selectedCourse?.name}
      headerSubtitle={t('grade.flow.headers.chooseAssignment')}
      onBack={() => setPhase(AppPhase.GRADE_COURSE_PICKER)}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <GradeFlowShell
        step={t('grade.flow.assignmentSelect.shellStep')}
        title={t('grade.flow.assignmentSelect.shellTitle')}
        subtitle={t('grade.flow.assignmentSelect.shellSubtitle')}
      >
        <GradeStepHeader
          label={t('grade.flow.assignmentSelect.stepLabel')}
          title={t('grade.flow.assignmentSelect.stepTitle')}
          courseName={selectedCourse?.name}
          assignmentTitle={selectedAssignment?.title}
        />
        <GradePipelineSteps activeIndex={selectedAssignment ? 1 : 0} className="mb-1" />
        <div className="flex flex-col gap-4 pb-2">
          <div className="bg-white/70 dark:bg-slate-800/55 border border-slate-200/70 dark:border-slate-700/60 rounded-2xl p-4 shadow-sm">
            <input
              type="search"
              value={assignmentSearchQuery}
              onChange={(e) => setAssignmentSearchQuery(e.target.value)}
              placeholder={t('grade.flow.assignmentSelect.searchPlaceholder')}
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/60 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none"
            />
            <button
              type="button"
              onClick={(e) => selectedCourse && handleOpenAsnCreation(selectedCourse, e)}
              disabled={
                !classroom || !isOnline || !selectedCourse || selectedCourse.source === 'local'
              }
              className="mt-3 w-full py-3 rounded-xl bg-emerald-500 text-white font-black uppercase tracking-widest text-[12px] shadow-sm hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('grade.flow.assignmentSelect.createInClassroom')}
            </button>
            {(!classroom || !isOnline || selectedCourse?.source === 'local') && (
              <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-300">
                {t('grade.flow.assignmentSelect.createHint')}
              </div>
            )}
          </div>

          <div className="px-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-2">
              {classroom && isOnline && selectedCourse?.source !== 'local'
                ? t('grade.flow.assignmentSelect.listHeadingClassroom')
                : t('grade.flow.assignmentSelect.listHeadingLocal')}
            </div>
          </div>

          {filteredAssignmentsList.map((assignment) => (
            <div
              key={assignment.id}
              draggable
              onDragStart={() => setDragAssignmentId(assignment.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!dragAssignmentId || dragAssignmentId === assignment.id) return;
                setAssignmentSort('manual');
                setAssignments((prev) => {
                  const next = [...prev];
                  const from = next.findIndex((a) => a.id === dragAssignmentId);
                  const to = next.findIndex((a) => a.id === assignment.id);
                  if (from === -1 || to === -1) return prev;
                  const [item] = next.splice(from, 1);
                  next.splice(to, 0, item);
                  return next;
                });
                setDragAssignmentId(null);
              }}
              className={`p-4 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border rounded-xl flex items-center justify-between shadow-sm hover:-translate-y-0.5 transition-all ${
                dragAssignmentId === assignment.id
                  ? 'border-emerald-400 ring-2 ring-emerald-300'
                  : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400'
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  setAssignments((prev) =>
                    prev.map((a) => (a.id === assignment.id ? { ...a, lastUsed: Date.now() } : a))
                  );
                  setSelectedAssignment({ ...assignment, lastUsed: Date.now() });
                  setPhase(AppPhase.GRADE_SCAN_SETUP);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (!selectedCourse) return;
                  const title = window.prompt(
                    t('grade.flow.assignmentSelect.renamePrompt'),
                    assignment.title
                  );
                  if (!title || !title.trim()) return;
                  if (classroom && isOnline && selectedCourse.source !== 'local') {
                    classroom
                      .updateAssignment(selectedCourse.id, assignment.id, title.trim())
                      .then((updated) => {
                        setAssignments((prev) =>
                          prev.map((a) => (a.id === assignment.id ? { ...a, ...updated } : a))
                        );
                      })
                      .catch((err) => {
                        console.error('Failed to rename assignment', err);
                        setAuthError('Could not rename assignment in Google Classroom.');
                      });
                  } else {
                    setAssignments((prev) =>
                      prev.map((a) => (a.id === assignment.id ? { ...a, title: title.trim() } : a))
                    );
                  }
                }}
                className="flex items-center gap-4 flex-1 text-left"
              >
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center text-slate-500 dark:text-slate-400">
                  <Layers className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-black text-slate-800 dark:text-slate-100 truncate">
                    {assignment.title}
                  </h4>
                  <p className="text-slate-500 text-[9px] font-black uppercase tracking-[0.15em]">
                    {t('grade.flow.assignmentSelect.pointsLabel', { max: assignment.maxScore })}
                  </p>
                </div>
              </button>

              <div className="flex items-center gap-1 ml-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!selectedCourse) return;
                    if (!window.confirm(t('grade.flow.assignmentSelect.deleteConfirm'))) return;
                    if (classroom && isOnline && selectedCourse.source !== 'local') {
                      classroom
                        .deleteAssignment(selectedCourse.id, assignment.id)
                        .then(() => {
                          setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
                          if (selectedAssignment?.id === assignment.id) {
                            setSelectedAssignment(null);
                          }
                        })
                        .catch((err) => {
                          console.error('Failed to delete assignment', err);
                          setAuthError('Could not delete assignment in Google Classroom.');
                        });
                    } else {
                      setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
                      if (selectedAssignment?.id === assignment.id) {
                        setSelectedAssignment(null);
                      }
                    }
                  }}
                  className="p-1.5 rounded-full bg-white/60 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-rose-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                  title={t('grade.flow.assignmentSelect.deleteTitle')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {filteredAssignmentsList.length === 0 && (
            <div className="py-10 text-center text-slate-500 text-sm font-bold">
              {t('grade.flow.assignmentSelect.noMatch')}
            </div>
          )}
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );

  const renderScanSetup = () => (
    <PageWrapper
      headerTitle={t('grade.flow.headers.scanSetup')}
      headerSubtitle={selectedAssignment?.title || undefined}
      onBack={() => setPhase(AppPhase.ASSIGNMENT_SELECT)}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <GradeFlowShell
        step={t('grade.flow.scanSetup.shellStep')}
        title={t('grade.flow.scanSetup.shellTitle')}
        subtitle={t('grade.flow.scanSetup.shellSubtitle')}
      >
        <GradeStepHeader
          label={t('grade.flow.scanSetup.stepLabel')}
          title={t('grade.flow.scanSetup.stepTitle')}
          courseName={selectedCourse?.name}
          assignmentTitle={selectedAssignment?.title}
        />
        <GradePipelineSteps activeIndex={1} className="mb-1" />
        <div className="flex flex-col gap-4 pb-2">
          <div className="p-4 rounded-2xl bg-white/70 dark:bg-slate-800/55 border border-slate-200/70 dark:border-slate-700/60 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {t('grade.flow.scanSetup.currentSession')}
            </div>
            <div className="mt-1 text-base font-black text-slate-900 dark:text-white truncate">
              {selectedCourse?.name || t('grade.flow.scanSetup.selectCourse')} ·{' '}
              {selectedAssignment?.title || t('grade.flow.scanSetup.selectAssignment')}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
              {t('grade.flow.scanSetup.batchRecommend')}
            </div>
          </div>
          {selectedCourse && selectedAssignment && !isPaid && (
            <div
              className="p-4 rounded-2xl bg-amber-500/15 dark:bg-amber-500/10 border border-amber-500/45 dark:border-amber-400/35 shadow-sm"
              role="status"
              aria-live="polite"
            >
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-900 dark:text-amber-200">
                {t('grade.flow.scanSetup.paywallCardTitle')}
              </div>
              <p className="mt-2 text-[12px] sm:text-[13px] font-semibold text-amber-950 dark:text-amber-50 leading-snug">
                {t('grade.flow.scanSetup.paywallCardBody')}
              </p>
              <button
                type="button"
                onClick={() => {
                  logEvent('paywall_view', { surface: 'scan_setup' });
                  setPhase(AppPhase.PAYWALL);
                }}
                className="mt-3 w-full py-2.5 rounded-xl bg-amber-600 text-white text-[11px] font-black uppercase tracking-widest shadow-sm hover:bg-amber-700 transition-colors"
              >
                {t('grade.flow.scanSetup.seeProTrial')}
              </button>
            </div>
          )}
          {!selectedCourse || !selectedAssignment ? (
            <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-200 text-sm font-semibold">
              {t('grade.flow.scanSetup.selectCourseAssignmentFirst')}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => setScanStudentMode('batch')}
                  className={`p-4 rounded-2xl border text-left shadow-sm transition-colors ${
                    scanStudentMode === 'batch'
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white/70 dark:bg-slate-800/55 border-slate-200/70 dark:border-slate-700/60 hover:opacity-90'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div
                        className={`text-[10px] font-black uppercase tracking-widest ${scanStudentMode === 'batch' ? 'text-emerald-100' : 'text-slate-500'}`}
                      >
                        {t('grade.flow.scanSetup.recommended')}
                      </div>
                      <div
                        className={`mt-1 text-base font-black ${scanStudentMode === 'batch' ? 'text-white' : 'text-slate-900 dark:text-white'}`}
                      >
                        {t('grade.flow.scanSetup.batchScan')}
                      </div>
                      <div
                        className={`text-sm mt-0.5 ${scanStudentMode === 'batch' ? 'text-emerald-50/90' : 'text-slate-600 dark:text-slate-300'}`}
                      >
                        {t('grade.flow.scanSetup.batchScanDesc')}
                      </div>
                    </div>
                    <div
                      className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${
                        scanStudentMode === 'batch'
                          ? 'border-white/30 bg-white/15'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                    >
                      {scanStudentMode === 'batch' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setScanStudentMode('single')}
                  className={`p-4 rounded-2xl border text-left shadow-sm transition-colors ${
                    scanStudentMode === 'single'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white/70 dark:bg-slate-800/55 border-slate-200/70 dark:border-slate-700/60 hover:opacity-90'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div
                        className={`text-[10px] font-black uppercase tracking-widest ${scanStudentMode === 'single' ? 'text-indigo-100' : 'text-slate-500'}`}
                      >
                        {t('grade.flow.scanSetup.quickStart')}
                      </div>
                      <div
                        className={`mt-1 text-base font-black ${scanStudentMode === 'single' ? 'text-white' : 'text-slate-900 dark:text-white'}`}
                      >
                        {t('grade.flow.scanSetup.singleStudent')}
                      </div>
                      <div
                        className={`text-sm mt-0.5 ${scanStudentMode === 'single' ? 'text-indigo-50/90' : 'text-slate-600 dark:text-slate-300'}`}
                      >
                        {t('grade.flow.scanSetup.singleStudentDesc')}
                      </div>
                    </div>
                    <div
                      className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${
                        scanStudentMode === 'single'
                          ? 'border-white/30 bg-white/15'
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                    >
                      {scanStudentMode === 'single' && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-auto grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                  className="w-full py-4 rounded-2xl bg-violet-600 text-white font-black uppercase tracking-widest text-[14px] shadow-sm hover:bg-violet-700 transition-colors"
                >
                  Paper scan + AI grading (beta)
                </button>
                {scanStudentMode === 'batch' ? (
                  <button
                    type="button"
                    onClick={() => setPhase(AppPhase.GRADE_BATCH_ROSTER)}
                    className="w-full py-4 rounded-2xl bg-emerald-500 text-white font-black uppercase tracking-widest text-[14px] shadow-sm hover:bg-emerald-600 transition-colors"
                  >
                    {t('grade.flow.scanSetup.chooseStudentsOrder')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPhase(AppPhase.RUBRIC_SETUP)}
                    className="w-full py-4 rounded-2xl bg-indigo-600 text-white font-black uppercase tracking-widest text-[14px] shadow-sm hover:bg-indigo-700 transition-colors"
                  >
                    {t('grade.flow.scanSetup.startScanning')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );

  const renderBatchRoster = () => (
    <PageWrapper
      headerTitle={t('grade.flow.headers.batchRoster')}
      headerSubtitle={selectedCourse?.name || undefined}
      onBack={() => setPhase(AppPhase.GRADE_SCAN_SETUP)}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <GradeFlowShell
        step={t('grade.flow.batchRoster.shellStep')}
        title={t('grade.flow.batchRoster.shellTitle')}
        subtitle={t('grade.flow.batchRoster.shellSubtitle')}
      >
        <GradeStepHeader
          label={t('grade.flow.batchRoster.stepLabel')}
          title={t('grade.flow.batchRoster.stepTitle')}
          courseName={selectedCourse?.name}
          assignmentTitle={selectedAssignment?.title}
        />
        <GradePipelineSteps activeIndex={1} className="mb-1" />
        <div className="flex flex-col gap-3 pb-1">
          {!selectedCourse || !selectedAssignment ? (
            <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-200 text-sm font-semibold">
              {t('grade.flow.scanSetup.selectBeforeBatchRoster')}
            </div>
          ) : (
            <>
              <div className="bg-white/70 dark:bg-slate-800/55 border border-slate-200/70 dark:border-slate-700/60 rounded-2xl p-4 shadow-sm">
                <input
                  type="search"
                  value={batchRosterQuery}
                  onChange={(e) => setBatchRosterQuery(e.target.value)}
                  placeholder={t('grade.flow.batchRoster.searchPlaceholder')}
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/60 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none"
                />

                <div className="mt-3 flex items-center gap-2 overflow-x-auto custom-scrollbar">
                  {(
                    [
                      ['all', t('grade.flow.batchRoster.filterAll')],
                      ['not_graded', t('grade.flow.batchRoster.filterNotGraded')],
                      ['at_risk', t('grade.flow.batchRoster.filterAtRisk')],
                      ['missing_email', t('grade.flow.batchRoster.filterMissingEmail')],
                      ['recently_graded', t('grade.flow.batchRoster.filterRecentlyGraded')],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setBatchRosterFilter(id)}
                      className={`px-3 py-2 rounded-full border text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-colors ${
                        batchRosterFilter === id
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white/60 dark:bg-slate-900/40 text-slate-600 dark:text-slate-200 border-slate-200/70 dark:border-slate-700/60 hover:opacity-90'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    {t('grade.flow.batchRoster.selectedCount', {
                      count: batchSelectedStudentIds.size,
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setBatchRosterSort('last_name')}
                      className={`px-3 py-2 rounded-full border text-[10px] font-black uppercase tracking-widest transition-colors ${
                        batchRosterSort === 'last_name'
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white/60 dark:bg-slate-900/40 text-slate-600 dark:text-slate-200 border-slate-200/70 dark:border-slate-700/60 hover:opacity-90'
                      }`}
                    >
                      {t('grade.flow.batchRoster.sortAz')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBatchRosterSort('manual')}
                      className={`px-3 py-2 rounded-full border text-[10px] font-black uppercase tracking-widest transition-colors ${
                        batchRosterSort === 'manual'
                          ? 'bg-emerald-500 text-white border-emerald-500'
                          : 'bg-white/60 dark:bg-slate-900/40 text-slate-600 dark:text-slate-200 border-slate-200/70 dark:border-slate-700/60 hover:opacity-90'
                      }`}
                    >
                      {t('grade.flow.batchRoster.sortManual')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 bg-white/60 dark:bg-slate-800/40 border border-slate-200/70 dark:border-slate-700/60 rounded-2xl shadow-sm overflow-hidden">
                <div className="h-full overflow-y-auto custom-scrollbar p-2">
                  {(() => {
                    const selectedCourseId = selectedCourse.id;
                    const selectedAssignmentId = selectedAssignment.id;
                    const query = deferredBatchRosterQuery.trim().toLowerCase();

                    const studentPctById: Record<string, number> = {};
                    const lastGradedById: Record<string, number> = {};
                    history.forEach((w) => {
                      if (
                        w.courseId !== selectedCourseId ||
                        w.assignmentId !== selectedAssignmentId ||
                        !w.studentId
                      )
                        return;
                      if (!studentPctById[w.studentId]) studentPctById[w.studentId] = 0;
                      const pct = w.maxScore ? (w.score / w.maxScore) * 100 : 0;
                      studentPctById[w.studentId] = pct;
                      lastGradedById[w.studentId] = Math.max(
                        lastGradedById[w.studentId] || 0,
                        w.timestamp || 0
                      );
                    });

                    const now = Date.now();
                    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

                    let roster = [...students];
                    if (query) roster = roster.filter((s) => s.name.toLowerCase().includes(query));

                    if (batchRosterFilter === 'missing_email')
                      roster = roster.filter((s) => !s.email);
                    if (batchRosterFilter === 'at_risk')
                      roster = roster.filter((s) => (studentPctById[s.id] ?? 100) < 70);
                    if (batchRosterFilter === 'recently_graded')
                      roster = roster.filter(
                        (s) => (lastGradedById[s.id] ?? 0) >= now - sevenDaysMs
                      );
                    if (batchRosterFilter === 'not_graded')
                      roster = roster.filter((s) => !lastGradedById[s.id]);

                    if (batchRosterSort === 'last_name') {
                      roster.sort((a, b) => a.name.localeCompare(b.name));
                    }

                    const move = (id: string, dir: -1 | 1) => {
                      setBatchRosterOrderIds((prev) => {
                        const idx = prev.indexOf(id);
                        if (idx === -1) return prev;
                        const nextIdx = idx + dir;
                        if (nextIdx < 0 || nextIdx >= prev.length) return prev;
                        const next = [...prev];
                        const [item] = next.splice(idx, 1);
                        next.splice(nextIdx, 0, item);
                        return next;
                      });
                    };

                    const orderedRoster =
                      batchRosterSort === 'manual' && batchRosterOrderIds.length > 0
                        ? [
                            ...batchRosterOrderIds
                              .map((id) => roster.find((s) => s.id === id))
                              .filter((s): s is Student => !!s),
                            ...roster.filter((s) => !batchRosterOrderIds.includes(s.id)),
                          ]
                        : roster;

                    if (orderedRoster.length === 0) {
                      return (
                        <div className="py-10 text-center text-slate-500 text-sm font-bold">
                          {t('grade.flow.batchRoster.noStudentsMatch')}
                        </div>
                      );
                    }

                    return orderedRoster.map((s) => {
                      const checked = batchSelectedStudentIds.has(s.id);
                      const pct = studentPctById[s.id];
                      const atRisk = typeof pct === 'number' ? pct < 70 : false;
                      return (
                        <div
                          key={s.id}
                          className={`p-3 rounded-xl border mb-2 flex items-center justify-between gap-3 ${
                            checked
                              ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400/60'
                              : 'bg-white dark:bg-slate-800 border-slate-200/70 dark:border-slate-700/60'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setBatchSelectedStudentIds((prev) => {
                                const next = new Set(prev);
                                const isSelected = next.has(s.id);
                                if (isSelected) next.delete(s.id);
                                else next.add(s.id);
                                return next;
                              });
                              if (batchRosterSort === 'manual') {
                                setBatchRosterOrderIds((prev) => {
                                  const has = prev.includes(s.id);
                                  // If already selected, remove from manual order. If newly selected, append.
                                  if (has) return prev.filter((id) => id !== s.id);
                                  return [...prev, s.id];
                                });
                              }
                            }}
                            className="flex items-center gap-3 flex-1 text-left min-w-0"
                          >
                            <div
                              className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${
                                checked
                                  ? 'border-indigo-500 bg-indigo-600'
                                  : 'border-slate-300 dark:border-slate-600'
                              }`}
                            >
                              {checked && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <div className="min-w-0">
                              <div className="font-black text-sm truncate text-slate-900 dark:text-white">
                                {s.name}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                {!s.email && (
                                  <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[9px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-200">
                                    {t('grade.flow.batchRoster.badgeNoEmail')}
                                  </span>
                                )}
                                {atRisk && (
                                  <span className="px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-[9px] font-black uppercase tracking-widest text-rose-600 dark:text-rose-300">
                                    {t('grade.flow.batchRoster.badgeAtRisk')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                          {batchRosterSort === 'manual' && checked && (
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                type="button"
                                onClick={() => move(s.id, -1)}
                                className="px-3 py-2 rounded-xl bg-white/60 dark:bg-slate-900/40 border border-slate-200/70 dark:border-slate-700/60 text-[10px] font-black uppercase tracking-widest"
                                title={t('grade.flow.batchRoster.moveUpTitle')}
                              >
                                {t('grade.flow.batchRoster.moveUpLabel')}
                              </button>
                              <button
                                type="button"
                                onClick={() => move(s.id, 1)}
                                className="px-3 py-2 rounded-xl bg-white/60 dark:bg-slate-900/40 border border-slate-200/70 dark:border-slate-700/60 text-[10px] font-black uppercase tracking-widest"
                                title={t('grade.flow.batchRoster.moveDownTitle')}
                              >
                                {t('grade.flow.batchRoster.moveDownLabel')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setScanStudentMode('batch');
                  const selectedIds = Array.from(batchSelectedStudentIds);
                  const ordered =
                    batchRosterSort === 'manual' && batchRosterOrderIds.length > 0
                      ? batchRosterOrderIds.filter((id) => batchSelectedStudentIds.has(id))
                      : selectedIds.sort((a, b) => {
                          const aName = students.find((s) => s.id === a)?.name || '';
                          const bName = students.find((s) => s.id === b)?.name || '';
                          return aName.localeCompare(bName);
                        });
                  batchStudentOrderRef.current = ordered;
                  batchNextIndexRef.current = 0;
                  setPhase(AppPhase.RUBRIC_SETUP);
                }}
                disabled={batchSelectedStudentIds.size === 0}
                className="mt-auto py-4 px-5 rounded-2xl bg-emerald-500 text-white font-black uppercase tracking-widest text-[14px] shadow-sm hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('grade.flow.batchRoster.continueToRubric')}
              </button>
            </>
          )}
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );

  const renderSyncPreflight = () => (
    <GradeSyncPreflightView
      courseNameHeader={selectedCourse?.name ?? undefined}
      selectedCourseName={selectedCourse?.name}
      selectedAssignmentTitle={selectedAssignment?.title}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
      onBack={() => setPhase(AppPhase.AUDIT)}
      gradedWorks={gradedWorks}
      syncPreflightIndexes={syncPreflightIndexes}
      syncReceipts={syncReceipts}
      syncFailedKeys={syncFailedKeys}
      isPaid={isPaid}
      gradeSyncPostToClassroom={gradeSyncPostToClassroom}
      setGradeSyncPostToClassroom={setGradeSyncPostToClassroom}
      gradeSyncEmailStudents={gradeSyncEmailStudents}
      setGradeSyncEmailStudents={setGradeSyncEmailStudents}
      gradeSyncSaveToDrive={gradeSyncSaveToDrive}
      setGradeSyncSaveToDrive={setGradeSyncSaveToDrive}
      onNavigatePaywall={(surface) => {
        logEvent('paywall_view', { surface });
        setPhase(AppPhase.PAYWALL);
      }}
      onQueueOfflinePublish={(indexes) => {
        saveOfflineGradeSyncIntent({
          indexes,
          post: gradeSyncPostToClassroom,
          email: gradeSyncEmailStudents,
          drive: gradeSyncSaveToDrive,
        });
        try {
          logEvent('grade_sync_queued_offline', { count: indexes.length });
        } catch {
          /* ignore */
        }
        appendSyncLog({
          level: 'info',
          message: t('gradeSync.offlineQueuedLog'),
        });
        setOfflineGradeSyncIntent(getOfflineGradeSyncIntent());
        setOfflineGradeSyncBannerDismissed(false);
        setPhase(AppPhase.AUDIT);
      }}
      onStartSyncOnline={(indexes) => void startSyncProcess(indexes)}
      onRetryFailedOnline={(indexes) => {
        setSyncPreflightIndexes(indexes);
        void startSyncProcess(indexes);
      }}
    />
  );

  const getScanStatusText = (progress: number) => {
    if (progress === 100) return t('grade.flow.scanStatus.success');
    if (progress < 30) return t('grade.flow.scanStatus.detecting');
    if (progress < 70) return t('grade.flow.scanStatus.enhancing');
    return t('grade.flow.scanStatus.extracting');
  };

  const renderCourseCreation = () => (
    <PageWrapper
      headerTitle={t('grade.flow.headers.newCourse')}
      headerSubtitle={t('grade.flow.headers.googleClassroom')}
      onBack={() => setPhase(AppPhase.DASHBOARD)}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <form
          onSubmit={handleCreateCourseLocal}
          className="flex flex-col gap-4 w-full min-w-0 max-w-full sm:max-w-md md:max-w-lg lg:max-w-xl mx-auto px-3 sm:px-5 pt-8 sm:pt-10 pb-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center mx-auto shadow-sm mb-4">
              <BookOpen className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white drop-shadow-sm">
              {t('grade.flow.createCourse.heading')}
            </h2>
          </div>
          {creationError && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-xs font-bold w-full text-center">
              {creationError}
            </div>
          )}
          <input
            value={newCourseName}
            onChange={(e) => setNewCourseName(e.target.value)}
            placeholder={t('grade.flow.createCourse.placeholder')}
            className="w-full p-4 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-slate-700 rounded-xl text-[16px] font-bold outline-none shadow-sm"
            required
          />
          <button
            type="submit"
            disabled={isCreatingCourse}
            className="w-full py-4 mt-4 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest text-[16px] shadow-sm flex items-center justify-center gap-2"
          >
            {isCreatingCourse ? (
              <Loader2 className="animate-spin w-5 h-5" />
            ) : (
              t('grade.flow.createCourse.submit')
            )}
          </button>
        </form>
      </div>
    </PageWrapper>
  );

  const renderAssignmentCreation = () => (
    <PageWrapper
      headerTitle={t('grade.flow.headers.newAssignment')}
      headerSubtitle={creationCourse?.name}
      onBack={() => setPhase(AppPhase.ASSIGNMENT_SELECT)}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <form
          onSubmit={handleCreateAssignment}
          className="flex flex-col gap-4 w-full min-w-0 max-w-full sm:max-w-md md:max-w-lg lg:max-w-xl mx-auto px-3 sm:px-5 pt-8 sm:pt-10 pb-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center mx-auto shadow-sm mb-4">
              <Layers className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white drop-shadow-sm">
              {t('grade.flow.createAssignment.heading')}
            </h2>
          </div>
          {creationError && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-xs font-bold w-full text-center">
              {creationError}
            </div>
          )}
          <input
            value={newAsnTitle}
            onChange={(e) => setNewAsnTitle(e.target.value)}
            placeholder={t('grade.flow.createAssignment.titlePlaceholder')}
            className="w-full p-4 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-slate-700 rounded-xl text-[16px] font-bold outline-none shadow-sm"
            required
          />
          <textarea
            value={newAsnDesc}
            onChange={(e) => setNewAsnDesc(e.target.value)}
            placeholder={t('grade.flow.createAssignment.descPlaceholder')}
            className="w-full p-4 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-slate-700 rounded-xl text-[16px] font-bold outline-none shadow-sm resize-none"
            rows={3}
          />
          <input
            type="number"
            value={newAsnMaxScore}
            onChange={(e) => setNewAsnMaxScore(Number(e.target.value))}
            placeholder={t('grade.flow.createAssignment.maxScorePlaceholder')}
            className="w-full p-4 bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-slate-700 rounded-xl text-[16px] font-bold outline-none shadow-sm"
            required
          />
          <button
            type="submit"
            disabled={isCreatingAssignment}
            className="w-full py-4 mt-4 bg-emerald-500 text-white rounded-xl font-black uppercase tracking-widest text-[16px] shadow-sm flex items-center justify-center gap-2"
          >
            {isCreatingAssignment ? (
              <Loader2 className="animate-spin w-5 h-5" />
            ) : (
              t('grade.flow.createAssignment.submit')
            )}
          </button>
        </form>
      </div>
    </PageWrapper>
  );

  const renderRubricSetup = () => {
    return (
      <PageWrapper
        headerTitle={t('grade.flow.headers.scanRubric')}
        headerSubtitle={selectedAssignment?.title || t('grade.flow.headers.criteriaSetupFallback')}
        onBack={() => setPhase(AppPhase.GRADE_SCAN_SETUP)}
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        onSyncClick={retryClassroomSync}
      >
        <GradeFlowShell
          step={t('grade.flow.rubricSetup.shellStep')}
          title={t('grade.flow.rubricSetup.shellTitle')}
          subtitle={t('grade.flow.rubricSetup.shellSubtitle')}
          footer={
            <button
              type="button"
              onClick={() => setPhase(AppPhase.MODE_SELECTION)}
              disabled={!customRubric.trim()}
              className={`w-full py-4 rounded-xl font-black text-[16px] tracking-[0.1em] uppercase shadow-sm transition-all ${customRubric.trim() ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:-translate-y-0.5 active:scale-[0.98]' : 'bg-slate-200/50 dark:bg-slate-800/50 text-slate-400 cursor-not-allowed border border-white/50 dark:border-slate-700'}`}
            >
              {t('grade.flow.rubricSetup.footerContinue')}
            </button>
          }
        >
          <GradeStepHeader
            label={t('grade.flow.rubricSetup.stepLabel')}
            title={t('grade.flow.rubricSetup.stepTitle')}
            courseName={selectedCourse?.name}
            assignmentTitle={selectedAssignment?.title}
          />
          <GradePipelineSteps activeIndex={0} className="mb-2" />
          <div className="flex flex-col gap-3">
            {rubricSuccess && (
              <div className="p-3 bg-emerald-500/10 backdrop-blur-xl border border-emerald-500/30 rounded-xl flex items-center gap-3 text-emerald-600 dark:text-emerald-400 font-bold text-[10px] tracking-widest uppercase animate-in slide-in-from-top shrink-0 shadow-sm">
                <CheckCircle className="w-4 h-4" />{' '}
                <span>{t('grade.flow.rubricSetup.capturedSuccess')}</span>
              </div>
            )}

            {!isPaid && (
              <div
                className="shrink-0 p-3 rounded-xl bg-indigo-500/10 dark:bg-indigo-500/15 border border-indigo-400/35 dark:border-indigo-400/25 text-[11px] text-indigo-950 dark:text-indigo-100 leading-snug"
                role="status"
              >
                <span className="font-black uppercase tracking-wider text-[10px] text-indigo-800 dark:text-indigo-200">
                  {t('grade.flow.rubricSetup.syncHeadsUpLabel')}
                </span>
                <p className="mt-1.5 font-medium">{t('grade.flow.rubricSetup.syncHeadsUpBody')}</p>
                <button
                  type="button"
                  onClick={() => {
                    logEvent('paywall_view', { surface: 'rubric_setup' });
                    setPhase(AppPhase.PAYWALL);
                  }}
                  className="mt-2 text-[11px] font-black text-indigo-600 dark:text-indigo-300 underline underline-offset-2"
                >
                  {t('grade.flow.rubricSetup.seeProTrial')}
                </button>
              </div>
            )}

            <div className="flex-1 min-h-0 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl rounded-3xl border border-white/60 dark:border-slate-700 overflow-hidden flex flex-col shadow-sm">
              {!isScanningRubric ? (
                <div className="flex-1 p-6 flex flex-col overflow-hidden">
                  <div className="mb-4 shrink-0 rounded-xl border border-slate-200/80 dark:border-slate-600/80 bg-slate-50/80 dark:bg-slate-900/40 p-3 space-y-2">
                    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      {t('grade.flow.rubricSetup.quickPicksLabel')}
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex-1 min-w-[10rem] flex flex-col gap-1">
                        <span className="sr-only">
                          {t('grade.flow.rubricSetup.loadSavedSrOnly')}
                        </span>
                        <select
                          value={rubricTemplatePickerId}
                          onChange={(e) => applyRubricTemplateById(e.target.value)}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-900/80 px-2 py-2 text-[11px] font-semibold text-slate-800 dark:text-slate-100"
                        >
                          <option value="">
                            {t('grade.flow.rubricSetup.loadSavedPlaceholder')}
                          </option>
                          {savedRubricTemplates.map((tpl) => (
                            <option key={tpl.id} value={tpl.id}>
                              {tpl.title}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        onClick={removeSelectedRubricTemplate}
                        disabled={!rubricTemplatePickerId}
                        className="shrink-0 rounded-lg border border-rose-200 dark:border-rose-800/60 bg-white/80 dark:bg-slate-900/60 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-rose-600 dark:text-rose-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {t('grade.flow.rubricSetup.remove')}
                      </button>
                    </div>
                    <div className="flex flex-wrap items-stretch gap-2">
                      <input
                        type="text"
                        value={rubricQuickSaveTitle}
                        onChange={(e) => setRubricQuickSaveTitle(e.target.value)}
                        placeholder={t('grade.flow.rubricSetup.quickSaveNamePlaceholder')}
                        className="min-w-[8rem] flex-1 rounded-lg border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-900/80 px-2 py-2 text-[11px] font-medium text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={saveCurrentRubricAsTemplate}
                        disabled={!customRubric.trim()}
                        className="shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-700"
                      >
                        {t('grade.flow.rubricSetup.saveQuickPick')}
                      </button>
                    </div>
                    {savedRubricTemplates.length === 0 ? (
                      <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400">
                        {t('grade.flow.rubricSetup.templatesHint')}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex justify-between items-center mb-3 shrink-0">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
                      <FileText className="w-3 h-3" /> {t('grade.flow.rubricSetup.customCriteria')}
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleGenerateRubric}
                        disabled={isGeneratingRubric}
                        className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {isGeneratingRubric ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        {t('grade.flow.rubricSetup.autoGenerate')}
                      </button>
                      <button
                        onClick={handleScanPaperRubric}
                        className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 transition-colors flex items-center gap-1"
                      >
                        <Camera className="w-3 h-3" /> {t('grade.flow.rubricSetup.scanPaper')}
                      </button>
                    </div>
                  </div>
                  <div className="relative flex-1 min-h-0">
                    <textarea
                      value={customRubric}
                      onChange={(e) => setCustomRubric(e.target.value)}
                      placeholder={t('grade.flow.rubricSetup.criteriaPlaceholder')}
                      className="w-full h-full p-4 pr-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-md border border-slate-200 dark:border-slate-700 rounded-xl resize-none outline-none focus:border-indigo-400 text-[16px] italic shadow-inner custom-scrollbar"
                    />
                    <VoiceInputButton
                      onResult={(text) =>
                        setCustomRubric((prev) => prev + (prev ? '\n' : '') + text)
                      }
                      className="absolute right-3 top-3 p-1.5"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-1 relative bg-black flex flex-col overflow-hidden animate-in zoom-in-[0.98] duration-300">
                  <style>{`
                  @keyframes scanSweep {
                    0% { top: 0%; opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    100% { top: 98%; opacity: 0; }
                  }
                  .animate-scan-sweep {
                    position: absolute;
                    width: 100%;
                    height: 3px;
                    background: #4ade80; 
                    box-shadow: 0 0 15px 3px rgba(52, 211, 153, 0.6);
                    animation: scanSweep 2s linear infinite;
                    z-index: 20;
                  }
                `}</style>

                  {cameraError ? (
                    <div className="flex-1 flex items-center justify-center p-6 text-center">
                      <div className="bg-red-500/20 text-red-400 p-4 rounded-xl border border-red-500/50 flex flex-col items-center">
                        <AlertCircle className="w-8 h-8 mb-2" />
                        <p className="font-bold text-[16px]">{cameraError}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${isProcessing ? 'opacity-20 blur-md scale-105' : 'opacity-90'}`}
                      />
                      <canvas ref={canvasRef} className="hidden" />

                      {/* ASPECT RATIO BOUNDING BOX FOR RUBRIC SCAN */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6 pb-3 z-10">
                        <div
                          className={`relative w-full max-w-[min(100%,22rem)] sm:max-w-md md:max-w-lg aspect-[3/4] border-2 transition-colors duration-500 rounded-xl ${isProcessing ? 'border-emerald-500/30' : 'border-white/10'}`}
                        >
                          <div
                            className={`absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 rounded-tl-2xl transition-colors duration-300 ${isProcessing ? 'border-emerald-500' : 'border-indigo-500'}`}
                          />
                          <div
                            className={`absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 rounded-tr-2xl transition-colors duration-300 ${isProcessing ? 'border-emerald-500' : 'border-indigo-500'}`}
                          />
                          <div
                            className={`absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 rounded-bl-2xl transition-colors duration-300 ${isProcessing ? 'border-emerald-500' : 'border-indigo-500'}`}
                          />
                          <div
                            className={`absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 rounded-br-2xl transition-colors duration-300 ${isProcessing ? 'border-emerald-500' : 'border-indigo-500'}`}
                          />

                          {!isProcessing && <div className="animate-scan-sweep" />}
                        </div>
                      </div>

                      <div className="absolute inset-0 z-30 flex flex-col items-center justify-between p-6 pb-8 pointer-events-none">
                        <div className="mt-4 px-5 py-2 bg-black/60 backdrop-blur-md rounded-full border border-white/10 shadow-lg pointer-events-auto">
                          <span className="text-[10px] font-black uppercase tracking-widest text-white">
                            {isProcessing
                              ? t('grade.flow.rubricSetup.processingDocument')
                              : t('grade.flow.rubricSetup.alignOrCapture')}
                          </span>
                        </div>

                        {rubricScanError && (
                          <div className="mt-4 px-4 py-3 bg-rose-500/90 backdrop-blur-md border border-rose-400 rounded-xl text-white text-[11px] font-bold text-center shadow-lg animate-in slide-in-from-top fade-in pointer-events-auto">
                            {rubricScanError}
                          </div>
                        )}

                        {isProcessing && (
                          <div className="flex flex-col items-center justify-center animate-in zoom-in duration-300 mb-10 pointer-events-auto">
                            <div className="relative w-32 h-32 flex items-center justify-center bg-black/40 rounded-full shadow-[0_0_30px_rgba(16,185,129,0.2)] backdrop-blur-md">
                              <svg className="w-full h-full transform -rotate-90">
                                <circle
                                  cx="64"
                                  cy="64"
                                  r="56"
                                  className="stroke-slate-800"
                                  strokeWidth="8"
                                  fill="none"
                                />
                                <circle
                                  cx="64"
                                  cy="64"
                                  r="56"
                                  className="stroke-emerald-400 transition-all duration-[50ms] ease-linear"
                                  strokeWidth="8"
                                  strokeLinecap="round"
                                  fill="none"
                                  strokeDasharray="351.8"
                                  strokeDashoffset={351.8 - (351.8 * rubricScanProgress) / 100}
                                />
                              </svg>
                              <div className="absolute flex flex-col items-center">
                                <span className="text-white font-black text-3xl">
                                  {rubricScanProgress}
                                  <span className="text-sm text-emerald-400">%</span>
                                </span>
                              </div>
                            </div>
                            <div className="mt-6 px-6 py-3 bg-black/70 backdrop-blur-xl rounded-xl border border-emerald-500/30 flex items-center gap-3 shadow-lg">
                              {rubricScanProgress < 100 ? (
                                <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-emerald-400" />
                              )}
                              <span className="text-[11px] font-black uppercase tracking-widest text-emerald-400">
                                {getScanStatusText(rubricScanProgress)}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col items-center gap-6 mt-auto pointer-events-auto">
                          {!isProcessing && (
                            <button
                              onClick={handleRubricSnap}
                              className="w-20 h-20 rounded-full border-[4px] border-emerald-400 bg-emerald-500/20 active:scale-90 flex items-center justify-center transition-all shadow-[0_0_30px_rgba(52,211,153,0.3)] backdrop-blur-md"
                            >
                              <Camera className="w-8 h-8 text-emerald-400" />
                            </button>
                          )}
                          <button
                            onClick={() => setIsScanningRubric(false)}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white backdrop-blur-md rounded-xl font-black uppercase text-[10px] tracking-widest border border-white/20 active:scale-95 transition-all"
                          >
                            {t('grade.flow.rubricSetup.cancelScan')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </GradeFlowShell>
      </PageWrapper>
    );
  };

  const renderModeSelection = () => (
    <PageWrapper
      headerTitle={t('grade.flow.headers.scanStudentWork')}
      headerSubtitle={
        selectedCourse && selectedAssignment
          ? t('grade.flow.headers.courseAssignmentContext', {
              course: selectedCourse.name,
              assignment: selectedAssignment.title,
            })
          : undefined
      }
      onBack={() => setPhase(AppPhase.RUBRIC_SETUP)}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <GradeFlowShell
        step={t('grade.flow.modeSelection.shellStep')}
        title={t('grade.flow.modeSelection.shellTitle')}
        subtitle={t('grade.flow.modeSelection.shellSubtitle')}
      >
        <GradeStepHeader
          label={t('grade.flow.modeSelection.stepLabel')}
          title={t('grade.flow.modeSelection.stepTitle')}
          courseName={selectedCourse?.name}
          assignmentTitle={selectedAssignment?.title}
        />
        <GradePipelineSteps activeIndex={1} className="mb-2" />
        <div className="mx-auto flex w-full min-w-0 max-w-full flex-col gap-4 sm:max-w-md md:max-w-lg lg:max-w-xl">
          <div className="flex flex-col gap-3">
            <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest">
              {t('grade.flow.modeSelection.chooseAttribution')}
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setScanStudentMode('single');
                  setBatchSelectedStudentIds(new Set());
                }}
                className={`p-5 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border rounded-xl flex items-center justify-between shadow-sm transition-all ${
                  scanStudentMode === 'single'
                    ? 'border-emerald-400 hover:border-emerald-400'
                    : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400/60'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
                    <Camera className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-black text-slate-800 dark:text-slate-100">
                      {t('grade.flow.modeSelection.singleTitle')}
                    </h3>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">
                      {t('grade.flow.modeSelection.singleHint')}
                    </p>
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full border ${scanStudentMode === 'single' ? 'border-emerald-400 bg-emerald-500' : 'border-slate-300 dark:border-slate-600'} flex items-center justify-center`}
                >
                  {scanStudentMode === 'single' && <Check className="w-4 h-4 text-white" />}
                </div>
              </button>

              <button
                onClick={() => setScanStudentMode('batch')}
                className={`p-5 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border rounded-xl flex items-center justify-between shadow-sm transition-all ${
                  scanStudentMode === 'batch'
                    ? 'border-indigo-400 hover:border-indigo-400'
                    : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400/60'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-black text-slate-800 dark:text-slate-100">
                      {t('grade.flow.modeSelection.batchTitle')}
                    </h3>
                    <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">
                      {t('grade.flow.modeSelection.batchHint')}
                    </p>
                  </div>
                </div>
                <div
                  className={`w-6 h-6 rounded-full border ${scanStudentMode === 'batch' ? 'border-indigo-400 bg-indigo-600' : 'border-slate-300 dark:border-slate-600'} flex items-center justify-center`}
                >
                  {scanStudentMode === 'batch' && <Check className="w-4 h-4 text-white" />}
                </div>
              </button>
            </div>

            {scanStudentMode === 'batch' && (
              <div className="p-4 rounded-xl bg-white/50 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-black text-slate-800 dark:text-slate-100">
                      {t('grade.flow.modeSelection.batchPanelTitle')}
                    </div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">
                      {t('grade.flow.modeSelection.selectedCount', {
                        count: batchSelectedStudentIds.size,
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setBatchSelectedStudentIds(new Set(students.map((s) => s.id)))}
                      className="px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200 text-[10px] font-black uppercase tracking-widest border border-indigo-200/60"
                      disabled={students.length === 0}
                    >
                      {t('grade.flow.modeSelection.selectAll')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setBatchSelectedStudentIds(new Set())}
                      className="px-3 py-1.5 rounded-lg bg-white/60 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-[10px] font-black uppercase tracking-widest border border-slate-200/70"
                    >
                      {t('grade.flow.modeSelection.clear')}
                    </button>
                  </div>
                </div>

                <div className="max-h-44 overflow-y-auto pr-1 custom-scrollbar space-y-1">
                  {students.map((s) => {
                    const checked = batchSelectedStudentIds.has(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center justify-between gap-3 p-2 rounded-lg border transition-colors ${
                          checked
                            ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300/70'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <span className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate">
                          {s.name}
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setBatchSelectedStudentIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(s.id)) next.delete(s.id);
                              else next.add(s.id);
                              return next;
                            });
                          }}
                        />
                      </label>
                    );
                  })}
                  {students.length === 0 && (
                    <div className="text-slate-500 text-[12px] font-bold">
                      {t('grade.flow.modeSelection.noStudentsLoaded')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 mt-2">
            <button
              onClick={() => startGrading(GradingMode.SINGLE_PAGE)}
              disabled={scanStudentMode === 'batch' && batchSelectedStudentIds.size === 0}
              className={`p-5 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border rounded-xl flex items-center justify-between shadow-sm transition-all ${
                scanStudentMode === 'batch' && batchSelectedStudentIds.size === 0
                  ? 'border-slate-200/70 text-slate-400 cursor-not-allowed'
                  : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
                  <Camera className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <h3 className="text-base font-black text-slate-800 dark:text-slate-100">
                    {t('grade.flow.modeSelection.singlePageMode')}
                  </h3>
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">
                    {t('grade.flow.modeSelection.singlePageHint')}
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => startGrading(GradingMode.MULTI_PAGE)}
              disabled={scanStudentMode === 'batch' && batchSelectedStudentIds.size === 0}
              className={`p-5 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl border rounded-xl flex items-center justify-between shadow-sm transition-all ${
                scanStudentMode === 'batch' && batchSelectedStudentIds.size === 0
                  ? 'border-slate-200/70 text-slate-400 cursor-not-allowed'
                  : 'border-slate-200 dark:border-slate-700 hover:border-indigo-400'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <h3 className="text-base font-black text-slate-800 dark:text-slate-100">
                    {t('grade.flow.modeSelection.multiPageMode')}
                  </h3>
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-1">
                    {t('grade.flow.modeSelection.multiPageHint')}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );

  const getAttributionConfidence = (detectedNameRaw: string, expectedStudentName?: string) => {
    const detectedName = (detectedNameRaw || '').toLowerCase().replace(/[^a-z]/g, '');
    const expectedName = (expectedStudentName || '').toLowerCase().replace(/[^a-z]/g, '');
    if (!detectedName || detectedName.length <= 2) return 'low' as const;
    if (
      expectedName &&
      (expectedName.includes(detectedName) || detectedName.includes(expectedName))
    ) {
      return 'high' as const;
    }
    if (
      expectedName &&
      (expectedName.startsWith(detectedName.slice(0, 3)) ||
        detectedName.startsWith(expectedName.slice(0, 3)))
    ) {
      return 'medium' as const;
    }
    return 'low' as const;
  };

  const renderGradingLoop = () => {
    // Dynamic derived states for the Review & Match modal
    const selectedStudentId =
      selectedQuickPickIds.size > 0 ? Array.from(selectedQuickPickIds)[0] : null;
    const selectedStudentObj = students.find((s) => s.id === selectedStudentId);
    const displayStudentName = selectedStudentObj
      ? selectedStudentObj.name
      : pendingWork?.studentName || t('grade.gradingLoop.unknownStudent');
    const expectedBatchStudentId =
      scanStudentMode === 'batch' ? batchStudentOrderRef.current[batchNextIndexRef.current] : null;
    const expectedBatchStudent = expectedBatchStudentId
      ? students.find((s) => s.id === expectedBatchStudentId)
      : null;
    const quickMatchConfidence = getAttributionConfidence(
      pendingWork?.studentName || '',
      expectedBatchStudent?.name
    );
    const confidenceLabel =
      quickMatchConfidence === 'high'
        ? t('grade.gradingLoop.confidenceHigh')
        : quickMatchConfidence === 'medium'
          ? t('grade.gradingLoop.confidenceMedium')
          : t('grade.gradingLoop.confidenceLow');
    const avgGradeMs =
      scanSessionStats.gradedCount > 0
        ? Math.round(scanSessionStats.totalGradeMs / scanSessionStats.gradedCount)
        : 0;
    const topCandidateStudents = (() => {
      const source =
        scanStudentMode === 'batch' && batchSelectedStudentIds.size > 0
          ? students.filter((s) => batchSelectedStudentIds.has(s.id))
          : students;
      const lowerDetected = (pendingWork?.studentName || '').toLowerCase().replace(/[^a-z]/g, '');
      if (!lowerDetected || lowerDetected.length <= 2) return source.slice(0, 3);
      return [...source]
        .sort((a, b) => {
          const aName = a.name.toLowerCase().replace(/[^a-z]/g, '');
          const bName = b.name.toLowerCase().replace(/[^a-z]/g, '');
          const aMatch = aName.includes(lowerDetected) || lowerDetected.includes(aName);
          const bMatch = bName.includes(lowerDetected) || lowerDetected.includes(bName);
          if (aMatch && !bMatch) return -1;
          if (!aMatch && bMatch) return 1;
          return a.name.localeCompare(b.name);
        })
        .slice(0, 3);
    })();
    const quickPickStudentPool = (
      scanStudentMode === 'batch' && batchSelectedStudentIds.size > 0
        ? students.filter((s) => batchSelectedStudentIds.has(s.id))
        : students
    ).sort((a, b) => {
      if (!pendingWork?.studentName) return 0;
      const lowerDetected = pendingWork.studentName.toLowerCase().replace(/[^a-z]/g, '');
      if (lowerDetected.length <= 2) return 0;
      const aName = a.name.toLowerCase().replace(/[^a-z]/g, '');
      const bName = b.name.toLowerCase().replace(/[^a-z]/g, '');
      const aMatch = aName.includes(lowerDetected) || lowerDetected.includes(aName);
      const bMatch = bName.includes(lowerDetected) || lowerDetected.includes(bName);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return a.name.localeCompare(b.name);
    });
    const cycleQuickPickSelection = (direction: 1 | -1) => {
      if (quickPickStudentPool.length === 0) return;
      const selectedId = selectedQuickPickIds.size > 0 ? Array.from(selectedQuickPickIds)[0] : null;
      const currentIndex = selectedId
        ? quickPickStudentPool.findIndex((s) => s.id === selectedId)
        : -1;
      const nextIndex =
        currentIndex < 0
          ? 0
          : (currentIndex + direction + quickPickStudentPool.length) % quickPickStudentPool.length;
      const nextStudent = quickPickStudentPool[nextIndex];
      if (!nextStudent) return;
      setSelectedQuickPickIds(new Set([nextStudent.id]));
    };
    const guardrailBlocked =
      eliteFlags.qualityGuardrailMode &&
      quickMatchConfidence === 'low' &&
      !guardrailOverride &&
      selectedQuickPickIds.size > 0;
    const feedbackTemplateOptions = [
      'Great effort. Keep showing your work for full credit.',
      'Strong understanding. Double-check unit labels next time.',
      'Good approach. Revisit the final step for full accuracy.',
    ];

    return (
      <PageWrapper
        headerTitle={
          gradingMode === GradingMode.MULTI_PAGE
            ? t('grade.flow.headers.multiPageMode')
            : t('grade.flow.headers.singlePageMode')
        }
        headerSubtitle={
          selectedCourse && selectedAssignment
            ? t('grade.flow.headers.courseAssignmentContext', {
                course: selectedCourse.name,
                assignment: selectedAssignment.title,
              })
            : undefined
        }
        onBack={() => setPhase(AppPhase.MODE_SELECTION)}
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        onSyncClick={retryClassroomSync}
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 px-2 pb-1 sm:px-3">
            <GradePipelineSteps activeIndex={1} className="py-2" />
          </div>
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border border-white/20 bg-black shadow-2xl">
            <style>{`
              @keyframes scanSweepVertical {
                0% { top: 0%; opacity: 0; }
                10% { opacity: 1; }
                90% { opacity: 1; }
                100% { top: 100%; opacity: 0; }
              }
              .animate-scan-sweep-vertical {
                animation: scanSweepVertical 3s ease-in-out infinite alternate;
              }
            `}</style>

            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />

            {/* ASPECT RATIO BOUNDING BOX (Matches Letter 8.5x11 and A4 perfectly on mobile) */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6 pb-3">
              <div
                className={`relative w-full max-w-[min(100%,22rem)] sm:max-w-md md:max-w-lg aspect-[3/4] border-[3px] border-dashed transition-all duration-500 rounded-3xl z-10 flex flex-col items-center justify-center overflow-hidden pointer-events-none ${isProcessing ? 'border-emerald-400 bg-emerald-400/20' : 'border-white/60 bg-black/10'}`}
              >
                {!isProcessing && (
                  <div className="absolute left-0 w-full h-1 bg-emerald-400 shadow-[0_0_20px_4px_rgba(52,211,153,0.8)] animate-scan-sweep-vertical" />
                )}
                <span className="bg-black/70 text-white font-black uppercase tracking-widest text-[10px] px-5 py-2.5 rounded-full backdrop-blur-md shadow-lg">
                  {isProcessing
                    ? t('grade.gradingLoop.processing')
                    : t('grade.gradingLoop.alignDocument')}
                </span>

                {gradingMode === GradingMode.MULTI_PAGE && (
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
                    <div className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[9px] font-black uppercase tracking-widest">
                      {multiPageCapture.croppedDataUrls.length === 1
                        ? t('grade.gradingLoop.multiPageOne', {
                            count: multiPageCapture.croppedDataUrls.length,
                          })
                        : t('grade.gradingLoop.multiPageOther', {
                            count: multiPageCapture.croppedDataUrls.length,
                          })}
                    </div>
                    {multiPageHint && (
                      <div className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[9px] font-semibold truncate">
                        {multiPageHint}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="absolute top-4 left-4 z-40 flex flex-col gap-2 pointer-events-auto">
              <div className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[9px] font-black uppercase tracking-widest">
                {t('grade.gradingLoop.healthLabel', {
                  pct: Math.max(0, Math.min(100, Math.round(scanHealth))),
                })}
              </div>
              <div className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[9px] font-black uppercase tracking-widest">
                {t('grade.gradingLoop.queueLabel', { count: scanQueueCount })}
              </div>
              {scanReviewQueueCount > 0 && (
                <button
                  type="button"
                  onClick={openNextQueuedReview}
                  className="px-3 py-1.5 rounded-full bg-emerald-500/25 hover:bg-emerald-500/35 backdrop-blur-md border border-emerald-400/40 text-emerald-100 text-[9px] font-black uppercase tracking-widest transition-all"
                  title={t('grade.gradingLoop.reviewNextTitle')}
                >
                  {t('grade.gradingLoop.readyQueue', { count: scanReviewQueueCount })}
                </button>
              )}
              {scanQueueHint && (
                <div className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[9px] font-black uppercase tracking-widest max-w-[220px] truncate">
                  {scanQueueHint}
                </div>
              )}
              {!isOnline && scanQueueCount > 0 && (
                <div className="px-3 py-2 rounded-xl bg-amber-500/20 backdrop-blur-md border border-amber-400/30 text-amber-100 text-[10px] font-semibold shadow-lg max-w-[220px]">
                  {t('grade.gradingLoop.offlineQueued')}
                </div>
              )}
              {eliteFlags.errorRecoveryCard &&
                (cameraError || (!isOnline && scanQueueCount > 0)) && (
                  <div className="px-3 py-2 rounded-xl bg-rose-500/20 backdrop-blur-md border border-rose-400/30 text-rose-100 text-[10px] font-semibold shadow-lg max-w-[220px]">
                    {t('grade.gradingLoop.recoveryMode', {
                      detail: cameraError
                        ? t('grade.gradingLoop.recoveryCamera')
                        : t('grade.gradingLoop.recoveryOffline'),
                    })}
                  </div>
                )}
              {scanQueueCount > 1 && avgGradeMs > 0 && (
                <div className="px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[9px] font-black uppercase tracking-widest">
                  {t('grade.gradingLoop.eta', {
                    seconds: Math.max(1, Math.round((avgGradeMs * scanQueueCount) / 1000)),
                  })}
                </div>
              )}
            </div>

            {scanStudentMode === 'batch' && expectedBatchStudent && !showQuickPick && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
                <div className="px-4 py-2 rounded-full bg-black/70 backdrop-blur-md border border-white/20 text-white text-[10px] font-black uppercase tracking-widest shadow-lg max-w-[320px] truncate">
                  {t('grade.gradingLoop.nextStudent', { name: expectedBatchStudent.name })}
                </div>
              </div>
            )}

            <div className="relative z-40 flex items-center justify-center gap-6 mb-6 mt-auto pb-4">
              <div className="bg-black/50 backdrop-blur-xl p-2 rounded-full flex gap-3 items-center shadow-lg border border-white/20">
                <button
                  onClick={toggleFlash}
                  className={`p-4 rounded-full border-2 transition-all ${isFlashOn ? 'bg-yellow-400 text-black border-transparent shadow-[0_0_20px_rgba(250,204,21,0.6)]' : 'bg-white/10 text-white border-white/30 hover:bg-white/20'}`}
                  title={t('grade.gradingLoop.toggleFlash')}
                >
                  <Zap className="w-6 h-6" />
                </button>

                {gradingMode === GradingMode.SINGLE_PAGE && (
                  <button
                    type="button"
                    onClick={() => void handleManualCaptureSinglePage()}
                    className="w-16 h-16 rounded-full border-[4px] border-emerald-400 bg-emerald-500/20 hover:bg-emerald-500/30 active:scale-95 flex items-center justify-center transition-all shadow-[0_0_30px_rgba(52,211,153,0.25)] backdrop-blur-md"
                    title={t('grade.gradingLoop.captureNow')}
                  >
                    <Camera className="w-7 h-7 text-emerald-200" />
                  </button>
                )}

                {gradingMode === GradingMode.MULTI_PAGE && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setMultiPageCapture({
                          croppedDataUrls: [],
                          apiBase64s: [],
                          detectedStudentName: undefined,
                        })
                      }
                      className="px-4 py-3 rounded-full bg-white/10 text-white border-2 border-white/30 hover:bg-white/20 transition-all text-[10px] font-black uppercase tracking-widest"
                      title={t('grade.gradingLoop.resetPages')}
                    >
                      {t('grade.gradingLoop.resetShort')}
                    </button>
                    <button
                      type="button"
                      disabled={multiPageCapture.apiBase64s.length === 0 || isProcessing}
                      onClick={() => void finalizeMultiPage()}
                      className={`px-4 py-3 rounded-full border-2 transition-all text-[10px] font-black uppercase tracking-widest ${
                        multiPageCapture.apiBase64s.length === 0 || isProcessing
                          ? 'bg-white/5 text-white/40 border-white/10'
                          : 'bg-emerald-500/20 text-emerald-200 border-emerald-400/60 hover:bg-emerald-500/30'
                      }`}
                      title={t('grade.gradingLoop.finishGradeTitle')}
                    >
                      {t('grade.gradingLoop.finishGrade')}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setShowCameraTray((v) => !v)}
                  className="px-4 py-3 rounded-full bg-white/10 text-white border-2 border-white/30 hover:bg-white/20 transition-all text-[10px] font-black uppercase tracking-widest"
                  title="More scan actions"
                >
                  {showCameraTray ? 'Hide tools' : 'More'}
                </button>
              </div>
            </div>

            {showCameraTray && (
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 w-[min(92vw,360px)] rounded-2xl bg-black/70 backdrop-blur-xl border border-white/20 p-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={openNextQueuedReview}
                    disabled={scanReviewQueueCount === 0}
                    className="py-2 rounded-xl bg-white/10 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40"
                  >
                    {t('grade.gradingLoop.openReady')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhase(AppPhase.AUDIT)}
                    disabled={gradedWorks.length === 0}
                    className="py-2 rounded-xl bg-white/10 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40"
                  >
                    {t('grade.gradingLoop.openAudit')}
                  </button>
                </div>
              </div>
            )}

            {showQuickPick && pendingWork && (
              <div className="absolute inset-0 z-50 bg-slate-900/95 backdrop-blur-2xl flex flex-col p-4 animate-in fade-in zoom-in duration-300">
                <div className="flex-1 w-full min-w-0 max-w-full sm:max-w-md md:max-w-lg lg:max-w-xl mx-auto bg-white dark:bg-slate-900 rounded-none sm:rounded-3xl p-4 sm:p-5 shadow-2xl border-0 sm:border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden">
                  <h3 className="font-black text-slate-800 dark:text-slate-100 text-center mb-4 text-[16px] uppercase tracking-widest border-b border-slate-100 dark:border-slate-700 pb-4">
                    Review & Match
                  </h3>
                  <div
                    className={`mb-3 rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest ${
                      quickMatchConfidence === 'high'
                        ? 'border-emerald-300/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                        : quickMatchConfidence === 'medium'
                          ? 'border-amber-300/50 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                          : 'border-rose-300/50 bg-rose-500/10 text-rose-700 dark:text-rose-300'
                    }`}
                  >
                    {confidenceLabel}
                  </div>
                  {eliteFlags.qualityGuardrailMode && quickMatchConfidence === 'low' && (
                    <div className="mb-3 rounded-xl border border-amber-300/50 bg-amber-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300">
                      {t('grade.gradingLoop.guardrailBanner')}
                      <button
                        type="button"
                        onClick={() =>
                          setGuardrailOverride((v) => {
                            const next = !v;
                            trackEliteEvent('guardrail_override_toggle', { enabled: next });
                            return next;
                          })
                        }
                        className="ml-2 rounded-lg border border-amber-400/60 px-2 py-1 text-[9px]"
                      >
                        {guardrailOverride
                          ? t('grade.gradingLoop.guardrailOverrideOn')
                          : t('grade.gradingLoop.guardrailAllowOnce')}
                      </button>
                    </div>
                  )}
                  {topCandidateStudents.length > 0 && (
                    <div className="mb-3 grid grid-cols-3 gap-2">
                      {topCandidateStudents.map((candidate) => (
                        <button
                          key={candidate.id}
                          type="button"
                          onClick={() => setSelectedQuickPickIds(new Set([candidate.id]))}
                          className="rounded-xl bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200/70 dark:border-indigo-700/60 px-2 py-2 text-[10px] font-black text-indigo-700 dark:text-indigo-200 truncate"
                        >
                          {candidate.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {eliteFlags.swipeQuickMatch && (
                    <div className="mb-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => cycleQuickPickSelection(-1)}
                        className="rounded-xl border border-slate-200/70 dark:border-slate-700/60 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-200"
                      >
                        {t('grade.gradingLoop.prevMatch')}
                      </button>
                      <button
                        type="button"
                        onClick={() => cycleQuickPickSelection(1)}
                        className="rounded-xl border border-slate-200/70 dark:border-slate-700/60 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-700 dark:text-slate-200"
                      >
                        {t('grade.gradingLoop.nextMatch')}
                      </button>
                    </div>
                  )}

                  {scanStudentMode === 'batch' &&
                    expectedBatchStudent &&
                    (() => {
                      const candidateStudents =
                        batchSelectedStudentIds.size > 0
                          ? students.filter((s) => batchSelectedStudentIds.has(s.id))
                          : students;
                      const detectedName = (pendingWork.studentName || '')
                        .toLowerCase()
                        .replace(/[^a-z]/g, '');
                      const detectedMatch =
                        detectedName.length > 2
                          ? candidateStudents.find((s) => {
                              const sName = s.name.toLowerCase().replace(/[^a-z]/g, '');
                              return sName.includes(detectedName) || detectedName.includes(sName);
                            })
                          : null;
                      const detectedId = detectedMatch?.id || null;
                      const conflict = !!detectedId && detectedId !== expectedBatchStudent.id;
                      if (!conflict) return null;
                      return (
                        <div className="mb-4 p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30">
                          <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-200">
                            {t('grade.gradingLoop.resolveTitle')}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                            {t('grade.gradingLoop.resolveBody', {
                              detected: detectedMatch?.name ?? '',
                              expected: expectedBatchStudent.name,
                            })}
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedQuickPickIds(new Set([expectedBatchStudent.id]))
                              }
                              className="py-2.5 rounded-xl bg-emerald-500 text-white font-black uppercase tracking-widest text-[10px]"
                            >
                              {t('grade.gradingLoop.useExpected')}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                detectedId && setSelectedQuickPickIds(new Set([detectedId]))
                              }
                              className="py-2.5 rounded-xl bg-indigo-600 text-white font-black uppercase tracking-widest text-[10px]"
                            >
                              {t('grade.gradingLoop.useDetected')}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                  {/* 1st Line: Student Name & Score */}
                  <div className="flex gap-4 mb-4 shrink-0 h-20">
                    <div className="flex flex-col flex-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 text-center w-full block">
                        {t('grade.gradingLoop.studentLabel')}
                      </label>
                      <div className="flex-1 flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-500/30 rounded-xl px-4 shadow-inner overflow-hidden">
                        <span className="text-emerald-600 dark:text-emerald-400 font-black text-lg text-center leading-tight truncate w-full">
                          {displayStudentName}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col w-28 shrink-0">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 text-center w-full block">
                        {t('grade.gradingLoop.scoreLabel')}
                      </label>
                      <input
                        type="number"
                        value={manualScore}
                        onChange={(e) => setManualScore(e.target.value)}
                        className="flex-1 w-full text-center font-black text-3xl text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl outline-none border border-indigo-100 dark:border-indigo-500/30 shadow-inner"
                      />
                    </div>
                  </div>

                  {/* 2nd Line: Feedback (Larger & Scrollable) + Voice Button */}
                  <div className="flex flex-col mb-4 shrink-0">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1 text-center w-full block">
                      {t('grade.gradingLoop.feedbackLabel')}
                    </label>
                    <div className="relative">
                      <textarea
                        value={manualFeedback}
                        onChange={(e) => setManualFeedback(e.target.value)}
                        rows={5}
                        className="w-full p-4 pr-14 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none resize-none text-[16px] text-slate-700 dark:text-slate-300 overflow-y-auto custom-scrollbar shadow-inner"
                        placeholder={t('grade.gradingLoop.feedbackPlaceholder')}
                      />
                      <VoiceInputButton
                        onResult={(text) =>
                          setManualFeedback((prev) => prev + (prev ? ' ' : '') + text)
                        }
                        className="absolute bottom-3 right-3 bg-white dark:bg-slate-700 shadow-md border-slate-200 dark:border-slate-600"
                      />
                    </div>
                    {eliteFlags.feedbackTemplates && (
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {feedbackTemplateOptions.map((template) => (
                          <button
                            key={template}
                            type="button"
                            onClick={() =>
                              setManualFeedback((prev) =>
                                prev ? `${prev}\n${template}` : template
                              )
                            }
                            className="rounded-lg border border-indigo-200/70 dark:border-indigo-700/60 bg-indigo-50 dark:bg-indigo-900/25 px-2 py-1.5 text-left text-[10px] font-semibold text-indigo-700 dark:text-indigo-200"
                          >
                            {template}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Student List */}
                  <div className="flex flex-col flex-1 min-h-0">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 text-center w-full block">
                      {t('grade.gradingLoop.selectStudentLabel')}
                    </label>
                    <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-2 space-y-1 shadow-inner">
                      {/* Dynamic Listing: Matches are always pushed to the top */}
                      {quickPickStudentPool.map((student) => (
                        <div
                          key={student.id}
                          onClick={() => setSelectedQuickPickIds(new Set([student.id]))}
                          className={`p-3 rounded-xl flex items-center justify-between cursor-pointer transition-all border ${selectedQuickPickIds.has(student.id) ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400 dark:border-indigo-500 shadow-sm' : 'bg-white dark:bg-slate-800 border-transparent hover:border-slate-300 dark:hover:border-slate-600'}`}
                        >
                          <span
                            className={`font-bold text-[16px] ${selectedQuickPickIds.has(student.id) ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-700 dark:text-slate-300'}`}
                          >
                            {student.name}
                          </span>
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selectedQuickPickIds.has(student.id) ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300 dark:border-slate-600'}`}
                          >
                            {selectedQuickPickIds.has(student.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4 shrink-0">
                    <button
                      onClick={() => {
                        trackEliteEvent('quick_match_cancel', {
                          selectedCount: selectedQuickPickIds.size,
                        });
                        setShowQuickPick(false);
                        setPendingWork(null);
                        setGuardrailOverride(false);
                      }}
                      className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 rounded-xl font-black text-slate-600 dark:text-slate-300 uppercase text-[11px] tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shadow-sm"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      title={
                        guardrailBlocked ? t('grade.gradingLoop.guardrailBlockedTitle') : undefined
                      }
                      onClick={confirmQuickPickStudents}
                      disabled={selectedQuickPickIds.size === 0 || guardrailBlocked}
                      className="flex-1 py-4 bg-emerald-500 text-white rounded-xl font-black uppercase text-[11px] tracking-widest disabled:opacity-50 hover:bg-emerald-600 transition-colors shadow-md"
                    >
                      {scanStudentMode === 'batch' && selectedStudentObj
                        ? t('grade.gradingLoop.applyToStudent', { name: selectedStudentObj.name })
                        : t('grade.gradingLoop.done')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {gradedWorks.length > 0 && (
            <div className="mt-4 flex w-full shrink-0 gap-2">
              <button
                type="button"
                onClick={() => setPhase(AppPhase.AUDIT)}
                className="min-w-0 flex-1 py-4 bg-indigo-600 text-white rounded-xl font-black uppercase tracking-widest text-[16px] shadow-sm hover:-translate-y-0.5 transition-all"
              >
                {t('grade.gradingLoop.reviewScans', { count: gradedWorks.length })}{' '}
                <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={undoLastPendingGrade}
                disabled={gradedWorks[gradedWorks.length - 1]?.status === 'synced'}
                title={t('ux.undoLastGradeHint')}
                className="shrink-0 px-4 py-4 rounded-xl border-2 border-white/30 bg-white/10 text-white font-black uppercase tracking-widest text-[11px] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/15 transition-colors"
              >
                {t('ux.undoLastGrade')}
              </button>
            </div>
          )}
          {eliteFlags.sessionSnapshot &&
            (scanSessionStats.captured > 0 || scanSessionStats.manualCorrections > 0) && (
              <div className="mt-2 rounded-xl border border-slate-200/70 bg-white/75 dark:bg-slate-900/60 dark:border-slate-700 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300">
                {t('grade.gradingLoop.sessionLine', {
                  captured: scanSessionStats.captured,
                  corrections: scanSessionStats.manualCorrections,
                  avgSuffix:
                    avgGradeMs > 0
                      ? t('grade.gradingLoop.sessionAvgSuffix', {
                          seconds: Math.max(1, Math.round(avgGradeMs / 1000)),
                        })
                      : '',
                })}
              </div>
            )}
          {scanLastDeleted && (
            <div className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-amber-300/50 bg-amber-500/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-200">
              <span>{t('grade.gradingLoop.scanRemoved')}</span>
              <button
                type="button"
                onClick={() => {
                  setGradedWorks((prev) => {
                    const next = [...prev];
                    const insertAt = Math.max(0, Math.min(scanLastDeleted.index, next.length));
                    next.splice(insertAt, 0, scanLastDeleted.work);
                    return next;
                  });
                  setScanLastDeleted(null);
                }}
                className="px-2 py-1 rounded-lg border border-amber-400/50"
              >
                {t('grade.gradingLoop.undoScan')}
              </button>
            </div>
          )}
        </div>
      </PageWrapper>
    );
  };

  const renderAudit = () => {
    const auditFeedbackTemplates = [
      'Clear explanation.',
      'Recheck calculations.',
      'Excellent work.',
    ];

    const visibleForBulk = auditVisibleFiltered;
    const selectedCount = auditSelectedIndexes.size;
    const effectiveSelection =
      selectedCount > 0 ? Array.from(auditSelectedIndexes) : visibleForBulk.map((v) => v.idx);

    const selectAuditRow = (idx: number) => {
      setAuditStudioFocusIdx(idx);
    };

    const onToggleBulk = (idx: number) => {
      setAuditSelectedIndexes((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return next;
      });
    };

    const saveFromStudio = () => {
      if (auditResolvedFocus === null) return;
      onAuditAutosaveCommit(auditResolvedFocus, auditDraftScore, auditDraftFeedback);
      if (eliteFlags.hapticsAudio && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(10);
      }
    };

    const saveAndNextFromStudio = () => {
      saveFromStudio();
      const order = auditVisibleFiltered.map((r) => r.idx);
      const i = order.indexOf(auditResolvedFocus ?? -1);
      const next = order[i + 1];
      if (next !== undefined) setAuditStudioFocusIdx(next);
    };

    const auditBulkFooterSlot = (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-dg-label font-medium uppercase tracking-wide text-slate-500">
              {t('audit.toolbar.queueControl')}
            </div>
            <div className="mt-1 truncate text-dg-body font-semibold text-slate-900 dark:text-white">
              {selectedCount > 0
                ? t('audit.toolbar.selectedCount', { count: selectedCount })
                : t('audit.toolbar.shownCount', { count: visibleForBulk.length })}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={undoLastPendingGrade}
              disabled={
                gradedWorks.length === 0 || gradedWorks[gradedWorks.length - 1]?.status === 'synced'
              }
              title={t('ux.undoLastGradeHint')}
              className="rounded-dg-control border border-slate-200/70 bg-white px-3 py-2 text-dg-label font-semibold uppercase tracking-wide text-slate-600 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200"
            >
              {t('ux.undoLastGrade')}
            </button>
            <button
              type="button"
              onClick={() => setAuditSelectedIndexes(new Set(visibleForBulk.map((v) => v.idx)))}
              disabled={visibleForBulk.length === 0}
              className="rounded-dg-control border border-slate-200/70 bg-white px-3 py-2 text-dg-label font-semibold uppercase tracking-wide text-slate-600 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200"
            >
              {t('audit.toolbar.selectAll')}
            </button>
            <button
              type="button"
              onClick={() => setAuditSelectedIndexes(new Set())}
              disabled={selectedCount === 0}
              className="rounded-dg-control border border-slate-200/70 bg-white px-3 py-2 text-dg-label font-semibold uppercase tracking-wide text-slate-600 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200"
            >
              {t('audit.toolbar.deselect')}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => {
              if (effectiveSelection.length === 0) return;
              trackEliteEvent('audit_bulk_delete', { count: effectiveSelection.length });
              setGradedWorks((prev) => prev.filter((_, i) => !new Set(effectiveSelection).has(i)));
              setAuditSelectedIndexes(new Set());
            }}
            disabled={effectiveSelection.length === 0}
            className="rounded-dg-control border border-red-200/60 bg-red-50 py-2.5 text-dg-label font-semibold uppercase tracking-wide text-red-600 disabled:opacity-50 dark:border-red-500/20 dark:bg-red-900/30 dark:text-red-300"
          >
            {t('audit.toolbar.bulkDelete')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (effectiveSelection.length === 0) return;
              trackEliteEvent('audit_bulk_mark_draft', { count: effectiveSelection.length });
              const sel = new Set(effectiveSelection);
              setGradedWorks((prev) =>
                prev.map((w, i) => (sel.has(i) ? { ...w, status: 'draft' as const } : w))
              );
            }}
            disabled={effectiveSelection.length === 0}
            className="rounded-dg-control border border-slate-200/70 bg-white py-2.5 text-dg-label font-semibold uppercase tracking-wide text-slate-700 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-200"
          >
            {t('audit.toolbar.markDraft')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (effectiveSelection.length === 0) return;
              trackEliteEvent('audit_bulk_sync', { count: effectiveSelection.length });
              setSyncPreflightIndexes(effectiveSelection);
              setAuditSelectedIndexes(new Set());
              setPhase(AppPhase.GRADE_SYNC_PREFLIGHT);
            }}
            disabled={effectiveSelection.length === 0 || !isOnline}
            title={!isOnline ? t('offline.banner') : undefined}
            className="rounded-dg-control border border-emerald-500 bg-emerald-500 py-2.5 text-dg-label font-semibold uppercase tracking-wide text-white disabled:opacity-50"
          >
            {t('audit.toolbar.syncSelected')}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            const indexes =
              auditSelectedIndexes.size > 0
                ? Array.from(auditSelectedIndexes).sort((a, b) => a - b)
                : gradedWorks.map((_, i) => i);
            const subset = indexes.map((i) => gradedWorks[i]).filter(Boolean);
            if (subset.length === 0) return;
            const html = buildFeedbackBatchExportHtml(subset, {
              title: t('audit.exportTitle'),
              subtitle: [selectedCourse?.name, selectedAssignment?.title]
                .filter(Boolean)
                .join(' — '),
              generatedAtMs: Date.now(),
            });
            const ok = openFeedbackBatchPrintWindow(html);
            logEvent('feedback_batch_export', { count: subset.length });
            if (!ok && typeof window !== 'undefined') {
              window.alert(t('audit.exportPopupBlocked'));
            }
          }}
          disabled={gradedWorks.length === 0}
          title={t('audit.exportHint')}
          className="w-full rounded-dg-control border border-slate-300/80 bg-white/80 py-2.5 text-dg-label font-semibold uppercase tracking-wide text-slate-700 hover:bg-white disabled:opacity-50 dark:border-slate-600/80 dark:bg-slate-900/50 dark:text-slate-200 dark:hover:bg-slate-900"
        >
          {t('audit.exportPrintPdf')}
        </button>
      </div>
    );

    return (
      <PageWrapper
        headerTitle={t('grade.flow.headers.reviewQueue')}
        headerSubtitle={t('grade.flow.headers.pendingSubtitle', {
          count: gradedWorks.length,
        })}
        onBack={() => setPhase(AppPhase.GRADING_LOOP)}
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        onSyncClick={retryClassroomSync}
      >
        <GradeFlowShell
          step={t('grade.flow.auditQueue.shellStep')}
          title={t('grade.flow.auditQueue.shellTitle')}
          subtitle={t('grade.flow.auditQueue.shellSubtitle')}
          bodyScrollable={false}
        >
          <GradeStepHeader
            label={t('grade.flow.auditQueue.stepLabel')}
            title={t('grade.flow.auditQueue.stepTitle')}
            courseName={selectedCourse?.name}
            assignmentTitle={selectedAssignment?.title}
          />
          <GradePipelineSteps activeIndex={2} className="mb-1" />
          <AuditDraftHydrator
            resolvedFocus={auditResolvedFocus}
            gradedWorks={gradedWorks}
            setAuditEditIndex={setAuditEditIndex}
            setAuditDraftScore={setAuditDraftScore}
            setAuditDraftFeedback={setAuditDraftFeedback}
          />
          <GradeSessionRestore
            courseId={selectedCourse?.id}
            assignmentId={selectedAssignment?.id}
            visibleIndexes={auditVisibleFiltered.map((r) => r.idx)}
            hasFocus={auditResolvedFocus !== null}
            onRestore={({ focusIndex, draftScore, draftFeedback }) => {
              setAuditStudioFocusIdx(focusIndex);
              setAuditDraftScore(draftScore);
              setAuditDraftFeedback(draftFeedback);
            }}
          />
          <GradeReviewStudio
            courseId={selectedCourse?.id}
            assignmentId={selectedAssignment?.id}
            courseName={selectedCourse?.name}
            assignmentTitle={selectedAssignment?.title}
            isOnline={isOnline}
            rubricText={selectedAssignment?.rubric}
            commentTemplates={eliteFlags.feedbackTemplates ? auditFeedbackTemplates : []}
            visibleRows={auditVisibleFiltered}
            resolvedFocus={auditResolvedFocus}
            auditSelectedIndexes={auditSelectedIndexes}
            onToggleBulk={onToggleBulk}
            onSelectRow={selectAuditRow}
            auditFilter={auditFilter}
            onAuditFilter={setAuditFilter}
            searchQuery={auditQueueSearch}
            onSearchQuery={setAuditQueueSearch}
            stats={auditStatsBundle}
            draftScore={auditDraftScore}
            onDraftScoreChange={setAuditDraftScore}
            draftFeedback={auditDraftFeedback}
            onDraftFeedbackChange={setAuditDraftFeedback}
            onSaveDraft={saveFromStudio}
            onSaveAndNext={saveAndNextFromStudio}
            onFinalize={saveFromStudio}
            onRescan={handleRescan}
            onDelete={handleDeleteScan}
            onAutosaveCommit={onAuditAutosaveCommit}
            bulkFooterSlot={auditBulkFooterSlot}
            emptyQueueMessage={t('audit.studio.emptyFilter')}
          />
        </GradeFlowShell>
      </PageWrapper>
    );
  };

  const renderSyncing = () => (
    <GradeSyncingView
      courseNameHeader={selectedCourse?.name ?? undefined}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
      syncProgress={syncProgress}
    />
  );

  const renderFinale = () => (
    <GradeFinaleView
      courseNameHeader={selectedCourse?.name ?? undefined}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
      syncReceipts={syncReceipts}
      gradeSyncPostToClassroom={gradeSyncPostToClassroom}
      gradeSyncEmailStudents={gradeSyncEmailStudents}
      gradeSyncSaveToDrive={gradeSyncSaveToDrive}
      onBackToGrade={() => setPhase(AppPhase.DASHBOARD)}
    />
  );

  const renderPaywall = () => (
    <PageWrapper
      headerTitle={t('grade.flow.headers.upgradeToContinue')}
      headerSubtitle={undefined}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={retryClassroomSync}
    >
      <div className="flex flex-col h-full w-full min-w-0 max-w-full md:max-w-xl lg:max-w-2xl mx-auto px-3 sm:px-5">
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <h2 className="text-xl font-black text-slate-900 dark:text-slate-50 text-center">
            {t('grade.flow.paywall.heroTitle')}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 text-center">
            {t('grade.flow.paywall.heroBody')}
          </p>
          <div className="w-full bg-white/70 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-700 rounded-xl p-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
            <p className="font-semibold">{t('grade.flow.paywall.includesTitle')}</p>
            <ul className="list-disc list-inside space-y-1 text-[13px]">
              <li>{t('grade.flow.paywall.bullet1')}</li>
              <li>{t('grade.flow.paywall.bullet2')}</li>
              <li>{t('grade.flow.paywall.bullet3')}</li>
              <li>{t('grade.flow.paywall.bullet4')}</li>
            </ul>
          </div>
          {paywallCheckoutError ? (
            <p className="w-full text-sm text-red-600 dark:text-red-400 text-center" role="alert">
              {paywallCheckoutError}
            </p>
          ) : null}
          <button
            type="button"
            disabled={paywallCheckoutLoading}
            onClick={() => {
              void (async () => {
                setPaywallCheckoutError(null);
                setPaywallCheckoutLoading(true);
                try {
                  const checkoutEmail = (firebaseUser?.email || email || '').trim();
                  const r = await fetch(`${billingApiBase}/api/billing/create-checkout-session`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(checkoutEmail ? { email: checkoutEmail } : {}),
                  });
                  const data = (await r.json()) as {
                    url?: string;
                    error?: string;
                    code?: string;
                  };
                  if (!r.ok) {
                    if (data.code === 'BILLING_DISABLED') {
                      setPaywallCheckoutError(t('grade.flow.paywall.billingDisabled'));
                    } else {
                      setPaywallCheckoutError(data.error || t('grade.flow.paywall.checkoutFailed'));
                    }
                    return;
                  }
                  if (data.url) {
                    logEvent('subscription_start', { surface: 'paywall' });
                    window.location.href = data.url;
                    return;
                  }
                  setPaywallCheckoutError(t('grade.flow.paywall.checkoutNoUrl'));
                } catch {
                  setPaywallCheckoutError(t('grade.flow.paywall.checkoutNetwork'));
                } finally {
                  setPaywallCheckoutLoading(false);
                }
              })();
            }}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:pointer-events-none text-white rounded-xl font-black uppercase tracking-[0.12em] text-[14px] shadow-sm hover:-translate-y-0.5 transition-all inline-flex items-center justify-center gap-2"
          >
            {paywallCheckoutLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                <span>{t('grade.flow.paywall.openingCheckout')}</span>
              </>
            ) : (
              t('grade.flow.paywall.startTrial')
            )}
          </button>
          <button
            type="button"
            onClick={() => setPhase(AppPhase.DASHBOARD)}
            className="w-full py-3 bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-xl text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-white/90 dark:hover:bg-slate-800/90 transition-colors"
          >
            {t('grade.flow.paywall.maybeLater')}
          </button>
          <p className="mt-2 text-[10px] text-slate-500 dark:text-slate-400 text-center">
            You can start your trial anytime from the Sync screen. We never sell your data and you
            can cancel anytime.
          </p>
        </div>
      </div>
    </PageWrapper>
  );

  const renderPlan = () => (
    <Suspense fallback={<PhaseLazyFallback />}>
      <LessonArchitectPlanScreenLazy
        classroom={classroom}
        accessToken={accessToken}
        effectiveGoogleClientId={effectiveGoogleClientId}
        completeGoogleSignIn={completeGoogleSignIn}
        classroomTokenHooks={classroomTokenHooks}
        jurisdictionOptions={PLAN_JURISDICTION_OPTIONS}
      />
    </Suspense>
  );

  const renderTeachDesk = () => (
    <Suspense fallback={<PhaseLazyFallback />}>
      <TeachDeskLazy
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        educatorName={educatorName}
        todayLabel={todayLabel}
        onOpenPresenter={() => setPhase(AppPhase.CLASS_PRESENTER)}
        onOpenCommunicate={() => {
          if (!isOnline) return;
          setPhase(AppPhase.RECORDS);
        }}
        courses={courses}
        classroom={classroom}
        rosterLoadingCourseId={teachRosterLoadingCourseId}
        rosterError={teachRosterError}
        rosterCourseStudents={teachRosterCourseStudents}
        rosterCourseAssignments={teachRosterCourseAssignments}
        rosterCourseOpenId={teachRosterCourseOpenId}
        setRosterCourseOpenId={setTeachRosterCourseOpenId}
        rosterSelectedAssignmentId={teachRosterSelectedAssignmentId}
        setRosterSelectedAssignmentId={setTeachRosterSelectedAssignmentId}
        rosterSelectedStudentIds={teachRosterSelectedStudentIds}
        setRosterSelectedStudentIds={setTeachRosterSelectedStudentIds}
        ensureRosterData={ensureTeachRosterData}
        onCohortSelected={onTeachCohortSelected}
        accessToken={accessToken}
        timer={teachTimer}
        cohort={teachCohort}
        lastRandomPick={teachLastRandomPick}
        onTimerStart={onTeachTimerStart}
        onTimerPause={onTeachTimerPause}
        onTimerResume={onTeachTimerResume}
        onTimerReset={onTeachTimerReset}
        onPickRandomStudent={onPickTeachRandomStudent}
      />
    </Suspense>
  );

  const renderTeachPresenter = () => (
    <Suspense fallback={<PhaseLazyFallback />}>
      <TeachPresenterLazy
        isOnline={isOnline}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        syncStatus={syncStatus}
        educatorName={educatorName}
        todayLabel={todayLabel}
        onExitPresenter={() => setPhase(AppPhase.CLASSROOM)}
        timer={teachTimer}
        cohort={teachCohort}
        lastRandomPick={teachLastRandomPick}
      />
    </Suspense>
  );

  if (!DEMO_GRADE_ONLY && firebaseAuthLoading) {
    return (
      <AuthLoadingScreen
        label={firebaseRedirectResolving ? 'Finishing Google sign-in' : 'Loading DoneGrading'}
      />
    );
  }

  if (showOnboarding) {
    return (
      <div
        id="app-shell"
        className="dg-wave-bg dg-viewport-full min-h-0 w-full min-w-0 h-dvh font-sans overflow-hidden relative flex flex-col text-dg-fg-primary dark:text-slate-100"
      >
        <PageWrapper
          isOnline={isOnline}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          syncStatus={syncStatus}
          onSyncClick={retryClassroomSync}
        >
          <Suspense fallback={<PhaseLazyFallback />}>
            <OnboardingLazy
              onComplete={() => setShowOnboarding(false)}
              onSkip={() => setShowOnboarding(false)}
            />
          </Suspense>
        </PageWrapper>
      </div>
    );
  }

  const showAppShell = isSignedIn && phase !== AppPhase.CLASS_PRESENTER && !hideNonGradeHubPhases;

  // App bottom nav: visible across the app-shell screens AND the grading
  // workflow, except on the live camera / full-screen crop capture surface.
  const appBottomNavVisible = shouldShowAppBottomNav({
    appShellEnabled: APP_SHELL_ENABLED,
    isSignedIn,
    phase,
    scanStep: scanCaptureState.scanStep,
    cropEditorOpen: scanCaptureState.cropEditorOpen,
  });

  // Bottom-nav taps. While grading with a live session, confirm first so
  // capture/review work is never lost silently. Otherwise navigate immediately.
  // This only changes phase + hash — it never exports, saves, sends, or posts.
  const handleAppNavNavigate = (target: AppPhase) => {
    if (
      shouldConfirmLeaveGrading({ phase, sessionActive: scanCaptureState.sessionActive }) &&
      typeof window !== 'undefined' &&
      !window.confirm(LEAVE_GRADING_CONFIRM_MESSAGE)
    ) {
      return;
    }
    navigateAppShell(target);
  };

  return (
    <AppContext.Provider value={appContextValue}>
      <FloatingChromeProvider>
        <>
          <DraggableSettingsMenu />
          <div id="app-shell" className="dg-viewport-full min-h-0 w-full min-w-0">
            <div
              id="main-content"
              data-dg-shell={showAppShell ? 'true' : 'false'}
              data-dg-grading-only={hideNonGradeHubPhases ? 'true' : 'false'}
              className={`dg-wave-bg h-dvh min-h-0 font-sans overflow-hidden relative flex flex-col selection:bg-dg-primary-500/25 ${
                isSignedIn
                  ? 'text-dg-fg-primary dark:text-slate-100'
                  : 'text-slate-800 dark:text-slate-200'
              }`}
              tabIndex={-1}
            >
              {!isOnline && !isOfflineBannerDismissed && (
                <div
                  className="fixed z-[100] animate-in slide-in-from-top duration-500 w-[min(100%,24rem)] max-w-[calc(100vw-1rem)] left-1/2 -translate-x-1/2 top-[max(0.75rem,env(safe-area-inset-top,0.5rem))]"
                  role="status"
                  aria-live="polite"
                >
                  <div className="px-4 py-3 bg-amber-500/90 backdrop-blur-xl border border-amber-400/50 rounded-xl shadow-xl flex flex-col gap-2 text-white drop-shadow-md">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <WifiOff className="w-4 h-4 shrink-0" aria-hidden />
                        <div className="min-w-0">
                          <span className="font-black text-[10px] uppercase tracking-widest block">
                            {t('offlineBanner.title')}
                          </span>
                          <p className="mt-0.5 text-[10px] font-semibold text-white/90 leading-snug">
                            {t('offlineBanner.body')}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsOfflineBannerDismissed(true)}
                        className="p-1 hover:bg-white/20 rounded-lg transition-colors shrink-0"
                        aria-label={t('offlineBanner.dismissAria')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showOnlineRestore && isOnline && (
                <div
                  className="fixed z-[100] animate-in slide-in-from-top duration-500 w-[min(100%,24rem)] max-w-[calc(100vw-1rem)] left-1/2 -translate-x-1/2 top-[calc(max(0.75rem,env(safe-area-inset-top,0.5rem))+3.5rem)]"
                  role="status"
                  aria-live="polite"
                >
                  <div className="px-4 py-2.5 bg-emerald-500/90 backdrop-blur-xl border border-emerald-400/50 rounded-xl shadow-lg flex items-center gap-2 text-white font-black text-[9px] uppercase tracking-widest justify-center drop-shadow-md">
                    <Wifi className="w-4 h-4" aria-hidden /> <span>{t('onlineRestore.title')}</span>
                  </div>
                </div>
              )}

              <OfflineGradeSyncIntentBanner
                visible={
                  isOnline &&
                  !!offlineGradeSyncIntent &&
                  !offlineGradeSyncBannerDismissed &&
                  isOfflineGradeSyncIntentValid(offlineGradeSyncIntent, gradedWorks.length)
                }
                showOnlineRestore={showOnlineRestore}
                onDismiss={() => setOfflineGradeSyncBannerDismissed(true)}
                onOpenPublish={() => {
                  const intent = getOfflineGradeSyncIntent();
                  if (!intent || !isOfflineGradeSyncIntentValid(intent, gradedWorks.length)) {
                    clearOfflineGradeSyncIntent();
                    setOfflineGradeSyncIntent(null);
                    return;
                  }
                  setSyncPreflightIndexes(intent.indexes);
                  setGradeSyncPostToClassroom(intent.post);
                  setGradeSyncEmailStudents(intent.email);
                  setGradeSyncSaveToDrive(intent.drive);
                  setOfflineGradeSyncBannerDismissed(true);
                  setPhase(AppPhase.GRADE_SYNC_PREFLIGHT);
                }}
              />

              {showAppShell ? (
                <DesktopSidebar
                  phase={phase}
                  isSignedIn={isSignedIn}
                  isOnline={isOnline}
                  setPhase={setPhase}
                  bumpNavUsage={bumpNavUsage}
                  recordShortcutUse={recordShortcutUse}
                />
              ) : null}
              {showMobileShellNav ? (
                <MobileShellDrawer
                  open={mobileShellNavOpen}
                  onClose={() => setMobileShellNavOpen(false)}
                  phase={phase}
                  isSignedIn={isSignedIn}
                  isOnline={isOnline}
                  setPhase={setPhase}
                  bumpNavUsage={bumpNavUsage}
                  recordShortcutUse={recordShortcutUse}
                />
              ) : null}

              {/* RENDER ROUTER — single flex child so content fits viewport */}
              <div
                className={`flex-1 min-h-0 overflow-hidden flex flex-col w-full min-w-0 ${
                  showAppShell
                    ? 'lg:pl-[var(--dg-sidebar-w)] transition-[padding-left] duration-200 ease-out'
                    : ''
                }`}
              >
                {!DEMO_GRADE_ONLY &&
                  (phase === AppPhase.AUTHENTICATION ||
                    (GRADING_ONLY_MODE && !firebaseAuthVerified)) && (
                    <Suspense fallback={<PhaseLazyFallback />}>
                      <AuthViewLazy />
                    </Suspense>
                  )}
                {/* App-shell phases (Phase 1B placeholders; behind VITE_APP_SHELL). */}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.HOME && (
                  <DashboardHomeContainer
                    firebaseSignedIn={firebaseAuthVerified}
                    accessToken={accessToken}
                    educatorName={educatorName}
                    todayLabel={todayLabel}
                    history={history}
                    onStartGrading={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                    onNavigate={navigateAppShell}
                    onTryDemo={DEMO_WORKFLOW_ENABLED ? handleTryDemo : undefined}
                  />
                )}
                {APP_SHELL_ENABLED &&
                  DEMO_WORKFLOW_ENABLED &&
                  isSignedIn &&
                  phase === AppPhase.DEMO_WORKFLOW && (
                    <DemoWorkflow
                      onStartRealGrading={handleStartRealGradingFromDemo}
                      onExit={handleExitDemoToHome}
                    />
                  )}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.GRADE_HOME && (
                  <GradeHomeContainer
                    firebaseSignedIn={firebaseAuthVerified}
                    accessToken={accessToken}
                    history={history}
                    onStartGrading={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                    onNavigate={navigateAppShell}
                  />
                )}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.PLAN_HOME && (
                  <PlanHomeContainer
                    history={history}
                    onStartGrading={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                    onNavigate={navigateAppShell}
                  />
                )}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.COMMUNICATE_HOME && (
                  <CommunicateHomeContainer
                    history={history}
                    onStartGrading={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                    onNavigate={navigateAppShell}
                  />
                )}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.MANAGE_HOME && (
                  <ManageHomeContainer
                    firebaseSignedIn={firebaseAuthVerified}
                    accessToken={accessToken}
                    educatorName={educatorName}
                    educatorEmail={firebaseUser?.email ?? null}
                    onStartGrading={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                    onNavigate={navigateAppShell}
                  />
                )}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.PRIVACY_DASHBOARD && (
                  <PrivacyDashboard
                    onBack={() => navigateAppShell(AppPhase.MANAGE_HOME)}
                    onOpenAccountSettings={() => setPhase(AppPhase.OPTIONS)}
                    onOpenGoogleConnectionSettings={() => navigateAppShell(AppPhase.MANAGE_HOME)}
                    onOpenGradingSetup={() => setPhase(AppPhase.SCAN_WORKFLOW)}
                    onOpenComplianceStatus={() => navigateAppShell(AppPhase.COMPLIANCE_STATUS)}
                    googleClassroomStatus={buildPrivacyGoogleClassroomStatus({
                      googleConnected: !!accessToken,
                      classroomSync: syncStatus,
                    })}
                  />
                )}
                {APP_SHELL_ENABLED && isSignedIn && phase === AppPhase.COMPLIANCE_STATUS && (
                  <ComplianceStatusPage
                    onBack={() => navigateAppShell(AppPhase.PRIVACY_DASHBOARD)}
                  />
                )}
                {!hideNonGradeHubPhases && phase === AppPhase.PLAN && renderPlan()}
                {!hideNonGradeHubPhases && phase === AppPhase.SCHEDULE && (
                  <Suspense fallback={<PhaseLazyFallback />}>
                    <SchedulePhaseViewLazy
                      educatorName={educatorName}
                      todayLabel={todayLabel}
                      isOnline={isOnline}
                      isDarkMode={isDarkMode}
                      setIsDarkMode={setIsDarkMode}
                      syncStatus={syncStatus}
                      onSyncClick={retryClassroomSync}
                      courses={courses}
                      assignments={assignments}
                      scheduleItems={scheduleItems}
                      setScheduleItems={setScheduleItems}
                      scheduleView={scheduleView}
                      setScheduleView={setScheduleView}
                      scheduleCursor={scheduleCursor}
                      setScheduleCursor={setScheduleCursor}
                      deferredScheduleSearch={deferredScheduleSearch}
                      scheduleSearch={scheduleSearch}
                      setScheduleSearch={setScheduleSearch}
                      scheduleFilterOpen={scheduleFilterOpen}
                      setScheduleFilterOpen={setScheduleFilterOpen}
                      scheduleFilterKinds={scheduleFilterKinds}
                      setScheduleFilterKinds={setScheduleFilterKinds}
                      scheduleFilterCourseId={scheduleFilterCourseId}
                      setScheduleFilterCourseId={setScheduleFilterCourseId}
                      scheduleFilterOverdueOnly={scheduleFilterOverdueOnly}
                      setScheduleFilterOverdueOnly={setScheduleFilterOverdueOnly}
                      scheduleFilterRecurringOnly={scheduleFilterRecurringOnly}
                      setScheduleFilterRecurringOnly={setScheduleFilterRecurringOnly}
                      scheduleUndo={scheduleUndo}
                      setScheduleUndo={setScheduleUndo}
                      scheduleTitle={scheduleTitle}
                      setScheduleTitle={setScheduleTitle}
                      scheduleKind={scheduleKind}
                      setScheduleKind={setScheduleKind}
                      scheduleDate={scheduleDate}
                      setScheduleDate={setScheduleDate}
                      scheduleNotes={scheduleNotes}
                      setScheduleNotes={setScheduleNotes}
                      scheduleCourseId={scheduleCourseId}
                      setScheduleCourseId={setScheduleCourseId}
                      scheduleAssignmentId={scheduleAssignmentId}
                      setScheduleAssignmentId={setScheduleAssignmentId}
                      scheduleTime={scheduleTime}
                      setScheduleTime={setScheduleTime}
                      scheduleRecurrence={scheduleRecurrence}
                      setScheduleRecurrence={setScheduleRecurrence}
                      scheduleFormOpen={scheduleFormOpen}
                      setScheduleFormOpen={setScheduleFormOpen}
                      editingScheduleId={editingScheduleId}
                      setEditingScheduleId={setEditingScheduleId}
                      activeScheduleHour={activeScheduleHour}
                      setActiveScheduleHour={setActiveScheduleHour}
                    />
                  </Suspense>
                )}
                {phase === AppPhase.DASHBOARD && (
                  <Suspense fallback={<PhaseLazyFallback />}>
                    <DashboardPhaseViewLazy
                      courses={courses}
                      assignments={assignments}
                      students={students}
                      gradedWorks={gradedWorks}
                      selectedCourse={selectedCourse}
                      selectedAssignment={selectedAssignment}
                      customRubric={customRubric}
                      gradingMode={gradingMode}
                      isOnline={isOnline}
                      isDarkMode={isDarkMode}
                      setIsDarkMode={setIsDarkMode}
                      syncStatus={syncStatus}
                      onSyncClick={retryClassroomSync}
                      classroom={classroom}
                      scanStudentMode={scanStudentMode}
                      batchSelectedStudentIds={batchSelectedStudentIds}
                      eliteFlags={{
                        cameraFirstLaunch: eliteFlags.cameraFirstLaunch,
                        publishReadinessMeter: eliteFlags.publishReadinessMeter,
                      }}
                      bumpNavUsage={bumpNavUsage}
                      trackEliteEvent={trackEliteEvent}
                      setPhase={setPhase}
                      startGrading={startGrading}
                      setSyncPreflightIndexes={setSyncPreflightIndexes}
                      resumeGradeSession={
                        resumeGradeSessionBanner
                          ? {
                              courseName: resumeGradeSessionBanner.courseName,
                              assignmentTitle: resumeGradeSessionBanner.assignmentTitle,
                              onResume: () => {
                                void resumeLastGradeSession();
                              },
                              busy: isResumingGradeSession,
                            }
                          : null
                      }
                      hasQueuedOfflinePublish={!!offlineGradeSyncIntent}
                      history={history}
                      onOpenGradingSettings={
                        GRADING_ONLY_MODE ? () => setGradingOnlySettingsOpen(true) : undefined
                      }
                      onOpenRecentTarget={GRADING_ONLY_MODE ? openRecentGradeTarget : undefined}
                      hubTrustStatus={hubTrustStatus}
                      nextHubAssignment={nextHubAssignment}
                      onContinueGrading={() => void continueGradingFromHub()}
                      onHubNextStudent={hubNextStudent}
                      onHubStartNext={hubStartNextAssignment}
                      onConnectClassroom={handleConnectClassroomForGrade}
                      onCreateClassManually={() => setPhase(AppPhase.COURSE_CREATION)}
                      onSkipWorkspaceSetup={skipGradeWorkspaceSetup}
                      classroomSyncErrorHint={syncStatus === 'error' ? authError : null}
                    />
                  </Suspense>
                )}
                {phase === AppPhase.GRADE_COURSE_PICKER && renderGradeCoursePicker()}
                {phase === AppPhase.ROSTER_VIEW && renderRosterView()}
                {!hideNonGradeHubPhases && phase === AppPhase.CLASSROOM && renderTeachDesk()}
                {!hideNonGradeHubPhases &&
                  phase === AppPhase.CLASS_PRESENTER &&
                  renderTeachPresenter()}
                {phase === AppPhase.COURSE_CREATION && renderCourseCreation()}
                {phase === AppPhase.ASSIGNMENT_SELECT && renderAssignmentSelect()}
                {phase === AppPhase.ASSIGNMENT_CREATION && renderAssignmentCreation()}
                {phase === AppPhase.GRADE_SCAN_SETUP && renderScanSetup()}
                {phase === AppPhase.SCAN_WORKFLOW && firebaseAuthVerified && (
                  <Suspense fallback={<PhaseLazyFallback />}>
                    <GradingWorkflowV2Lazy
                      accessToken={accessToken}
                      firebaseSignedIn={firebaseAuthVerified}
                      isOnline={isOnline}
                      isDarkMode={isDarkMode}
                      setIsDarkMode={setIsDarkMode}
                      syncStatus={syncStatus}
                      onSyncClick={retryClassroomSync}
                      onFirebaseSignIn={handleFirebaseGoogleSignIn}
                      onExit={() => setPhase(appLandingPhase())}
                      onCaptureStateChange={
                        APP_SHELL_ENABLED ? handleScanCaptureStateChange : undefined
                      }
                      appBottomNavVisible={appBottomNavVisible}
                    />
                  </Suspense>
                )}
                {phase === AppPhase.GRADE_BATCH_ROSTER && renderBatchRoster()}
                {phase === AppPhase.RUBRIC_SETUP && renderRubricSetup()}
                {phase === AppPhase.MODE_SELECTION && renderModeSelection()}
                {phase === AppPhase.GRADING_LOOP && renderGradingLoop()}
                {phase === AppPhase.AUDIT && renderAudit()}
                {phase === AppPhase.GRADE_SYNC_PREFLIGHT && renderSyncPreflight()}
                {phase === AppPhase.SYNCING && renderSyncing()}
                {phase === AppPhase.FINALE && renderFinale()}
                {phase === AppPhase.PAYWALL && renderPaywall()}
                {!hideNonGradeHubPhases && phase === AppPhase.RECORDS && (
                  <PageWrapper
                    headerTitle={educatorName || t('nav.communicate')}
                    headerSubtitle={todayLabel || undefined}
                    isOnline={isOnline}
                    isDarkMode={isDarkMode}
                    setIsDarkMode={setIsDarkMode}
                    syncStatus={syncStatus}
                    onSyncClick={retryClassroomSync}
                  >
                    <Suspense fallback={<PhaseLazyFallback />}>
                      <CommunicationDashboardLazy
                        educatorName={educatorName}
                        courses={courses}
                        classroom={classroom}
                        students={students}
                        accessToken={accessToken}
                        isOnline={isOnline}
                      />
                    </Suspense>
                  </PageWrapper>
                )}
                {!DEMO_GRADE_ONLY && phase === AppPhase.OPTIONS && (
                  <Suspense fallback={<PhaseLazyFallback />}>
                    <OptionsPhaseViewLazy
                      educatorName={educatorName}
                      accessToken={accessToken}
                      todayLabel={todayLabel}
                      isOnline={isOnline}
                      isDarkMode={isDarkMode}
                      setIsDarkMode={setIsDarkMode}
                      syncStatus={syncStatus}
                      onSyncClick={retryClassroomSync}
                      onBack={() => setPhase(AppPhase.DASHBOARD)}
                      handleShareApp={handleShareApp}
                      handleSignOut={handleSignOut}
                      scanStudentMode={scanStudentMode}
                      eliteFlags={eliteFlags}
                      setEliteFlags={setEliteFlags}
                      eliteMetrics={eliteMetrics}
                      setEliteMetrics={setEliteMetrics}
                      trackEliteEvent={trackEliteEvent}
                      setPhase={setPhase}
                      optionsConfirm={optionsConfirm}
                      setOptionsConfirm={setOptionsConfirm}
                      optionsDataPanelOpen={optionsDataPanelOpen}
                      setOptionsDataPanelOpen={setOptionsDataPanelOpen}
                      onExecuteDataConfirm={executeOptionsDataConfirm}
                      subscriptionStatus={subscriptionStatus}
                      subscriptionRefreshBusy={subscriptionRefreshBusy}
                      subscriptionRefreshError={subscriptionRefreshError}
                      onRefreshSubscription={refreshSubscriptionFromStripe}
                      onDeleteAccount={handleDeleteAccount}
                    />
                  </Suspense>
                )}
              </div>

              {/* App-level bottom nav (behind VITE_APP_SHELL). Visible on the
                  app-shell phases AND across the grading workflow, except on the
                  live camera / full-screen crop capture surface. `forceVisible`
                  lets it render during SCAN_WORKFLOW; App owns the decision via
                  `shouldShowAppBottomNav`. */}
              {appBottomNavVisible && (
                <AppBottomNav phase={phase} onNavigate={handleAppNavNavigate} forceVisible />
              )}

              {GRADING_ONLY_MODE ? (
                <GradingOnlySettingsSheet
                  open={gradingOnlySettingsOpen}
                  onClose={() => setGradingOnlySettingsOpen(false)}
                  isDarkMode={isDarkMode}
                  setIsDarkMode={setIsDarkMode}
                  scanStudentMode={scanStudentMode}
                  eliteFlags={eliteFlags}
                  setEliteFlags={setEliteFlags}
                  onRetryClassroomSync={retryClassroomSync}
                  syncStatus={syncStatus}
                  isOnline={isOnline}
                />
              ) : null}
            </div>
          </div>
          <ConsentBanner />
        </>
      </FloatingChromeProvider>
    </AppContext.Provider>
  );
};

export default App;
